<template>
  <!-- ===== Minimized: a tiny, movable mascot ===== -->
  <div
    v-if="windowState === 'min'"
    class="ct-fab"
    :style="{ top: fab.y + 'px', left: fab.x + 'px' }"
    @mousedown="startDrag"
    @click="onFabClick"
    title="Ask GNey — uses your current view"
  >
    <fts-gney-ai class="ct-fab-mascot" :type="inflight ? 'loading' : 'default'" :size="58" />
    <span v-if="unread" class="ct-fab-dot"></span>
  </div>

  <!-- ===== Open: docked popup or fullscreen ===== -->
  <div
    v-else
    class="ct-panel"
    :class="[`ct-panel--${windowState}`, { 'ct-panel--dragging': false }]"
  >
    <!-- drag-to-resize the docked rail (anchored right; drag left widens) -->
    <div v-if="windowState === 'side'" class="ct-resize" @mousedown.prevent="startResize"
      title="Drag to resize"></div>

    <!-- header -->
    <header class="ct-head">
      <div class="ct-head-bg"><gradient-ai-background /></div>
      <div class="ct-head-main">
        <fts-gney-ai class="ct-head-mascot" :type="inflight ? 'loading' : 'default'" :size="34" />
        <div class="ct-head-text">
          <div class="ct-head-title" :class="{ 'ct-head-title--session': !!chat.title }"
            :title="chat.title || ''">{{ headerTitle }}
            <span v-if="!chat.title" class="ct-alpha">alpha</span>
          </div>
        </div>
      </div>
      <div class="ct-head-actions">
        <button class="ct-icon-btn" type="button" title="Conversation history"
          data-hist-toggle :class="{ 'ct-icon-btn--on': historyOpen }" @click="toggleHistory">
          <font-awesome-icon :icon="['fas', 'clock-rotate-left']" />
        </button>
        <button class="ct-icon-btn" type="button" title="New chat"
          :disabled="chat.history.length === 0 && !inflight" @click="resetChat">
          <font-awesome-icon :icon="['far', 'plus-square']" />
        </button>
        <!-- Internal-only: download this conversation's full log (transcript + per-turn model/token/
             cost/latency/tool-call metrics) as JSON, for offline prompt/model optimization. Own
             session only — reuses the same user+workspace-scoped fetch as the history picker. -->
        <button v-if="isInternal" class="ct-icon-btn" type="button"
          title="Download this conversation's log (internal — for optimization)"
          :disabled="!chat.sessionId || chat.history.length === 0 || downloadingId === chat.sessionId"
          @click="downloadCurrentLog">
          <font-awesome-icon :icon="['fas', downloadingId === chat.sessionId ? 'spinner' : 'download']"
            :spin="downloadingId === chat.sessionId" />
        </button>
        <button class="ct-icon-btn" type="button"
          :class="{ 'ct-icon-btn--on': windowState === 'side' }"
          :title="windowState === 'side' ? 'Float (overlay)' : 'Dock to side'"
          @click="toggleSide">
          <font-awesome-icon :icon="['far', windowState === 'side' ? 'window-restore' : 'window-maximize']" />
        </button>
        <button class="ct-icon-btn" type="button" title="Minimize" @click="minimize">
          <font-awesome-icon :icon="['fas', 'minus']" />
        </button>
      </div>
    </header>

    <!-- conversation history picker — past sessions, searchable; resume / rename / delete -->
    <div v-if="historyOpen" ref="hist" class="ct-hist">
      <div class="ct-hist-top">
        <div class="ct-hist-search">
          <font-awesome-icon class="ct-hist-search-ic" :icon="['fas', 'search']" />
          <input ref="histSearch" v-model="sessionSearch" type="text" class="ct-hist-search-input"
            placeholder="Search conversations…" @keydown.esc="closeHistory" />
        </div>
        <button class="ct-hist-new" type="button" title="Start a new conversation" @click="newFromHistory">
          <font-awesome-icon :icon="['fas', 'plus']" /> New
        </button>
      </div>

      <div class="ct-hist-list">
        <div v-if="sessionsLoading" class="ct-hist-state">
          <font-awesome-icon :icon="['fas', 'spinner']" spin /> Loading…
        </div>
        <div v-else-if="sessionsError" class="ct-hist-state ct-hist-state--err">
          {{ sessionsError }}
          <button type="button" class="ct-hist-retry" @click="loadSessions">Retry</button>
        </div>
        <div v-else-if="!filteredSessions.length" class="ct-hist-state">
          {{ sessionSearch ? 'No matching conversations.' : 'No past conversations yet.' }}
        </div>
        <template v-else>
          <div v-for="s in filteredSessions" :key="s.sessionId"
            class="ct-hist-item" :class="{ 'is-active': s.sessionId === chat.sessionId }"
            @click="openSession(s)">
            <template v-if="renamingId === s.sessionId">
              <input ref="renameInput" v-model="renameDraft" type="text" class="ct-hist-rename"
                @click.stop @keydown.enter.prevent="commitRename(s, $event)" @keydown.esc.stop="cancelRename"
                @blur="commitRename(s)" />
            </template>
            <template v-else>
              <span class="ct-hist-title" :title="s.title || 'Untitled conversation'">{{ s.title || 'Untitled conversation' }}</span>
              <span class="ct-hist-time">{{ relativeTime(s.updatedAt) }}</span>
              <span class="ct-hist-actions">
                <button v-if="isInternal" type="button" class="ct-hist-act"
                  title="Download log (internal — for optimization)"
                  :disabled="downloadingId === s.sessionId" @click.stop="downloadSessionLog(s)">
                  <font-awesome-icon :icon="['fas', downloadingId === s.sessionId ? 'spinner' : 'download']"
                    :spin="downloadingId === s.sessionId" />
                </button>
                <button type="button" class="ct-hist-act" title="Rename" @click.stop="startRename(s)">
                  <font-awesome-icon :icon="['fas', 'pencil']" />
                </button>
                <button type="button" class="ct-hist-act ct-hist-act--del" title="Delete"
                  :disabled="deletingId === s.sessionId" @click.stop="deleteSession(s)">
                  <font-awesome-icon :icon="['fas', deletingId === s.sessionId ? 'spinner' : 'trash']"
                    :spin="deletingId === s.sessionId" />
                </button>
              </span>
            </template>
          </div>
        </template>
      </div>
    </div>

    <!-- messages -->
    <div class="ct-list" ref="list" @click="onListClick">
      <div v-if="chat.history.length === 0 && !inflight" class="ct-empty">
        <fts-gney-ai :type="'default'" :size="64" />
        <p class="ct-empty-title">Hello, I'm GNey. Let's do magic!</p>
        <p class="ct-empty-sub">{{ contextHint }}</p>
        <div class="ct-suggest">
          <button v-for="(s, i) in suggestions" :key="i" type="button"
            class="ct-suggest-btn" @click="useSuggestion(s)">{{ s }}</button>
        </div>
      </div>

      <div v-for="(msg, i) in chat.history" :key="i" :class="['ct-msg', `ct-msg--${msg.role}`]">
        <div class="ct-msg-body">
          <div class="ct-msg-content ct-md" v-html="renderMarkdown(msg.content)"></div>

          <!-- A committed design/modify turn carries a clickable module pill — the receipt for the
               created/updated module, in place of a separate "Created module" action card. -->
          <div v-if="msg.moduleChip" class="ct-msg-modulechip" v-html="msg.moduleChip"></div>

          <!-- files the user sent with this turn. A stored ref (path) makes the chip a download
               link served by the workspace download route; a fresh unsent preview stays a span. -->
          <div v-if="msg.attachments && msg.attachments.length" class="ct-msg-attachments">
            <component v-for="(att, ai) in msg.attachments" :key="ai" class="ct-msg-att"
              :is="chatFileHref(att.path) ? 'a' : 'span'"
              :href="chatFileHref(att.path) || undefined" target="_blank" rel="noopener noreferrer">
              <img v-if="att.isImage && att.dataUrl" :src="att.dataUrl" class="ct-msg-att-thumb" :alt="att.name" :title="att.name" />
              <span v-else class="ct-msg-att-file" :title="att.name">
                <font-awesome-icon :icon="['far', 'file']" /> {{ att.name }}
              </span>
            </component>
          </div>

          <!-- Done cards — receipts for activities the agent performed this turn. Design card
               anatomy: muted "✓ …" title line (red tinted strip when failed), white body with
               the entry chip + resulting state, the submitted values, and the state-aware
               next-step buttons. -->
          <div v-if="msg.actions && msg.actions.length" class="ct-cards">
            <div v-for="(act, ai) in msg.actions" :key="ai" class="ct-card">
              <div class="ct-card-head" :class="act.error ? 'is-error' : 'is-done'">
                <span class="ct-card-title">{{ doneActionLabel(act) }}{{ act.error ? ' failed' : '' }}</span>
                <div v-if="!act.error && !actionEntryChip(act) && actionHref(act)" class="ct-card-actions">
                  <a :href="actionHref(act)" class="ct-card-link">
                    view <font-awesome-icon :icon="['fas', 'up-right-from-square']" />
                  </a>
                </div>
              </div>
              <div class="ct-card-body">
                <!-- A create/edit (or any entry-touching activity) always surfaces a clickable entry
                     chip so the user can open the affected entry; the resulting state rides beside it. -->
                <div v-if="!act.error && (actionEntryChip(act) || actionState(act) || act.module)" class="ct-receipt-row">
                  <span v-if="actionEntryChip(act)" class="ct-receipt-chip" v-html="actionEntryChip(act)"></span>
                  <span v-else-if="act.module" class="ct-ident-name">{{ act.module }}</span>
                  <span v-if="actionState(act)" class="ct-status-chip">{{ actionState(act) }}</span>
                  <!-- State-aware next-step activities stay aligned with the receipt identity.
                       Each button still only OPENS that activity's form on the entry. -->
                  <div v-if="nextActivities(act).length" class="ct-done-next">
                    <button v-for="(na, ni) in nextActivities(act)" :key="`nx-${ni}`" type="button"
                      class="ct-next-btn" :class="{ 'is-custom': !na.standard }" :disabled="inflight"
                      :title="`Open the ${nextActivityLabel(na)} form for this entry`"
                      @click="openNextActivity(act, na)">
                      <span v-if="na.emoji" class="ct-next-btn-emoji">{{ na.emoji }}</span>
                      <span class="ct-next-btn-label">{{ nextActivityLabel(na) }}</span>
                    </button>
                  </div>
                </div>
                <div v-if="!act.error && act.type === 'module' && act.message" class="ct-receipt-note">{{ act.message }}</div>
                <!-- Keep the first two submitted values visible. Longer receipts progressively
                     reveal their remaining rows through the same View more pattern as activity cards. -->
                <div v-if="!act.error && actionValues(act).length" class="ct-kv-rows">
                  <div v-for="(f, fi) in visibleActionValues(act)" :key="fi" class="ct-kv">
                    <span class="ct-kv-k">{{ f.label }}</span>
                    <span class="ct-kv-v">{{ f.value }}</span>
                  </div>
                  <button v-if="actionValuesOverflow(act)" type="button" class="ct-kv-more"
                    @click="toggleActionValues(act)">
                    <span>{{ act._kvExpanded ? 'View less' : 'View more' }}</span>
                    <span class="material-symbols-outlined ct-kv-more-icon">
                      {{ act._kvExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
                    </span>
                  </button>
                </div>
                <div v-if="act.error" class="ct-action-err">{{ act.message || act.error }}</div>
              </div>
            </div>
          </div>

          <!-- Activity card — ONE component for performing an activity (ruling #12), one look,
               buttons by actor: the hybrid variant (is-confirm) shows the prepared values with
               Confirm + Open form in the header strip; the human / no-actor variant (is-open) is
               the identical card minus Confirm — the AI may prefill values, but the user must
               open the form and submit it themselves. Wire unchanged — items still arrive on
               pendingActions[] (confirm) and formPills[] (open). A confirmed card morphs in
               place into the quiet receipt (muted ✓ title over the entry identity + resulting
               state) rather than stacking a second card under it — the clickable entry chip
               lives in the receipt prose the server sends below the card.
               A bulk fan-out — several items of the SAME (module, activity) — collapses into one
               paged group (ruling #6): ‹ n of N › pager like the ask card, per-page buttons as
               above, and confirm groups add a counted "Confirm all (N)" that commits every
               remaining item in a single Confirmations[] round-trip. Singles are groups of one
               and render without any group chrome — pixel-identical to before. -->
          <div v-if="hasActivityCards(msg)" class="ct-cards">
            <div v-for="(grp, gi) in msg.activityGroups" :key="`ag-${gi}`"
              class="ct-group" :class="{ 'is-multi': grp.items.length > 1 }">
            <div v-if="grp.items.length > 1" class="ct-group-bar">
              <span class="ct-group-title">{{ groupTitle(grp) }}</span>
              <div class="ct-ask-pager">
                <button type="button" class="ct-ask-nav" :disabled="groupPage(grp) === 0"
                  title="Previous item" @click="moveGroupPage(grp, -1)">‹</button>
                <span class="ct-ask-count">{{ groupPage(grp) + 1 }} of {{ grp.items.length }}</span>
                <button type="button" class="ct-ask-nav" :disabled="groupPage(grp) >= grp.items.length - 1"
                  title="Next item" @click="moveGroupPage(grp, 1)">›</button>
              </div>
            </div>
            <template v-if="grp.kind === 'confirm'">
            <div v-for="(pa, pai) in grp.items" v-show="pai === groupPage(grp)" :key="`ac-${pai}`" class="ct-card is-confirm"
              :class="{ 'is-inactive': pa._opened && pa._status !== 'done' }">
              <template v-if="pa._status === 'done'">
                <div class="ct-card-head is-done">
                  <span class="ct-card-title">{{ pendingReceiptTitle(pa) }}</span>
                </div>
                <div class="ct-card-body">
                  <div class="ct-receipt-row">
                    <span class="ct-ident-ic">{{ moduleEmojiFor(pendingReceiptModule(pa)) || '📄' }}</span>
                    <span class="ct-ident-name">{{ pendingReceiptRef(pa) }}</span>
                    <span v-if="pendingReceiptState(pa)" class="ct-status-chip">{{ pendingReceiptState(pa) }}</span>
                  </div>
                  <div v-if="pendingReceiptNext(pa).length" class="ct-done-next">
                    <button v-for="(na, ni) in pendingReceiptNext(pa)" :key="`pnx-${ni}`" type="button"
                      class="ct-next-btn" :class="{ 'is-custom': !na.standard }" :disabled="inflight"
                      :title="`Open the ${nextActivityLabel(na)} form for this entry`"
                      @click="openNextActivity(pa._result, na)">
                      <span v-if="na.emoji" class="ct-next-btn-emoji">{{ na.emoji }}</span>
                      <span class="ct-next-btn-label">{{ nextActivityLabel(na) }}</span>
                    </button>
                  </div>
                </div>
              </template>
              <template v-else>
                <div class="ct-card-head" :class="pa._opened ? 'is-muted' : 'is-pending'">
                  <span class="ct-card-title">{{ pa.activity || 'activity' }}</span>
                  <div class="ct-card-actions">
                    <button class="ct-ghost-btn" type="button"
                      :disabled="inflight || pa._status === 'committing'"
                      title="Open the form to review or change values before submitting"
                      @click="openPendingForm(pa, grp)">Open form</button>
                    <button class="ct-confirm" type="button"
                      :disabled="inflight || pa._status === 'committing' || pa._opened"
                      :title="pa._opened ? 'You opened the form — submit it there instead' : ''"
                      @click="confirmPending(pa, grp)">
                      <font-awesome-icon v-if="pa._status === 'committing'" :icon="['fas', 'spinner']" spin />
                      Confirm
                    </button>
                  </div>
                </div>
                <div class="ct-card-body">
                  <div class="ct-ident">
                    <span class="ct-ident-ic">{{ moduleEmojiFor(pa.module) || '📄' }}</span>
                    <span class="ct-ident-name">{{ pa.module || formPillLabel(pa) }}</span>
                    <span v-if="pendingDocRef(pa)" class="ct-ident-doc">| #{{ pendingDocRef(pa) }}</span>
                  </div>
                  <!-- Prepared values the AI filled in: shown so the user confirms with full sight of
                       what will be submitted, rather than confirming blind. Long lists fold behind
                       "View more" (design). Once the form is opened the values live THERE — the
                       card folds to identity + note. -->
                  <div v-if="pendingValues(pa).length && !pa._opened" class="ct-kv-rows">
                    <div v-for="(f, fi) in visiblePendingValues(pa)" :key="fi" class="ct-kv">
                      <span class="ct-kv-k">{{ f.label }}</span>
                      <span class="ct-kv-v">{{ f.value }}</span>
                    </div>
                    <button v-if="kvOverflow(pa)" type="button" class="ct-kv-more" @click="toggleKv(pa)">
                      {{ pa._kvExpanded ? 'View less ▴' : 'View more ▾' }}
                    </button>
                  </div>
                  <div v-if="pa._opened" class="ct-card-note">Opened in form — review and submit it there.</div>
                </div>
              </template>
            </div>
            </template>
            <template v-else>
            <div v-for="(pa, pai) in grp.items" v-show="pai === groupPage(grp)" :key="`ao-${pai}`" class="ct-card is-open"
              :class="{ 'is-inactive': pa._opened }">
              <div class="ct-card-head" :class="pa._opened ? 'is-muted' : 'is-pending'">
                <span class="ct-card-title">{{ formPillLabel(pa) }}</span>
                <div class="ct-card-actions">
                  <button class="ct-ghost-btn" type="button" :disabled="inflight"
                    title="Open the form to complete and submit it yourself"
                    @click="openPendingForm(pa, grp)">Open form</button>
                </div>
              </div>
              <div class="ct-card-body">
                <div class="ct-ident">
                  <span class="ct-ident-ic">{{ moduleEmojiFor(pa.module) || '📄' }}</span>
                  <span class="ct-ident-name">{{ pa.module || formPillLabel(pa) }}</span>
                  <span v-if="pendingDocRef(pa)" class="ct-ident-doc">| #{{ pendingDocRef(pa) }}</span>
                </div>
                <!-- Values the AI prefilled (if any) — the form opens with these; the user completes
                     the rest and submits from the form. No Confirm on a human activity. Folded
                     away once the form is open (the values live there). -->
                <div v-if="pendingValues(pa).length && !pa._opened" class="ct-kv-rows">
                  <div v-for="(f, fi) in visiblePendingValues(pa)" :key="fi" class="ct-kv">
                    <span class="ct-kv-k">{{ f.label }}</span>
                    <span class="ct-kv-v">{{ f.value }}</span>
                  </div>
                  <button v-if="kvOverflow(pa)" type="button" class="ct-kv-more" @click="toggleKv(pa)">
                    {{ pa._kvExpanded ? 'View less ▴' : 'View more ▾' }}
                  </button>
                </div>
                <div v-if="pa._opened" class="ct-card-note">Opened in form — review and submit it there.</div>
              </div>
            </div>
            </template>
            <div v-if="grp.items.length > 1" class="ct-group-foot">
              <span class="ct-ask-hint">{{ groupProgress(grp) }}</span>
              <button v-if="grp.kind === 'confirm' && groupRemaining(grp).length > 1"
                type="button" class="ct-confirm" :disabled="inflight"
                :title="`Confirm the ${groupRemaining(grp).length} remaining items in one go`"
                @click="confirmAllPending(grp)">
                <font-awesome-icon v-if="grp._bulkCommitting" :icon="['fas', 'spinner']" spin />
                Confirm all ({{ groupRemaining(grp).length }})
              </button>
            </div>
            </div>
          </div>

          <!-- Form card — fill_form proposal; the user reviews the checklist and applies the
               checked values into the open form. Apply fills, it never commits (ruling #12). -->
          <div v-if="msg.formCards && msg.formCards.length" class="ct-cards">
            <div v-for="(fc, fci) in msg.formCards" :key="`fcd-${fci}`"
              class="ct-card" :class="{ 'is-inactive': !fc._pushed && !formCardActive(fc) }">
              <template v-if="!fc._pushed">
                <div class="ct-card-head is-pending">
                  <span class="ct-card-title" :title="formCardTarget(fc)">{{ formCardTitle(fc) }}</span>
                  <div class="ct-card-actions">
                    <button type="button" class="ct-confirm"
                      :disabled="!formCardActive(fc) || !checkedRowCount(fc)"
                      :title="formCardActive(fc)
                        ? `Fill ${checkedRowCount(fc)} value${checkedRowCount(fc) === 1 ? '' : 's'} into the open form — you still submit it`
                        : 'The target form is closed — reopen it to apply'"
                      @click="pushFormCard(fc)">Apply</button>
                  </div>
                </div>
                <div class="ct-card-body">
                  <label class="ct-form-row ct-form-row--all">
                    <input type="checkbox" :checked="allRowsChecked(fc)" :indeterminate.prop="someRowsChecked(fc)"
                      :disabled="!formCardActive(fc)" @change="toggleAllRows(fc, $event)" />
                    <span class="ct-form-row-label">Select All</span>
                  </label>
                  <label v-for="row in fc._rows" :key="row.fieldName"
                    :class="['ct-form-row', { 'is-off': !row._checked }]">
                    <input type="checkbox" v-model="row._checked" :disabled="!formCardActive(fc)" />
                    <span class="ct-form-row-main">
                      <span class="ct-form-row-label">{{ row.label }}</span>
                      <span class="ct-form-row-vals">
                        <template v-if="fmtRowVal(row.current)">
                          <span class="ct-val-chip is-old">{{ fmtRowVal(row.current) }}</span>
                          <span class="ct-form-row-arrow">→</span>
                        </template>
                        <span class="ct-val-chip">{{ fmtRowVal(row.proposed) || "(empty)" }}</span>
                      </span>
                    </span>
                  </label>
                  <div v-if="!formCardActive(fc)" class="ct-card-note">Form closed — reopen it to apply these values.</div>
                  <div v-else-if="fc.note" class="ct-card-note">⚡ {{ fc.note }}</div>
                </div>
              </template>
              <template v-else>
                <div class="ct-card-head is-done">
                  <span class="ct-card-title">{{ formCardTitle(fc) }}</span>
                </div>
                <div class="ct-card-body">
                  <div class="ct-card-receipt">{{ fc._pushedLabel }}</div>
                </div>
              </template>
            </div>
          </div>

          <!-- Ask card — clarifying questions (ask_user), paged one question at a time with
               ‹ 1 of N › (design). Predefined single-select answers advance automatically;
               multi-select and typed custom answers require Send answers. Skip sends everything
               as skipped. Answers echo in the user's bubble as a Q/A quote block. -->
          <div v-if="msg.questions && msg.questions.length" class="ct-card ct-ask">
            <template v-if="!msg._questionsSent">
              <div class="ct-card-head is-pending">
                <div v-if="msg.questions.length > 1" class="ct-ask-pager">
                  <button type="button" class="ct-ask-nav" :disabled="askPage(msg) === 0"
                    title="Previous question" @click="movePage(msg, -1)">‹</button>
                  <span class="ct-ask-count">{{ askPage(msg) + 1 }} of {{ msg.questions.length }}</span>
                  <button type="button" class="ct-ask-nav" :disabled="askPage(msg) >= msg.questions.length - 1"
                    title="Next question" @click="movePage(msg, 1)">›</button>
                </div>
                <span v-else class="ct-card-title">A quick question</span>
                <div class="ct-card-actions">
                  <button type="button" class="ct-ghost-btn" :disabled="inflight"
                    title="Skip these questions" @click="submitAnswers(msg, { skipAll: true })">Skip</button>
                </div>
              </div>
              <div class="ct-card-body">
                <div v-for="q in [askCurrent(msg)]" :key="`qp-${askPage(msg)}`" class="ct-question">
                  <div class="ct-question-head">
                    <span class="ct-question-chip">{{ q.header }}</span>
                    <span class="ct-question-q">{{ q.question }}</span>
                  </div>
                  <div class="ct-qopts">
                    <button v-for="(o, oi) in q.options" :key="o.id || oi" type="button"
                      class="ct-qopt" :class="{ 'is-sel': isOptSel(q, o) }"
                      :disabled="inflight" @click="pickOpt(msg, q, o)">
                      <span class="ct-qopt-badge">
                        <font-awesome-icon v-if="isOptSel(q, o)" :icon="['fas', 'check']" />
                        <template v-else>{{ oi + 1 }}</template>
                      </span>
                      <span class="ct-qopt-body">
                        <span class="ct-qopt-label">{{ o.label }}</span>
                        <span v-if="o.description" class="ct-qopt-desc">{{ o.description }}</span>
                      </span>
                    </button>
                    <div class="ct-qopt ct-qopt-else" :class="{ 'is-sel': !!q._custom }">
                      <span class="ct-qopt-badge">✎</span>
                      <input type="text" class="ct-qelse-input" v-model="q._customDraft"
                        :disabled="inflight" placeholder="Something else…"
                        @input="updateCustom(q)" @keydown.enter="commitElse(q, $event)" />
                    </div>
                  </div>
                </div>
                <div class="ct-ask-foot">
                  <span v-if="msg.questions.length > 1" class="ct-ask-hint">{{ answeredCount(msg) }}/{{ msg.questions.length }} answered</span>
                  <button v-if="askCurrentNeedsSubmit(msg)" type="button" class="ct-confirm"
                    :disabled="inflight" @click="submitOrAdvanceAnswers(msg)">Send answers</button>
                </div>
              </div>
            </template>
            <div v-else class="ct-card-head is-done">
              <span class="ct-card-title">Answers sent</span>
            </div>
          </div>

          <!-- Module cards — design / modify proposals, reviewed then committed via button
               (design mode). Pending: accent header ("Create module" / "Update module") with
               Show diagram + the commit button in the strip; the body is the module identity
               plus a one-line state-flow summary — details live in the diagram (hover a
               state/activity there). Committed: muted "✓ Module Created" line over the
               identity + status chip, with diagram access on the identity row (design). -->
          <div v-if="msg.proposals && msg.proposals.length" class="ct-cards">
            <div v-for="(p, ppi) in msg.proposals" :key="`pp-${ppi}`" class="ct-card">
              <!-- Canvas-ops checklist card (studio surface, D9): every op individually
                   reviewable; Apply runs exactly the checked set through the canvasOps
                   engine against the live draft — atomic, recorded, revertible (D12). -->
              <template v-if="p.kind === 'canvasOps' && p._status === 'done'">
                <div class="ct-card-head is-done">
                  <span class="ct-card-title">✓ Changes applied · {{ p._appliedCount }} op{{ p._appliedCount === 1 ? '' : 's' }}</span>
                  <div class="ct-card-actions">
                    <button v-for="tid in appliedAutomationIds(p)" :key="`aut-${tid}`"
                      class="ct-ghost-btn" type="button"
                      @click="reviewAppliedAutomation(tid)">Review automation</button>
                    <button class="ct-ghost-btn" type="button" @click="revertCanvasOpsProposal(p)">Revert…</button>
                  </div>
                </div>
                <div class="ct-card-body">
                  <div v-if="p.summary" class="ct-flow-summary">{{ p.summary }}</div>
                  <div class="ct-card-note">Applied to the draft — review in the designer, then Save.</div>
                </div>
              </template>
              <!-- htmljs document/report draft (studio, D16): the editor's live preview is
                   the review surface — the card is just the doorway into it. -->
              <template v-else-if="p.kind === 'htmljsDocument' && p._status === 'done'">
                <div class="ct-card-head is-done">
                  <span class="ct-card-title">✓ {{ p.dataSource === 'Entries' ? 'Report' : 'Document' }} saved</span>
                </div>
                <div class="ct-card-body">
                  <div class="ct-ident">
                    <font-awesome-icon :icon="['far', 'file']" class="ct-ident-ic" />
                    <span class="ct-ident-name">{{ p._savedName || p.name }}</span>
                  </div>
                </div>
              </template>
              <template v-else-if="p.kind === 'htmljsDocument'">
                <div class="ct-card-head is-pending">
                  <span class="ct-card-title">{{ p.dataSource === 'Entries' ? 'Report draft' : 'Document draft' }}</span>
                  <div class="ct-card-actions">
                    <button class="ct-confirm" type="button" :disabled="!isStudio"
                      @click="openHtmlJsDraft(p)">Open in document editor</button>
                  </div>
                </div>
                <div class="ct-card-body">
                  <div class="ct-ident">
                    <font-awesome-icon :icon="['far', 'file']" class="ct-ident-ic" />
                    <span class="ct-ident-name">{{ p.name }}</span>
                    <span class="ct-ops-tag">{{ p.dataSource === 'Entries' ? 'report · many entries' : 'per entry' }}</span>
                  </div>
                  <div v-if="p.notes" class="ct-flow-summary">{{ p.notes }}</div>
                  <div class="ct-card-note">Review the live preview in the editor, adjust anything, then Save there — nothing is stored yet.</div>
                </div>
              </template>
              <template v-else-if="p.kind === 'canvasOps'">
                <div class="ct-card-head is-pending">
                  <span class="ct-card-title">Review canvas changes</span>
                  <div class="ct-card-actions">
                    <button class="ct-confirm" type="button"
                      :disabled="inflight || p._status === 'committing' || !checkedOpCount(p)"
                      @click="applyCanvasOpsProposal(p)">
                      <font-awesome-icon v-if="p._status === 'committing'" :icon="['fas', 'spinner']" spin />
                      Apply {{ checkedOpCount(p) }} checked
                    </button>
                  </div>
                </div>
                <div class="ct-card-body">
                  <div v-if="p.summary" class="ct-flow-summary">{{ p.summary }}</div>
                  <div class="ct-ops-list">
                    <label v-for="(op, oi) in p.ops" :key="`op-${oi}`" class="ct-ops-row"
                      :class="{ 'is-unviable': opProblem(p, oi) }"
                      :title="JSON.stringify(op, null, 1)">
                      <input type="checkbox" v-model="opChecks(p)[oi]" :disabled="!!opProblem(p, oi)" />
                      <span class="ct-ops-label">{{ opLabel(op) }}
                        <span v-if="opProblem(p, oi)" class="ct-ops-problem">{{ opProblem(p, oi) }}</span>
                      </span>
                      <span v-if="op.op === 'remove'" class="ct-ops-tag is-remove">remove</span>
                      <span v-else-if="isFormulaOp(op)" class="ct-ops-tag is-formula">formula</span>
                    </label>
                  </div>
                  <div v-if="p._errors" class="ct-proposal-errors">{{ p._errors }}</div>
                </div>
              </template>
              <template v-else-if="p._status === 'done'">
                <div class="ct-card-head is-done">
                  <span class="ct-card-title">{{ p.kind === 'modify' ? 'Module updated' : 'Module created' }}</span>
                </div>
                <div class="ct-card-body">
                  <div class="ct-ident">
                    <span v-if="proposalEmoji(p)" class="ct-ident-ic">{{ proposalEmoji(p) }}</span>
                    <i v-else class="fts-icon-locus ct-ident-ic"></i>
                    <span class="ct-ident-name">{{ proposalName(p) }}</span>
                    <button v-if="proposalCount(p, 'states') || proposalCount(p, 'activities')"
                      class="ct-ghost-btn ct-ident-action" type="button"
                      @click="showProposalDiagram(p)">Show diagram</button>
                  </div>
                </div>
              </template>
              <template v-else>
                <div class="ct-card-head is-pending">
                  <span class="ct-card-title">{{ proposalActionTitle(p) }}</span>
                  <div class="ct-card-actions">
                    <!-- Preview the proposed workflow as a full state/activity diagram. Shown whenever
                         there's a flow to draw (states or activities), independent of validity. -->
                    <button v-if="proposalCount(p, 'states') || proposalCount(p, 'activities')"
                      class="ct-ghost-btn" type="button" @click="showProposalDiagram(p)">
                      Show diagram
                    </button>
                    <button class="ct-confirm" type="button"
                      :disabled="inflight || !proposalValid(p) || p._status === 'committing'"
                      @click="confirmProposal(p)">
                      <font-awesome-icon v-if="p._status === 'committing'" :icon="['fas', 'spinner']" spin />
                      {{ proposalCommitLabel(p) }}
                    </button>
                  </div>
                </div>
                <div class="ct-card-body">
                  <div class="ct-ident">
                    <span v-if="proposalEmoji(p)" class="ct-ident-ic">{{ proposalEmoji(p) }}</span>
                    <i v-else class="fts-icon-locus ct-ident-ic"></i>
                    <span class="ct-ident-name">{{ proposalName(p) }}</span>
                  </div>
                  <!-- The card stays minimal: identity + flow line. What changed (a modify's
                       adds/removes, the proposal's rationale) belongs to the response PROSE
                       above the card, not the card body. Validation stays — it's operational. -->
                  <div v-if="proposalFlowSummary(p)" class="ct-flow-summary">{{ proposalFlowSummary(p) }}.</div>
                  <div v-if="proposalErrors(p)" class="ct-proposal-errors">{{ proposalErrors(p) }}</div>
                  <div v-if="!proposalValid(p)" class="ct-card-note is-danger">Fix validation errors before applying.</div>
                </div>
              </template>
            </div>
          </div>

          <!-- Draft-check card (studio, P4): the local canvasOps audit's findings — the
               live twin of the server's report-only save-time consistency check. -->
          <div v-if="msg.audit" class="ct-cards">
            <div class="ct-card">
              <div class="ct-card-head" :class="msg.audit.length ? 'is-pending' : 'is-done'">
                <span class="ct-card-title">
                  {{ msg.audit.length
                    ? `Draft check · ${msg.audit.length} finding${msg.audit.length === 1 ? '' : 's'}`
                    : '✓ Draft check — no issues found' }}
                </span>
              </div>
              <div class="ct-card-body" v-if="msg.audit.length">
                <div v-for="(row, ri) in msg.audit" :key="ri"
                  class="ct-audit-row" :class="`is-${row.severity}`">
                  <span class="ct-audit-dot"></span>
                  <span class="ct-audit-label">{{ row.label }}</span>
                </div>
              </div>
            </div>
          </div>
          <!-- Notification card (docs/notification-content-proposal.md D11): a bell row opened
               INTO the dock — the reader's content as the first card of a fresh conversation,
               composer beneath, the notification already attached as context. Rendered by the
               same markdown path as prose, so entry chips open through onListClick. -->
          <div v-if="msg.notification" class="ct-cards">
            <div class="ct-card ct-ntf">
              <template v-if="msg.notification.item">
                <div class="ct-card-head is-pending">
                  <span class="ct-ntf-source">{{ notificationCardLabel(msg.notification.item) }}</span>
                  <span v-if="msg.notification.item.source && msg.notification.item.source.aiComposed"
                    class="ct-ntf-ai" title="Written by GNey">AI</span>
                  <span class="ct-ntf-time">{{ notificationCardTime(msg.notification.item) }}</span>
                </div>
                <div class="ct-card-body">
                  <div class="ct-ntf-title">{{ msg.notification.item.title || 'Notification' }}</div>
                  <div v-if="msg.notification.item.actor" class="ct-ntf-meta">{{ msg.notification.item.actor }}</div>
                  <div v-if="notificationCardBody(msg.notification.item)" class="ct-md ct-ntf-body"
                    v-html="notificationCardBody(msg.notification.item)"></div>
                </div>
                <div class="ct-ntf-foot">
                  <button v-if="msg.notification.item.about && msg.notification.item.about.locusId"
                    class="ct-confirm" type="button" @click="openNotificationEntry(msg.notification.item)">Open entry</button>
                  <button v-if="notificationRoutineId(msg.notification.item)"
                    class="ct-ghost-btn" type="button" @click="openNotificationRoutine(msg.notification.item)">Open routine</button>
                  <button v-if="notificationSessionId(msg.notification.item)"
                    class="ct-ghost-btn" type="button" @click="openNotificationSession(msg.notification.item)">Open conversation</button>
                  <span class="ct-ntf-hint">Reading is free · asking uses GNey</span>
                </div>
              </template>
              <div v-else-if="msg.notification.error" class="ct-card-body ct-ntf-state">{{ msg.notification.error }}</div>
              <div v-else class="ct-card-body ct-ntf-state">Loading notification…</div>
            </div>
          </div>
          <!-- Analytic report card (create_analytics): an artifact RECEIPT, not an interaction
               card — it persists and restores; its only action, Open report, is idempotent and
               shows the report in the Analytic Bench. Never an iframe in the chat column. -->
          <div v-if="msg.reports && msg.reports.length" class="ct-cards">
            <div v-for="(r, ri) in msg.reports" :key="`rp-${ri}`" class="ct-card ct-report">
              <div class="ct-card-head is-pending">
                <span class="ct-card-title">{{ r.revised ? 'Report revised' : 'Report ready' }}</span>
                <div class="ct-card-actions">
                  <button class="ct-confirm" type="button" :disabled="!analyticsAvailable" @click="openReport(r)">Open report</button>
                </div>
              </div>
              <div class="ct-card-body">
                <div class="ct-ident">
                  <font-awesome-icon class="ct-ident-ic" :icon="['fas', 'chart-column']" />
                  <span class="ct-ident-name">{{ r.title }}</span>
                </div>
                <div class="ct-receipt-note">{{ reportScope(r) }}</div>
              </div>
            </div>
          </div>

          <!-- Publish-to-workspace confirm card (publish_report / create_analytics kind:'report',
               P2 §7c): a hybrid confirm — the workspace admin reviews and clicks Confirm, the commit
               rides back in confirmations[] (type "report") like every other card, and the card
               morphs into a receipt with "Open in Reports". Non-admins never get this card: the
               tool answers admin_required and GNey hands off in prose instead. -->
          <div v-if="msg.publishCards && msg.publishCards.length" class="ct-cards">
            <div v-for="(pc, pci) in msg.publishCards" :key="`pc-${pci}`" class="ct-card ct-report">
              <template v-if="pc._status === 'done' && pc._result">
                <div class="ct-card-head is-done">
                  <span class="ct-card-title">{{ pc._result.update ? 'Published report updated' : 'Published to Reports' }}</span>
                  <div class="ct-card-actions">
                    <button class="ct-confirm" type="button" :disabled="!analyticsAvailable" @click="openPublished(pc._result)">Open in Reports</button>
                  </div>
                </div>
                <div class="ct-card-body">
                  <div class="ct-ident">
                    <font-awesome-icon class="ct-ident-ic" :icon="['fas', 'share-from-square']" />
                    <span class="ct-ident-name">{{ pc._result.title || pc.title }}</span>
                  </div>
                  <div class="ct-receipt-note">Every workspace member can open it under Dashboard › Reports, with the data resolved under their own rights.</div>
                </div>
              </template>
              <template v-else>
                <div class="ct-card-head" :class="pc._status === 'done' || pc._status === 'error' ? 'is-error' : 'is-pending'">
                  <span class="ct-card-title">{{ publishCardTitle(pc) }}</span>
                  <div class="ct-card-actions">
                    <button class="ct-confirm" type="button"
                      :disabled="inflight || pc._status === 'committing' || pc._status === 'done'"
                      @click="confirmPending(pc)">
                      <font-awesome-icon v-if="pc._status === 'committing'" :icon="['fas', 'spinner']" spin />
                      Confirm
                    </button>
                  </div>
                </div>
                <div class="ct-card-body">
                  <div class="ct-ident">
                    <font-awesome-icon class="ct-ident-ic" :icon="['fas', 'chart-column']" />
                    <span class="ct-ident-name">{{ pc.title }}</span>
                  </div>
                  <div class="ct-receipt-note">{{ reportScope(pc) }}</div>
                  <div class="ct-receipt-note">{{ pc.update ? 'Confirming replaces the already-published version for every workspace member.' : 'Confirming publishes this report to Dashboard › Reports for every workspace member; each viewer sees the rows they are allowed to see.' }}</div>
                </div>
              </template>
            </div>
          </div>

          <!-- Access card (configure-access proposal §13.7, ruling D8): GNey's proposed change to who
               can open the module, run an activity or see a field, column, listing, print template or
               action. GNey never applies it: Approve sends the card back in confirmations[] (type
               "access"), the server plans it again on the module as it is now and saves it as this
               user, or refuses it if the module changed since. Every change and warning shows before
               Approve. -->
          <div v-if="msg.accessCards && msg.accessCards.length" class="ct-cards">
            <div v-for="(ac, aci) in msg.accessCards" :key="`ac-${aci}`" class="ct-card ct-access">
              <div class="ct-card-head"
                :class="ac._status === 'done' && ac._result ? 'is-done' : (ac._status === 'done' || ac._status === 'error' ? 'is-error' : 'is-pending')">
                <span class="ct-card-title">{{ accessCardTitle(ac) }}</span>
                <div v-if="ac._status !== 'done'" class="ct-card-actions">
                  <button class="ct-confirm" type="button"
                    :disabled="inflight || ac._status === 'committing'"
                    @click="confirmPending(ac)">
                    <font-awesome-icon v-if="ac._status === 'committing'" :icon="['fas', 'spinner']" spin />
                    Approve
                  </button>
                </div>
              </div>
              <div class="ct-card-body">
                <div class="ct-ident">
                  <font-awesome-icon class="ct-ident-ic" :icon="['fas', 'lock']" />
                  <span class="ct-ident-name">{{ ac.module }}</span>
                </div>
                <ul class="ct-access-changes">
                  <li v-for="(row, ri) in accessChangeRows(ac)" :key="`acr-${ri}`"
                    class="ct-access-change" :class="{ 'is-widening': row.widens }">
                    <span class="ct-access-item">{{ row.label }}</span>
                    <span class="ct-access-rule">{{ row.before }} → {{ row.after }}</span>
                    <span v-if="row.widens" class="ct-access-flag">Lets more people in</span>
                  </li>
                </ul>
                <ul v-if="ac.warnings && ac.warnings.length" class="ct-access-warnings">
                  <li v-for="(w, wi) in ac.warnings" :key="`acw-${wi}`">{{ w.message }}</li>
                </ul>
                <div v-if="ac._status !== 'done'" class="ct-receipt-note">Nothing changes until you approve. Approving saves a new version of {{ ac.module }}, made by you.</div>
              </div>
            </div>
          </div>

          <!-- billing gate card (402): an expected state with a next step, not an error bubble -->
          <div v-if="msg.billing" class="ct-billing-card">
            <div class="ct-billing-title">{{ billingCardTitle(msg.billing) }}</div>
            <div class="ct-billing-text">{{ billingCardText(msg.billing) }}</div>
          </div>

          <div v-if="msg.error" class="ct-msg-error">{{ msg.error }}</div>

          <!-- Detailed per-turn readout — model / tokens / cache / latency / credits. Internal
               staff only (detailedUsage); admins and end users only ever see the conversation
               total, below. -->
          <div v-if="detailedUsage && msg.usage && (msg.usage.input || msg.usage.output)" class="ct-msg-usage">
            <span v-if="msg.usage.model" class="ct-u-model" :title="`Model: ${msg.usage.model}`">🧠 {{ formatModel(msg.usage.model) }}</span>
            <span title="Input tokens (uncached)">↑ {{ formatNumber(msg.usage.input) }}</span>
            <span title="Output tokens">↓ {{ formatNumber(msg.usage.output) }}</span>
            <span v-if="msg.usage.cacheRead" class="ct-u-cache" title="Prompt tokens served from cache">⚡ {{ formatNumber(msg.usage.cacheRead) }}</span>
            <span v-if="msg.usage.toolCalls" class="ct-u-tools" :title="`${msg.usage.toolCalls} tool call${msg.usage.toolCalls === 1 ? '' : 's'} this turn`">⚙ {{ msg.usage.toolCalls }} tool {{ msg.usage.toolCalls === 1 ? 'call' : 'calls' }}</span>
            <span class="ct-u-total" title="Total tokens incl. cached">{{ formatNumber(totalTokens(msg.usage)) }} tok</span>
            <span v-if="msg.usage.latencyMs" class="ct-u-latency" title="Time taken this turn">⏱ {{ formatLatency(msg.usage.latencyMs) }}</span>
            <span class="ct-u-cost" :title="creditTitle(msg.usage)">{{ creditsReported(msg.usage) ? '' : '≈ ' }}{{ formatCredits(creditsOf(msg.usage)) }}</span>
          </div>
        </div>
      </div>

      <div v-if="inflight" class="ct-msg ct-msg--assistant">
        <div class="ct-msg-body">
          <div class="ct-thinking">
            <span class="ct-dot"></span><span class="ct-dot"></span><span class="ct-dot"></span>
            <span v-if="progressLabel || thinkingMsg" class="ct-progress">{{ progressLabel || thinkingMsg }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Total-only readout: the ONLY usage line admins and end users ever see is the total
         credits used across the whole conversation — never a per-turn breakdown, never token
         counts. Also what internal staff see while previewing the end-user view. -->
    <div v-if="!detailedUsage && chatTotals.credits > 0" class="ct-session ct-session--enduser"
      title="Total credits used in this conversation">
      <span class="ct-session-label">This conversation</span>
      <span class="ct-session-cost">{{ chatTotals.reported ? '' : '≈ ' }}{{ formatCredits(chatTotals.credits) }} used</span>
    </div>
    <!-- Internal staff: full per-session token breakdown plus the credit total.
         The tooltip keeps the raw provider USD — the figure that tallies against
         the provider invoice; credits are the customer charge, not a replacement. -->
    <div v-else-if="detailedUsage && chatTotals.total > 0" class="ct-session"
      :title="(chatTotals.reported ? 'Credits charged to the workspace wallet' : 'Includes estimated turns from before server-side pricing')
        + ' · provider cost ' + formatCost(chatTotals.cost)">
      <span class="ct-session-label">Session · {{ chatTotals.turns }} turn{{ chatTotals.turns === 1 ? '' : 's' }}</span>
      <span class="ct-session-toks">
        <span title="Input tokens">↑ {{ formatNumber(chatTotals.input) }}</span>
        <span title="Output tokens">↓ {{ formatNumber(chatTotals.output) }}</span>
        <span v-if="chatTotals.cacheRead" class="ct-u-cache" title="Cached prompt tokens">⚡ {{ formatNumber(chatTotals.cacheRead) }}</span>
        <span v-if="chatTotals.toolCalls" class="ct-u-tools" :title="`${chatTotals.toolCalls} tool calls total`">⚙ {{ chatTotals.toolCalls }}</span>
      </span>
      <span class="ct-session-cost">{{ chatTotals.reported ? '' : '≈ ' }}{{ formatCredits(chatTotals.credits) }} total</span>
    </div>

    <!-- composer -->
    <div class="ct-composer" :class="{ 'ct-composer--drag': attachDragOver }"
      @dragover.prevent="onComposerDragOver" @dragenter.prevent="onComposerDragOver"
      @dragleave="onComposerDragLeave" @drop.prevent="onComposerDrop">
      <!-- Removable context: what GNey is grounded on this turn. Dismissing a tag stops that item
           being sent (omission == removal); navigating to new context re-adds its tag, and the
           "+" re-attaches anything dismissed for the CURRENT view without navigating away. -->
      <div v-if="contextTags.length || dismissedContextCount" class="ct-ctx-tags">
        <span v-for="tag in contextTags" :key="tag.type + ':' + tag.key" class="ct-ctx-tag">
          <span v-if="tag.icon" class="ct-ctx-icon">{{ tag.icon }}</span>
          <span v-else class="ct-ctx-kind">{{ tag.kind }}</span>
          <span class="ct-ctx-label" :title="tag.label">{{ tag.label }}</span>
          <button type="button" class="ct-ctx-x" title="Remove this context" @click="dismissContext(tag)">×</button>
        </span>
        <button v-if="dismissedContextCount" type="button" class="ct-ctx-add"
          :title="`Re-attach the removed context (${dismissedContextCount})`"
          @click="restoreContext">+</button>
      </div>
      <!-- Attachments fold into the composer: chips sit just above the input; the "+" opens the
           picker. Drag-drop (whole composer) and paste (textarea) add files too.
           Not offered on the studio surface — its lane carries no attachments. -->
      <teammate-attachments v-if="!isStudio" ref="attach" compact v-model="chat.attachments" :disabled="inflight"
        :ws-id="workspaceId" :session-id="chat.sessionId" />
      <textarea ref="composer" rows="1" v-model="chat.draft" :disabled="inflight"
        class="ct-input" placeholder="Ask GNey anything…"
        @keydown="onKeydown" @input="autoGrow" @paste="onPaste" />
      <!-- bottom control row (Figma): attach · seniority pill · internal toggles · send -->
      <div class="ct-composer-row">
        <button v-if="!isStudio" class="ct-attach-btn" type="button" :disabled="inflight"
          title="Attach images &amp; files" @click="pickAttachment">
          <font-awesome-icon :icon="['fas', 'plus']" />
        </button>
        <!-- Local draft integrity check (studio, P4): the canvasOps audit over the open
             draft — no model, no network, results as a card in the conversation. -->
        <button v-if="isStudio" class="ct-checkdraft-btn" type="button" :disabled="inflight"
          title="Check the open draft for dangling references and invariant problems (runs locally)"
          @click="runDraftAudit">
          <font-awesome-icon :icon="['fas', 'check']" /> Check draft
        </button>
        <!-- Seniority/model picker is alpha (internal) only; everyone else runs on the default
             tier (see effectiveSeniority). Same gate as the Mock / End-user toggles below. -->
        <div v-if="isInternal" class="ct-level" :class="{ 'is-open': levelOpen }"
          title="How senior should GNey be? Higher levels use more capable (and costlier) models.">
          <button type="button" class="ct-level-btn" data-level-toggle
            :disabled="inflight" aria-label="GNey seniority level" aria-haspopup="listbox"
            :aria-expanded="levelOpen ? 'true' : 'false'" @click="toggleLevel">
            <font-awesome-icon class="ct-level-ic" :icon="['fas', 'graduation-cap']" />
            <span class="ct-level-txt">{{ seniorityLabel }}</span>
            <span v-if="isInternal && modelOverride" class="ct-level-model-tag" :title="modelOverride">{{ shortModelLabel(modelOverride) }}</span>
            <font-awesome-icon class="ct-level-caret" :icon="['fas', 'chevron-down']" />
          </button>
          <div v-if="levelOpen" ref="levelMenu" class="ct-level-menu" role="listbox">
            <button v-for="opt in seniorityOptions" :key="opt.value" type="button"
              class="ct-level-opt" :class="{ 'is-sel': opt.value === seniority }"
              :disabled="seniorityDisabled(opt.value)"
              :title="seniorityDisabled(opt.value) ? 'Design mode runs Senior and above' : null"
              role="option" :aria-selected="opt.value === seniority" @click="selectSeniority(opt.value)">
              <span class="ct-level-opt-txt">{{ opt.label }}</span>
              <font-awesome-icon v-if="opt.value === seniority" class="ct-level-opt-check" :icon="['fas', 'check']" />
            </button>
            <!-- Alpha-only: pin an exact model within the tier just picked above, instead of
                 leaving the server to draw one at random (see ResolveTierModel). -->
            <template v-if="isInternal && alphaModelOptions.length">
              <div class="ct-level-divider"></div>
              <button type="button" class="ct-level-opt ct-level-opt-model"
                :class="{ 'is-sel': !modelOverride }" role="option"
                :aria-selected="!modelOverride" @click="selectModel('')">
                <span class="ct-level-opt-txt">Auto (random in tier)</span>
                <font-awesome-icon v-if="!modelOverride" class="ct-level-opt-check" :icon="['fas', 'check']" />
              </button>
              <button v-for="m in alphaModelOptions" :key="m" type="button"
                class="ct-level-opt ct-level-opt-model" :class="{ 'is-sel': modelOverride === m }"
                role="option" :aria-selected="modelOverride === m" :title="m" @click="selectModel(m)">
                <span class="ct-level-opt-txt">{{ shortModelLabel(m) }}</span>
                <font-awesome-icon v-if="modelOverride === m" class="ct-level-opt-check" :icon="['fas', 'check']" />
              </button>
            </template>
          </div>
        </div>
        <!-- Mode pill: the state is never ambiguous — Analytics while the Bench is on screen. -->
        <span v-if="benchOpen" class="ct-mode-pill"
          title="You are on the Analytic Bench (Dashboard) — GNey is in analytics mode: read-only, reports on request. Leave the tab to return to operation mode.">
          <font-awesome-icon :icon="['fas', 'chart-column']" /> Analytics
        </span>
        <label v-if="canDesign" class="ct-design"
          :title="benchOpen ? 'Design is an operation-mode tool — leave the Analytic Bench tab first.' : 'Let GNey design and modify modules. Proposals appear with a Create/Apply button.'">
          <input type="checkbox" class="ct-design-cb" v-model="design"
            :disabled="inflight || benchOpen" @change="onDesignChange" />
          <font-awesome-icon class="ct-design-ic" :icon="['fas', 'pen-ruler']" />
          <span>Design</span>
        </label>
        <label v-if="isInternal" class="ct-design ct-mock"
          title="Alpha (internal only): return mock responses without calling the backend, to test the cards. Type 'module', 'modify', 'hybrid', 'pill', 'fill', 'ask', 'done' or 'chips' to mock a specific interaction.">
          <input type="checkbox" class="ct-design-cb" v-model="mockMode"
            :disabled="inflight" @change="onMockChange" />
          <span>Mock</span>
        </label>
        <label v-if="isInternal" class="ct-design ct-enduser"
          title="Internal only: preview the chat exactly as an end user sees it — the model, token usage and cost readouts are hidden. Display-only; doesn't change what's sent.">
          <input type="checkbox" class="ct-design-cb" v-model="endUserView"
            @change="onEndUserViewChange" />
          <span>End-user</span>
        </label>
        <button class="ct-send" type="button" :disabled="!canSend" @click="sendChat" title="Send">
          <font-awesome-icon :icon="['fas', 'arrow-up']" />
        </button>
      </div>
    </div>
    <!-- proposed-workflow preview (opened by a proposal's "Show diagram" button) -->
    <fts-flow-diagram-modal ref="ftsFlowDiagramModal" />
    <!-- revert checklist for applied canvas-ops cards (studio surface, D12) -->
    <fts-canvas-guard-dialog v-if="isStudio" ref="ftsCanvasGuardDialog" />
  </div>
</template>

<script>
import { commonStore } from "../store/commonStore";
import { clientStore } from "../store/clientStore";
import { store as canvasDesignStore } from "../store/canvasDesignStore";
import { buildCanvasDraft } from "../../business/canvasOps/studioDraft";
import { describeOpHuman, precheckOp, auditCanvas } from "../../business/canvasOps";
import FTSCanvasGuardDialog from "../design/FTSCanvasGuardDialog";
import { authorizationStore } from "../store/authorizationStore";
import commons from "../commons/commons";
import signalr from "../store/signalr";
import teammateChatMixin from "../teammate/teammateChatMixin";
import { blockFromError, blockTitle, blockText } from "./teammateBlocks";
import FTSGneyAi from "./FTSGneyAi";
import GradientAiBackground from "./FTSGradientAiBackground.vue";
import TeammateAttachments from "../teammate/TeammateAttachments";
import FTSFlowDiagramModal from "./FTSFlowDiagramModal";
import teammateMock from "../teammate/teammateMock";
import { isAccessCard, accessChangeRows, accessCardTitle } from "../teammate/accessCard";
import { contentSourceLabel } from "./notification/FTSNotificationContent";

const FAB_SIZE = 58;
const MARGIN = 24;

// Prepared-value rows shown on an Activity card before it folds behind "View more" (design).
const KV_VISIBLE = 3;
const DONE_KV_VISIBLE = 2;

// Light-hearted filler shown in the "thinking" bubble once a turn has been running a while with
// no specific tool step to report (THINKING_DELAY_MS), rotated every THINKING_ROTATE_MS so a long
// wait doesn't feel stalled. A real progress label (a running tool) always takes precedence.
const THINKING_DELAY_MS = 5000;
const THINKING_ROTATE_MS = 4000;
const THINKING_MESSAGES = [
  "Still thinking…",
  "Crunching the details…",
  "Working through it…",
  "Putting the pieces together…",
  "Almost there…",
  "Reading the room…",
  "Connecting the dots…",
  "Mulling it over…",
  "Doing the heavy lifting…",
  "Hang tight, nearly done…",
];
// Docked side-rail width bounds (px). Max is clamped further against the viewport at runtime
// so the app content keeps a usable minimum.
const DOCK_MIN = 320;
const DOCK_MAX = 900;
const DOCK_DEFAULT = 380;

// Seniority the user picks for GNey; the server maps it to a model tier (least → most capable).
// Values must match the backend SeniorityTiers keys. Persisted per-browser so the choice sticks.
const SENIORITY_KEY = "ftsTeammateSeniority";
const SENIORITY_OPTIONS = [
  { value: "junior", label: "Junior" },
  { value: "senior", label: "Senior" },
  { value: "expert", label: "Expert" },
  { value: "consultant", label: "Consultant" },
];
// Default for everyone: the cheapest/fastest tier. Alpha (internal) users can override via the
// seniority picker; non-alpha users always run on this (see effectiveSeniority).
const SENIORITY_DEFAULT = "junior";
function readStoredSeniority() {
  try {
    const v = window.localStorage.getItem(SENIORITY_KEY);
    return SENIORITY_OPTIONS.some((o) => o.value === v) ? v : SENIORITY_DEFAULT;
  } catch (e) {
    return SENIORITY_DEFAULT;
  }
}

// Exact-model pin within the chosen tier, for alpha/internal testers (gated to isInternal in the
// UI below) — lets them pick one of the tier's candidate models instead of leaving the server to
// draw one at random (see AITeammateService.ResolveTierModel). Sent as Model alongside Seniority;
// "" means auto (server picks within the tier). Persisted per-browser like seniority.
const MODEL_OVERRIDE_KEY = "ftsTeammateModelOverride";
function readStoredModelOverride() {
  try { return window.localStorage.getItem(MODEL_OVERRIDE_KEY) || ""; } catch (e) { return ""; }
}

// Design mode opts in to the module design/modify tools. Only honored server-side for
// Administrator / Workspace Admin (the switch is hidden for everyone else), and persisted
// per-browser so the choice sticks.
const DESIGN_KEY = "ftsTeammateDesign";
function readStoredDesign() {
  try { return window.localStorage.getItem(DESIGN_KEY) === "1"; } catch (e) { return false; }
}

// Internal-only mock mode: when on, the chat returns canned responses (teammateMock) instead of
// calling /api/teammate/chat — for alpha testing the cards without the backend. Gated to isInternal.
const MOCK_KEY = "ftsTeammateMock";
function readStoredMock() {
  try { return window.localStorage.getItem(MOCK_KEY) === "1"; } catch (e) { return false; }
}

// Internal-only "end-user view": when on, hide the per-turn / session usage metadata (model,
// token counts, cost) so staff can preview exactly what a customer sees. Display-only — it does
// not change what's sent to the server. Gated to isInternal and persisted per-browser.
const ENDUSER_KEY = "ftsTeammateEndUserView";
function readStoredEndUserView() {
  try { return window.localStorage.getItem(ENDUSER_KEY) === "1"; } catch (e) { return false; }
}

// Last window placement ('min' | 'normal' | 'side' | 'full'), persisted per-browser so the panel
// reopens where the user left it (e.g. docked) instead of dropping back to the mascot each reload.
const WINDOW_STATE_KEY = "ftsTeammateWindowState";
const WINDOW_STATES = ["min", "normal", "side", "full"];
function readStoredWindowState() {
  try {
    const v = window.localStorage.getItem(WINDOW_STATE_KEY);
    return WINDOW_STATES.indexOf(v) > -1 ? v : "min";
  } catch (e) {
    return "min";
  }
}

export default {
  name: "FTSClientTeammate",
  mixins: [teammateChatMixin],
  // Host adapter (studio-gney proposal D1/D10): the same shipped component serves two
  // hosts. surface="studio" re-points the context seams at canvasDesignStore, forces the
  // entry context off (Studio's ?id is the MODULE id), switches the endpoint to the
  // isolated studio lane, and attaches the code-stripped CanvasDraft while the diff is
  // dirty. Everything else — cards, composer, sessions, markdown, progress — is shared.
  props: {
    surface: { type: String, default: "client" },
  },
  components: {
    "fts-gney-ai": FTSGneyAi,
    "gradient-ai-background": GradientAiBackground,
    "teammate-attachments": TeammateAttachments,
    "fts-flow-diagram-modal": FTSFlowDiagramModal,
    "fts-canvas-guard-dialog": FTSCanvasGuardDialog,
  },
  data() {
    return {
      // 'min' (movable mascot) | 'normal' (floating popup) | 'side' (full-height
      // right rail that reflows the app content beside it) | 'full' (fullscreen).
      // Restored from localStorage so the panel reopens where the user left it.
      windowState: readStoredWindowState(),
      inflight: false,
      unread: false,
      // Transient label of the step the teammate is running this turn, pushed over SignalR
      // (signalr.teammateProgress). null between turns; shown in the "thinking" bubble.
      progressLabel: null,
      // Rotating light-hearted filler for a long, tool-less wait (see THINKING_MESSAGES). Set by
      // a timer started when the turn goes inflight; null until it's been thinking THINKING_DELAY_MS.
      thinkingMsg: null,
      // sessionId: stable per-conversation id sent as SessionId on every /chat turn so the
      // server groups this session's trace into one log file; resetChat() mints a fresh one.
      // title: the current conversation's title, shown in the header. Empty for a fresh chat
      // (the server auto-titles after the first turn); adopted from the session when resuming one.
      chat: { draft: "", history: [], attachments: [], sessionId: commons.newUUID(), title: "" },
      // Seniority level the user picked in the alpha-only picker; the effectiveSeniority computed
      // decides what actually rides on each /chat turn (non-alpha users are pinned to the default
      // tier). Maps server-side to a model tier. Persisted across reloads (see readStoredSeniority).
      seniority: readStoredSeniority(),
      seniorityOptions: SENIORITY_OPTIONS,
      // Whether the custom seniority dropdown menu is open (replaces the native <select> popup).
      levelOpen: false,
      // Alpha-only exact model pin within the current tier; sent as Model alongside Seniority.
      // "" means auto. Populated into modelTiers from /api/teammate/meta (isInternal only).
      modelOverride: readStoredModelOverride(),
      modelTiers: {},
      // Design mode toggle (gated to admins via canDesign). Sent as Design on each /chat turn,
      // which exposes the module design/modify tools server-side.
      design: readStoredDesign(),
      // Whether the Analytic Bench is on screen (the Dashboard's Reports / My Insights tab) — the
      // client-side fact Mode is derived from (analytic proposal §4g). Announced by the Bench over
      // the event bus; never picked by the model.
      benchOpen: false,
      // Internal alpha mock mode (gated to isInternal): short-circuits /chat with canned responses.
      mockMode: readStoredMock(),
      // Internal-only end-user preview (gated to isInternal): when on, hides the model/token/cost
      // usage readouts so staff see what a customer sees. Display-only; persisted per-browser.
      endUserView: readStoredEndUserView(),
      // true while a file is dragged over the composer, to show the drop outline
      attachDragOver: false,
      // movable mascot position; set on mount
      fab: { x: 0, y: 0 },
      dragging: false,
      moved: false,
      dragOffset: { x: 0, y: 0 },
      // docked side-rail width (px); persisted, and pushed to a CSS var that drives the
      // panel width, the content gutter, and the modal right-offset together.
      dockWidth: DOCK_DEFAULT,
      resizing: false,
      // friendly context for the open entry ({ entryId, documentId, title, moduleId }),
      // broadcast by the quick-view; null when no entry is on screen.
      entryContext: null,
      // snapshot of the single-entry activity form the user has open ({ module, activityId,
      // entryId, values }), broadcast by the quick-view; null when no fillable form is on screen.
      // Attached to each /chat turn so the teammate can help complete it (and fill it via fill_form).
      formContext: null,
      // Applied views by `${module}|${listingId}` — recorded as responses arrive so a Listing
      // chip can restore the exact filter/sort/search the teammate used (plain lookup, no
      // reactivity needed: only read inside click handlers).
      teammateViews: {},
      // Context the user has dismissed from the strip above the composer, keyed by the *instance*
      // it was dismissed for (module id / entry id / form key). A dismissal only suppresses that
      // exact instance — navigating to a different module/entry/form re-shows its tag automatically.
      dismissed: { module: null, entry: null, form: null, view: null, selection: null, script: null, notification: null },
      // The focused script editor's announcement (studio, D17 chip): { key, label, lang, script }.
      scriptContext: null,
      // The Content notification the user asked GNey about (reader "Ask GNey"): { id, title, label }.
      // Rides to /chat as NotificationId until dismissed or replaced (docs/notification-content-proposal.md D7).
      notificationContext: null,
      // Snapshot of the open listing view for the context strip's List tag (best-effort mirror —
      // the send path always pulls a fresh view, so gating never depends on this being current).
      viewSnapshot: null,
      // ---- conversation history picker ----
      // Whether the past-conversations dropdown is open (anchored under the header).
      historyOpen: false,
      // Fetched session list ({ sessionId, title, updatedAt, turnCount }), newest-first from the server.
      sessions: [],
      sessionsLoading: false,
      sessionsError: null,
      // Free-text filter over the session list (title match).
      sessionSearch: "",
      // sessionId currently being inline-renamed, and the in-progress title text.
      renamingId: null,
      renameDraft: "",
      // sessionId currently being deleted (shows a spinner, disables the row's delete button).
      deletingId: null,
      // sessionId currently being exported to a downloadable JSON log (internal-only optimization aid).
      downloadingId: null,
    };
  },
  computed: {
    canSend() {
      return !this.inflight && (this.chat.draft.trim().length > 0 || this.chat.attachments.length > 0);
    },
    // Label of the currently-selected seniority, shown on the dropdown pill.
    seniorityLabel() {
      const opt = this.seniorityOptions.find((o) => o.value === this.seniority);
      return opt ? opt.label : "";
    },
    // The seniority actually sent to the server. Alpha (internal) users get whatever they picked;
    // everyone else runs on the default tier — the picker is hidden for them, so any value left in
    // storage from before can't pin a costlier tier. Design mode runs Senior and above only
    // (the server floors it too, see AITeammateService.ChatAsync), so a junior level — picked or
    // defaulted — is sent as senior while Design is on.
    effectiveSeniority() {
      const level = this.isInternal ? this.seniority : SENIORITY_DEFAULT;
      return this.canDesign && this.design && level === "junior" ? "senior" : level;
    },
    // Model ids selectable under the current tier (alpha only); empty until /meta resolves.
    alphaModelOptions() {
      return this.modelTiers[this.seniority] || [];
    },
    // Whether the user may turn on design mode. Matches the server gate (UserCanDesign):
    // Administrator or Workspace Admin only — Consultant is excluded.
    canDesign() {
      const roles = authorizationStore.getters.userRoles || "";
      return roles.indexOf("Administrator") > -1 || roles.indexOf("Workspace Admin") > -1;
    },
    // Inistate/GNey staff (email domain or specific usernames) — gates the alpha-only mock toggle.
    isInternal() {
      return !!authorizationStore.getters.isInternal;
    },
    // The Analytic Bench / analytics mode — internal-first rollout (proposal §10); widen to the
    // workspaceGney cohort here when it graduates. The server gates it too (Claude.AnalyticsEnabled).
    analyticsAvailable() {
      return this.isInternal;
    },
    // Sent on every /chat turn as Mode. operation (default) | analytics (the Bench is open).
    mode() {
      return this.benchOpen ? "analytics" : "operation";
    },
    // Whether to show the detailed per-turn / per-session usage readout — model, token
    // counts, cache, latency and per-turn credits. That telemetry is internal-only: only
    // Inistate/GNey staff see it, and only when they're not previewing the end-user view.
    // Everyone else (workspace admins, end users, and staff in demo mode — isInternal is
    // demo-aware) sees just the conversation's total credit. See endUserView / isInternal.
    detailedUsage() {
      return this.isInternal && !this.endUserView;
    },
    // ---- live context derived from the host (no pickers) ----
    isStudio() {
      return this.surface === "studio";
    },
    workspaceId() {
      return this.isStudio
        ? canvasDesignStore.getters.workspaceId
        : clientStore.getters.workspaceId;
    },
    workspaceName() {
      return this.isStudio
        ? canvasDesignStore.getters.workspaceName
        : clientStore.getters.workspaceName;
    },
    currentModule() {
      if (this.isStudio) {
        const v = canvasDesignStore.state.vector;
        return v && v.id ? { id: v.id, name: v.name, emoji: v.emoji } : null;
      }
      const m = clientStore.state.module;
      return m && m.id ? m : null;
    },
    currentEntryId() {
      // In the Studio the route's ?id is the MODULE id — there is no entry context, ever.
      if (this.isStudio) return "";
      const raw = this.$route && this.$route.query && this.$route.query.id;
      if (!raw) return "";
      try {
        const decoded = commons.queryDecodeids(raw);
        return (decoded && decoded.entryId) ? String(decoded.entryId) : "";
      } catch (e) {
        return "";
      }
    },
    // Whether the open Studio draft differs from the save baseline — drives the "draft ·
    // unsaved changes" badge and whether CanvasDraft rides the next turn.
    studioDraftBadge() {
      if (!this.isStudio) return false;
      return !!buildCanvasDraft(
        canvasDesignStore.state.vector, canvasDesignStore.state.originalVector);
    },
    // The element selected in the designer (P4): rides the turn so "make this required"
    // grounds to a concrete id. The store's selection.item is the live canvas node (or the
    // 'start' pseudo-node, which has no id and is skipped).
    studioSelection() {
      if (!this.isStudio) return null;
      const sel = canvasDesignStore.state.selection;
      if (!sel || !sel.type || !sel.item || typeof sel.item !== "object" || sel.item.id == null)
        return null;
      return {
        type: sel.type,
        id: String(sel.item.id),
        name: sel.item.displayName || sel.item.name || "",
      };
    },
    /**
     * Friendly label for the open entry: its document id (e.g. "JB 19987"), title, or name when the
     * quick-view has broadcast them for this exact entry, otherwise the raw "entry <id>".
     */
    entryDisplay() {
      const id = this.currentEntryId;
      if (!id) return "";
      const ctx = this.entryContext;
      if (ctx && String(ctx.entryId) === String(id)) {
        const friendly = ctx.documentId || ctx.title || ctx.name;
        if (friendly) return friendly;
      }
      return `entry ${id}`;
    },
    /** Header label: the current conversation's title, falling back to the GNey wordmark for a fresh chat. */
    headerTitle() {
      return this.chat.title || "GNey";
    },
    /**
     * Dismissible context tags shown above the composer — module / entry / open form. Each is keyed
     * by its instance (module id, entry id, form key); a tag drops out while its instance matches a
     * dismissal, and reappears automatically when the user navigates to a different instance.
     */
    contextTags() {
      const tags = [];
      const mod = this.currentModule;
      if (mod && mod.id && this.dismissed.module !== String(mod.id)) {
        // The module's own emoji stands in for the kind label (a module icon reads faster than "MODULE").
        // Studio host: the draft badge shows WHICH truth GNey reads — the unsaved draft
        // supersedes the persisted module while the diff is dirty.
        const draftSuffix = this.studioDraftBadge ? " · draft (unsaved changes)" : "";
        tags.push({ type: "module", key: String(mod.id), kind: "Module", icon: mod.emoji || "", label: (mod.name || "Module") + draftSuffix });
      }
      const eid = this.currentEntryId;
      if (eid && this.dismissed.entry !== String(eid)) {
        tags.push({ type: "entry", key: String(eid), kind: "Entry", label: this.entryDisplay || `entry ${eid}` });
      }
      // The designer's current selection (studio host, P4) — dismissable like any context.
      const sel = this.studioSelection;
      if (sel) {
        const skey = `${sel.type}:${sel.id}`;
        if (this.dismissed.selection !== skey)
          tags.push({ type: "selection", key: skey, kind: "Selected", label: `${sel.type}: ${sel.name || sel.id}` });
      }
      // The open script (studio host, D17 chip) — the panel-side half of the isolated
      // script mode: while this chip is on, THAT one script rides the turn.
      if (this.isStudio && this.scriptContext && this.dismissed.script !== this.scriptContext.key) {
        tags.push({
          type: "script", key: this.scriptContext.key, kind: "Script",
          label: `${this.scriptContext.label} (${this.scriptContext.lang})`,
        });
      }
      const ntf = this.notificationContext;
      if (ntf && this.dismissed.notification !== ntf.id) {
        tags.push({ type: "notification", key: ntf.id, kind: "Notification", icon: "📨", label: ntf.title });
      }
      // List tag: the open listing (and its filter) rides to the server as `View` — surface it so
      // the user can see and drop it independently of the module. Snapshot-driven (see
      // refreshViewSnapshot); the send path re-pulls fresh, so gating never depends on freshness.
      const vs = this.viewSnapshot;
      if (mod && vs && String(vs.moduleId) === String(mod.id)) {
        const vkey = `${vs.moduleId}|${vs.listingId != null ? vs.listingId : ""}`;
        if (this.dismissed.view !== vkey) {
          const filtered = vs.filter && Array.isArray(vs.filter.items) && vs.filter.items.length;
          tags.push({
            type: "view", key: vkey, kind: "List",
            // The listing's own emoji stands in for the kind label, like the module tag.
            icon: vs.listingEmoji || "",
            label: (vs.listing || "Current list") + (filtered ? " · filtered" : ""),
          });
        }
      }
      const fkey = this.formKey();
      if (fkey && this.dismissed.form !== fkey) {
        // Label with the open activity's name ("Edit", "New", "Approve", …) when known.
        const fc = this.formContext;
        tags.push({ type: "form", key: fkey, kind: "Form", label: (fc && fc.activityName) || "Open form" });
      }
      return tags;
    },
    /**
     * How many context items the CURRENT view derives that the user has dismissed — i.e. tags the
     * strip's "+" would bring back. Dismissals for other instances (older modules/entries) don't
     * count; they already re-add themselves on navigation.
     */
    dismissedContextCount() {
      let n = 0;
      const mod = this.currentModule;
      if (mod && mod.id && this.dismissed.module === String(mod.id)) n++;
      const eid = this.currentEntryId;
      if (eid && this.dismissed.entry === String(eid)) n++;
      const vs = this.viewSnapshot;
      if (mod && vs && String(vs.moduleId) === String(mod.id)
        && this.dismissed.view === `${vs.moduleId}|${vs.listingId != null ? vs.listingId : ""}`) n++;
      const fkey = this.formKey();
      if (fkey && this.dismissed.form === fkey) n++;
      if (this.notificationContext && this.dismissed.notification === this.notificationContext.id) n++;
      return n;
    },
    contextHint() {
      if (this.currentEntryId && this.currentModule) {
        return `I can see this ${this.currentModule.name} entry. Ask me to summarize or act on it.`;
      }
      if (this.currentModule) {
        return `I'm scoped to "${this.currentModule.name}". Ask about its entries or run an activity.`;
      }
      return "Ask me anything about your workspace.";
    },
    suggestions() {
      if (this.isStudio) {
        if (this.currentModule) {
          return [
            `Explain how ${this.currentModule.name} is configured`,
            "Suggest improvements to this module",
            "What do this module's automations do?",
          ];
        }
        return ["Create a new module for…", "How do listings and filters work?", "What can you configure?"];
      }
      if (this.currentEntryId) {
        return ["Summarize this entry", "What can I do with this entry?", "What changed recently?"];
      }
      if (this.currentModule) {
        return [`Summarize ${this.currentModule.name}`, "Show me recent entries", "What activities are available?"];
      }
      return ["What can you do?", "List my modules", "What needs my attention?"];
    },
    /** Running session totals across every assistant turn (tokens, tool calls, cost). */
    chatTotals() {
      return this.computeTotals(this.chat.history);
    },
    /** Session list filtered by the search box (case-insensitive title match); order preserved. */
    filteredSessions() {
      const q = this.sessionSearch.trim().toLowerCase();
      if (!q) return this.sessions;
      return this.sessions.filter((s) => String(s.title || "").toLowerCase().includes(q));
    },
  },
  watch: {
    // Reset the drafted-but-unsent indicator once the panel is opened, and reserve a
    // gutter on the app shell while docked so content sits beside the rail (not under it).
    windowState(v) {
      if (v === "min" && this.historyOpen) this.closeHistory();
      if (v === "min" && this.levelOpen) this.closeLevel();
      if (v !== "min") {
        this.unread = false;
        // Refresh the List context tag for whatever the user is looking at now.
        this.refreshViewSnapshot(true);
        this.$nextTick(() => { this.autoGrow(); this.scrollToEnd(); this.focusComposer(); });
      }
      try { window.localStorage.setItem(WINDOW_STATE_KEY, v); } catch (e) { /* non-fatal */ }
      document.body.classList.toggle("gney-docked", v === "side");
      // Tell the open form whether to bother tracking/broadcasting its state for co-fill: only
      // while the panel is open (not minimized). Keeps the shared quick-view fully inert otherwise.
      this.signalFormInterest();
    },
    dockWidth() { this.applyDockWidth(); },
    "chat.sessionId"() { this.announceSession(); },
    // Drive the long-wait "thinking" filler off the turn lifecycle so we don't have to touch every
    // call site that flips inflight (sendChat, confirmPending, applyProposal).
    inflight(busy) {
      if (busy) this.startThinkingFiller();
      else {
        this.stopThinkingFiller();
        // A turn may have changed/applied the open list (applyTeammateView) — re-mirror the tag.
        this.refreshViewSnapshot();
      }
    },
    /*
     * SS05980 — the conversation is workspace-scoped, but the panel stays mounted across an
     * in-place workspace change (the deep-link resolver, switchWorkspaceForDeepLink, swaps the
     * workspace without reloading the SPA), so the previous workspace's turns and history would
     * carry over. Guarded on `prev`: the first resolve is undefined -> id on boot, which is not
     * a switch and must not churn the session id.
     */
    workspaceId(next, prev) {
      if (!prev || String(prev) === String(next)) return;
      this.closeHistory();
      // Sessions are user+workspace scoped server-side — the cached list is the old workspace's.
      this.sessions = [];
      this.sessionsLoading = false;
      this.unread = false;
      // Dismissals are keyed by module/entry/form instance, which no longer exist here.
      this.dismissed = { module: null, entry: null, form: null, view: null, selection: null, script: null, notification: null };
      this.resetChat();
      this.$nextTick(() => this.refreshViewSnapshot(true));
    },
    // Module navigation and listing switches (route query `l`) both retarget the List tag.
    currentModule() { this.$nextTick(() => this.refreshViewSnapshot(true)); },
    $route() { this.$nextTick(() => this.refreshViewSnapshot(true)); },
  },
  mounted() {
    this.placeFabDefault();
    try {
      const saved = parseInt(window.localStorage.getItem("gneyDockWidth"), 10);
      if (saved) this.dockWidth = this.clampDockWidth(saved);
    } catch (e) { /* ignore */ }
    this.applyDockWidth();
    // The windowState watcher only fires on change — mirror its body-class side effect for the
    // initial (restored) state so a panel reopened in the docked rail reserves the content gutter.
    document.body.classList.toggle("gney-docked", this.windowState === "side");
    // Seed the List context tag for a panel restored already-open (watchers fire on change only).
    if (this.windowState !== "min") this.refreshViewSnapshot(true);
    window.addEventListener("resize", this.clampFab);
    // Live mid-turn progress over SignalR: subscribe this chat session's group and listen for
    // its frames. Best-effort — if the hub isn't connected the chat still works, just without
    // the per-step status line.
    window.eventBus.$on("signalr.teammateProgress", this.onTeammateProgress);
    window.eventBus.$on("teammate:entryContext", this.onEntryContext);
    window.eventBus.$on("teammate:notificationContext", this.onNotificationContext);
    window.eventBus.$on("gney:openNotification", this.openNotificationCard);
    window.eventBus.$on("gney:open", this.open);
    window.eventBus.$on("teammate:formContext", this.onFormContext);
    // A quick-view that mounts/opens after us asks for our current interest; answer it (and
    // announce our initial state now in case a form is already open).
    window.eventBus.$on("teammate:queryFormInterest", this.signalFormInterest);
    // Studio host: the htmljs editor (hosted by FTSConsole) reports a drafted document's
    // save back to the card that opened it (D16).
    window.eventBus.$on("gney:htmlJsDraftSaved", this.onHtmlJsDraftSaved);
    // Studio host: the focused script editor announces its script (D17 chip) — the
    // client-surface form/entry context pattern, applied to scripts.
    window.eventBus.$on("teammate:scriptContext", this.onScriptContext);
    this.signalFormInterest();
    // Analytic Bench coupling (§4g): the Bench lives in the Dashboard page, and whether it is on
    // screen is our mode; it asks which session we're on for its "This chat" list, and it pre-fills
    // or focuses the composer.
    window.eventBus.$on("gney:benchState", this.onBenchState);
    window.eventBus.$on("gney:querySession", this.announceSession);
    window.eventBus.$on("gney:composerPrefill", this.onComposerPrefill);
    this.announceSession();
    // We may have mounted after the Bench (a direct load onto its tab) and missed its announce —
    // ask, so the mode we send is right from the first turn.
    window.eventBus.$emit("gney:queryBenchState");
    signalr.joinSession(this.chat.sessionId);
    // Alpha-only: fetch the tier->model lists so the level dropdown can offer an exact pick.
    if (this.isInternal) this.fetchModelTiers();
    // A Junior pick stored before Design was toggled on can't ride under it (design runs
    // Senior and above) — normalize once so the pill shows what will actually be sent.
    if (this.canDesign && this.design && this.seniority === "junior") this.selectSeniority("senior");
  },
  beforeDestroy() {
    clearTimeout(this._viewSnapTimer);
    window.removeEventListener("resize", this.clampFab);
    document.removeEventListener("mousemove", this.onDrag);
    document.removeEventListener("mouseup", this.stopDrag);
    document.removeEventListener("mousemove", this.onResize);
    document.removeEventListener("mouseup", this.stopResize);
    window.eventBus.$off("signalr.teammateProgress", this.onTeammateProgress);
    window.eventBus.$off("teammate:entryContext", this.onEntryContext);
    window.eventBus.$off("teammate:notificationContext", this.onNotificationContext);
    window.eventBus.$off("gney:openNotification", this.openNotificationCard);
    window.eventBus.$off("gney:open", this.open);
    window.eventBus.$off("teammate:formContext", this.onFormContext);
    window.eventBus.$off("teammate:queryFormInterest", this.signalFormInterest);
    window.eventBus.$off("gney:htmlJsDraftSaved", this.onHtmlJsDraftSaved);
    window.eventBus.$off("teammate:scriptContext", this.onScriptContext);
    window.eventBus.$off("gney:benchState", this.onBenchState);
    window.eventBus.$off("gney:querySession", this.announceSession);
    window.eventBus.$off("gney:composerPrefill", this.onComposerPrefill);
    // We're gone — release the quick-view from any co-fill tracking.
    window.eventBus.$emit("teammate:wantFormContext", false);
    signalr.leaveSession(this.chat.sessionId);
    this.stopThinkingFiller();
    document.removeEventListener("mousedown", this.onHistoryOutside);
    document.removeEventListener("mousedown", this.onLevelOutside);
    document.body.classList.remove("gney-docked", "gney-resizing");
  },
  methods: {
    /* ---------------- Analytic Bench coupling (analytic proposal §4g / §7) ---------------- */
    openReport(r) {
      if (r && r.id) window.eventBus.$emit("gney:openReport", { id: r.id });
    },
    /** A published report lives on the Dashboard's Reports tab (P2): route there by analytic id. */
    openPublished(result) {
      const id = result && (result.analyticId || result.id);
      if (id) window.eventBus.$emit("gney:openAnalytic", { id });
    },
    publishCardTitle(pc) {
      if (pc._status === "done" || pc._status === "error") return "Publishing failed";
      return pc.update ? "Update the published report?" : "Publish to workspace Reports?";
    },
    accessCardTitle(ac) {
      return accessCardTitle(ac);
    },
    accessChangeRows(ac) {
      return accessChangeRows(ac);
    },
    onBenchState(evt) {
      this.benchOpen = !!(evt && evt.open);
      // Analytics mode needs the chat on screen: the Bench IS the Dashboard's Reports / My Insights
      // tab (user ruling 2026-08-23), so un-minimise when the user lands there.
      if (this.benchOpen && this.windowState === "min") this.open();
      // Design is an operation-mode tool (the server forces it off in analytics) — untick it so the
      // pill shows what will actually be sent.
      if (this.benchOpen && this.design) { this.design = false; this.onDesignChange(); }
    },
    announceSession() {
      window.eventBus.$emit("gney:session", { sessionId: this.chat.sessionId });
    },
    // `{text}` pre-fills a revision brief; `{focus:true}` alone just brings the composer forward
    // (the Bench's "Ask GNey for an insight" — the chat is already in analytics mode on that tab).
    onComposerPrefill(evt) {
      const text = evt && evt.text ? evt.text : "";
      if (!text && !(evt && evt.focus)) return;
      if (this.windowState === "min") this.open();
      if (text) this.chat.draft = text;
      this.$nextTick(() => { this.autoGrow(); this.focusComposer(); });
    },
    reportScope(r) {
      if (!r) return "";
      const parts = [];
      if (r.module) parts.push(r.module);
      if (r.listingName) parts.push(r.listingName);
      if (typeof r.rowCount === "number") parts.push(`${r.rowCount} row${r.rowCount === 1 ? "" : "s"}${r.truncated ? " (capped)" : ""}`);
      return parts.join(" · ");
    },

    /* ---------------- window state ---------------- */
    open() { this.windowState = "normal"; },
    minimize() { this.windowState = "min"; this.$nextTick(this.clampFab); },
    toggleSide() { this.windowState = this.windowState === "side" ? "normal" : "side"; },

    /* ---------------- resize the docked rail ---------------- */
    clampDockWidth(w) {
      // never let the rail eat more than (viewport - 360px) so app content stays usable
      const max = Math.min(DOCK_MAX, window.innerWidth - 360);
      return Math.max(DOCK_MIN, Math.min(Math.round(w), Math.max(DOCK_MIN, max)));
    },
    applyDockWidth() {
      document.body.style.setProperty("--gney-dock-width", this.dockWidth + "px");
    },
    startResize() {
      this.resizing = true;
      // suppress the width transitions while dragging so the rail tracks the cursor 1:1
      document.body.classList.add("gney-resizing");
      document.addEventListener("mousemove", this.onResize);
      document.addEventListener("mouseup", this.stopResize);
    },
    onResize(e) {
      if (!this.resizing) return;
      // rail is anchored to the right edge; width = distance from cursor to that edge
      this.dockWidth = this.clampDockWidth(window.innerWidth - e.clientX);
    },
    stopResize() {
      this.resizing = false;
      document.body.classList.remove("gney-resizing");
      document.removeEventListener("mousemove", this.onResize);
      document.removeEventListener("mouseup", this.stopResize);
      try { window.localStorage.setItem("gneyDockWidth", String(this.dockWidth)); } catch (e) { /* ignore */ }
    },
    useSuggestion(text) {
      this.chat.draft = text;
      this.$nextTick(() => { this.autoGrow(); this.focusComposer(); });
    },

    /* ---------------- movable mascot ---------------- */
    placeFabDefault() {
      this.fab = {
        x: window.innerWidth - FAB_SIZE - MARGIN,
        y: window.innerHeight - FAB_SIZE - MARGIN,
      };
    },
    clampFab() {
      this.fab = {
        x: Math.max(MARGIN, Math.min(window.innerWidth - FAB_SIZE - MARGIN, this.fab.x)),
        y: Math.max(MARGIN, Math.min(window.innerHeight - FAB_SIZE - MARGIN, this.fab.y)),
      };
    },
    startDrag(e) {
      this.dragging = true;
      this.moved = false;
      this.dragOffset = { x: e.clientX - this.fab.x, y: e.clientY - this.fab.y };
      document.addEventListener("mousemove", this.onDrag);
      document.addEventListener("mouseup", this.stopDrag);
    },
    onDrag(e) {
      if (!this.dragging) return;
      // ignore micro-jitter so a normal click still opens the chat
      if (Math.abs(e.clientX - this.dragOffset.x - this.fab.x) > 3 ||
          Math.abs(e.clientY - this.dragOffset.y - this.fab.y) > 3) {
        this.moved = true;
      }
      let x = e.clientX - this.dragOffset.x;
      let y = e.clientY - this.dragOffset.y;
      x = Math.max(0, Math.min(window.innerWidth - FAB_SIZE, x));
      y = Math.max(0, Math.min(window.innerHeight - FAB_SIZE, y));
      this.fab = { x, y };
    },
    stopDrag() {
      this.dragging = false;
      document.removeEventListener("mousemove", this.onDrag);
      document.removeEventListener("mouseup", this.stopDrag);
    },
    onFabClick(e) {
      if (this.moved) { e.preventDefault(); e.stopPropagation(); this.moved = false; return; }
      this.open();
    },

    /* ---------------- composer ---------------- */
    onKeydown(evt) {
      // A CJK IME commit fires keydown with key === "Enter" — don't send half-composed text.
      // keyCode 229 covers older Safari, which doesn't set isComposing on that event.
      if (evt.isComposing || evt.keyCode === 229) return;
      if (evt.key === "Enter" && !evt.shiftKey) {
        evt.preventDefault();
        if (this.canSend) this.sendChat();
      }
    },
    onPaste(evt) {
      const items = evt.clipboardData && evt.clipboardData.items;
      const ref = this.$refs.attach;
      if (!items || !ref || typeof ref.addClipboardItems !== "function") return;
      // only swallow the paste when it actually carries files (e.g. a screenshot)
      if (ref.addClipboardItems(items)) evt.preventDefault();
    },
    /** "+" button → open the OS file picker on the (folded-in) attachments component. */
    pickAttachment() {
      const ref = this.$refs.attach;
      if (ref && typeof ref.pickFiles === "function") ref.pickFiles();
    },
    onComposerDragOver() {
      if (!this.inflight) this.attachDragOver = true;
    },
    onComposerDragLeave(evt) {
      // ignore moves between children; only clear when the cursor truly leaves the composer
      if (evt.currentTarget.contains(evt.relatedTarget)) return;
      this.attachDragOver = false;
    },
    onComposerDrop(evt) {
      this.attachDragOver = false;
      if (this.inflight) return;
      const dt = evt.dataTransfer;
      const ref = this.$refs.attach;
      if (dt && dt.files && dt.files.length && ref && typeof ref.addFiles === "function") {
        ref.addFiles(dt.files);
      }
    },
    autoGrow(evt) {
      const el = (evt && evt.target) || this.$refs.composer;
      if (!el) return;
      const styles = window.getComputedStyle(el);
      const lineHeight = parseFloat(styles.lineHeight) || 21;
      const verticalPadding =
        (parseFloat(styles.paddingTop) || 0) + (parseFloat(styles.paddingBottom) || 0);
      const maxHeight = Math.ceil((lineHeight * 5) + verticalPadding);
      el.style.height = "auto";
      el.style.height = Math.min(el.scrollHeight, maxHeight) + "px";
      const isOverflowing = el.scrollHeight > maxHeight;
      el.style.overflowY = isOverflowing ? "auto" : "hidden";
      // Native textarea scrolling can stop between rows. When typing at the end,
      // align the viewport to the bottom so it contains five complete lines.
      if (isOverflowing && el.selectionEnd === el.value.length) {
        el.scrollTop = el.scrollHeight - el.clientHeight;
      }
    },
    focusComposer() {
      const el = this.$refs.composer;
      if (el) el.focus();
    },
    scrollToEnd() {
      this.$nextTick(() => {
        const el = this.$refs.list;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    resetChat() {
      this.chat.history = [];
      this.chat.attachments = [];
      this.chat.title = "";
      // A cleared chat is a new conversation — mint a fresh session id and move the SignalR
      // progress subscription to it so live progress follows the new conversation.
      const prev = this.chat.sessionId;
      this.chat.sessionId = commons.newUUID();
      if (prev) signalr.leaveSession(prev);
      signalr.joinSession(this.chat.sessionId);
      this.progressLabel = null;
    },

    /* ---------------- conversation history picker ---------------- */
    toggleHistory() {
      if (this.historyOpen) { this.closeHistory(); return; }
      this.historyOpen = true;
      this.loadSessions();
      // Defer wiring the outside-click close + focusing search until the dropdown is in the DOM,
      // so the click that opened it doesn't immediately re-close it.
      this.$nextTick(() => {
        document.addEventListener("mousedown", this.onHistoryOutside);
        const el = this.$refs.histSearch;
        if (el) el.focus();
      });
    },
    closeHistory() {
      this.historyOpen = false;
      this.sessionSearch = "";
      this.cancelRename();
      document.removeEventListener("mousedown", this.onHistoryOutside);
    },
    /** Close the dropdown on a click anywhere outside it (and outside the toggle, which handles itself). */
    onHistoryOutside(evt) {
      const t = evt.target;
      if (t && t.closest && (t.closest(".ct-hist") || t.closest("[data-hist-toggle]"))) return;
      this.closeHistory();
    },
    /** Fetch the past-conversation list (newest-first; server auto-titles each session). */
    async loadSessions() {
      this.sessionsLoading = true;
      this.sessionsError = null;
      try {
        const res = (this.isInternal && this.mockMode)
          ? await this.mockPost(() => teammateMock.mockSessions())
          : await commonStore.state.client.get(
              // D4: each surface lists only its own history (client = null-or-absent rows).
              `/api/teammate/sessions?surface=${this.isStudio ? "studio" : "client"}`,
              this.requestConfig());
        const list = Array.isArray(res.data) ? res.data : (res.data && res.data.sessions) || [];
        // The server sorts newest-first and dates each session by its last turn (`lastTurnAt`);
        // mirror that onto `updatedAt` so the row's relative time reads it uniformly.
        this.sessions = list.map((s) => ({ ...s, updatedAt: s.lastTurnAt || s.updatedAt }));
        // Adopt the server's auto-generated title for the open conversation once it exists, so the
        // header stops showing the bare wordmark after the first turn has been titled.
        if (!this.chat.title) {
          const cur = this.sessions.find((s) => s.sessionId === this.chat.sessionId);
          if (cur && cur.title) this.chat.title = cur.title;
        }
      } catch (err) {
        this.sessionsError = this.errorMessage(err);
        this.reportError(err);
      } finally {
        this.sessionsLoading = false;
      }
    },
    /**
     * Resume a past conversation: pull its full message list and replay it into the chat, adopting
     * its sessionId so subsequent turns continue the same server-side session (and live progress
     * follows it over SignalR). The currently-open conversation is left as-is on the server — it's
     * already persisted under its own SessionId, so switching away loses nothing.
     */
    async openSession(s) {
      if (!s || this.inflight || this.renamingId === s.sessionId) return;
      if (s.sessionId === this.chat.sessionId) { this.closeHistory(); return; }
      this.inflight = true;
      try {
        const res = (this.isInternal && this.mockMode)
          ? await this.mockPost(() => teammateMock.mockSessionDetail(s.sessionId))
          : await commonStore.state.client.get(`/api/teammate/sessions/${encodeURIComponent(s.sessionId)}/turns`, this.requestConfig());
        const data = res.data || {};
        const turns = Array.isArray(data.turns) ? data.turns : [];
        const prev = this.chat.sessionId;
        this.chat.history = this.turnsToHistory(turns);
        this.chat.attachments = [];
        this.chat.draft = "";
        this.chat.sessionId = data.sessionId || s.sessionId;
        this.chat.title = data.title || s.title || "";
        if (prev && prev !== this.chat.sessionId) signalr.leaveSession(prev);
        signalr.joinSession(this.chat.sessionId);
        this.progressLabel = null;
        this.closeHistory();
        this.$nextTick(() => { this.autoGrow(); this.scrollToEnd(); this.focusComposer(); });
      } catch (err) {
        this.reportError(err);
      } finally {
        this.inflight = false;
      }
    },
    /**
     * Rebuild chat.history from the server's persisted turns (GET …/turns). Each append-only turn is
     * one user→assistant exchange, so it expands to up to two bubbles: the user message (with any
     * attachments) and the assistant reply. History is the conversation RECORD only — prose + usage.
     * Cards never restore: interaction cards (proposals, confirm cards, ask cards, form cards) are
     * live-turn surfaces, and commits are represented by their receipt line in the prose ("Module
     * «chip» created.", "«entry chip» has been confirmed … Status updated to X."), not action cards.
     */
    turnsToHistory(turns) {
      const out = [];
      for (const t of (turns || [])) {
        if (!t) continue;
        const atts = this.storedAttachments(t.attachments);
        if ((t.userMessage && t.userMessage.trim()) || atts.length) {
          const u = { role: "user", content: t.userMessage || "", attachments: atts };
          // Path-bearing stored refs ride along so buildHistory replays them — a restored
          // session keeps its files attachable, same as a live one.
          const refs = (Array.isArray(t.attachments) ? t.attachments : []).filter((a) => a && a.path);
          if (refs.length) u.attachmentRefs = refs;
          out.push(u);
        }
        const assistant = {
          role: "assistant",
          content: t.assistantResponse || "",
          // Report receipts DO restore (artifact receipts, not interaction cards — proposal §6).
          reports: Array.isArray(t.reports) ? t.reports : [],
          usage: this.usageFromTurn(t),
        };
        if (this.chatResponseHasContent(assistant) || assistant.usage) out.push(assistant);
      }
      return out;
    },
    /**
     * Map a persisted turn's stored attachment refs ({ name, path, mimeType, size }) to the chat
     * bubble's preview shape. The bytes aren't kept (only an S3 web ref), so images point at that
     * canonical path; non-images render as a named file chip.
     */
    storedAttachments(list) {
      return (Array.isArray(list) ? list : []).map((a) => {
        const isImage = /^image\//i.test((a && a.mimeType) || "");
        const path = (a && a.path) || null;
        // The raw "/s/…" ref isn't a servable route — thumbnails need the workspace
        // download URL (which redirects to a presigned S3 GET).
        const href = path ? this.chatFileHref(path) : null;
        return {
          isImage,
          dataUrl: isImage ? href : null,
          name: (a && a.name) || "file",
          path,
          mimeType: (a && a.mimeType) || "",
        };
      });
    },
    /** Normalize a turn's stored token/cost counts to the usage shape the bubble renders. */
    usageFromTurn(t) {
      const input = Number(t.inputTokens || 0);
      const output = Number(t.outputTokens || 0);
      if (!input && !output) return null;
      return {
        input,
        output,
        cacheRead: Number(t.cacheReadInputTokens || 0),
        cacheWrite: Number(t.cacheCreationInputTokens || 0),
        cost: t.costUsd != null ? Number(t.costUsd) : null,
        // The credits this turn charged when it ran (server receipt) — factor
        // changes since then must not rewrite a restored conversation's spend.
        credits: t.credits != null ? Number(t.credits) : null,
        toolCalls: Number(t.toolCalls || 0),
        latencyMs: Number(t.latencyMs || 0),
        model: t.model ? String(t.model) : "",
      };
    },
    /** "New" in the history dropdown: start a fresh conversation and close the picker. */
    newFromHistory() {
      this.resetChat();
      this.closeHistory();
    },
    /** Begin inline-renaming a session row; seed the field with the current title and select it. */
    startRename(s) {
      this.renamingId = s.sessionId;
      this.renameDraft = s.title || "";
      this.$nextTick(() => {
        const el = Array.isArray(this.$refs.renameInput) ? this.$refs.renameInput[0] : this.$refs.renameInput;
        if (el) { el.focus(); el.select(); }
      });
    },
    cancelRename() {
      this.renamingId = null;
      this.renameDraft = "";
    },
    /**
     * Commit an inline rename (Enter / blur). No-ops on an empty or unchanged title. Optimistically
     * updates the row, then PATCHes the server; on failure the list is reloaded to resync.
     */
    async commitRename(s, evt) {
      // ignore the Enter that commits an IME composition (keyCode 229 = older Safari)
      if (evt && (evt.isComposing || evt.keyCode === 229)) return;
      if (this.renamingId !== s.sessionId) return;
      const title = this.renameDraft.trim();
      this.renamingId = null;
      if (!title || title === s.title) { this.renameDraft = ""; return; }
      const previous = s.title;
      s.title = title;
      // Keep the header in sync when it's the open conversation being renamed.
      if (s.sessionId === this.chat.sessionId) this.chat.title = title;
      this.renameDraft = "";
      try {
        if (this.isInternal && this.mockMode) {
          await this.mockPost(() => ({}));
        } else {
          await commonStore.state.client.patch(
            `/api/teammate/sessions/${encodeURIComponent(s.sessionId)}`, { title }, this.requestConfig());
        }
      } catch (err) {
        s.title = previous;
        this.reportError(err);
      }
    },
    /**
     * Delete a past conversation. If it's the one currently open, the in-panel chat is reset to a
     * fresh session so the user isn't left talking into a deleted session.
     */
    async deleteSession(s) {
      if (!s || this.deletingId) return;
      this.deletingId = s.sessionId;
      try {
        if (this.isInternal && this.mockMode) {
          await this.mockPost(() => ({}));
        } else {
          await commonStore.state.client.delete(
            `/api/teammate/sessions/${encodeURIComponent(s.sessionId)}`, this.requestConfig());
        }
        this.sessions = this.sessions.filter((x) => x.sessionId !== s.sessionId);
        if (s.sessionId === this.chat.sessionId) this.resetChat();
      } catch (err) {
        this.reportError(err);
      } finally {
        this.deletingId = null;
      }
    },
    /** Download the currently-open conversation as a JSON log (internal-only optimization aid). */
    downloadCurrentLog() {
      if (!this.chat.sessionId) return;
      this.downloadSessionLog({ sessionId: this.chat.sessionId, title: this.chat.title });
    },
    /**
     * Export one of the user's OWN conversations to a downloadable JSON log — the full transcript
     * plus each turn's model / token / cache / cost / latency / tool-call-count metrics — so internal
     * staff can review a chat offline for prompt/model optimization. Own-session only: it reuses the
     * same user+workspace-scoped GET the history picker uses, so it can never reach another user's
     * chat. Pure client-side — the file is built and saved in the browser, no export endpoint.
     */
    async downloadSessionLog(s) {
      if (!s || !s.sessionId || this.downloadingId) return;
      this.downloadingId = s.sessionId;
      try {
        const res = (this.isInternal && this.mockMode)
          ? await this.mockPost(() => teammateMock.mockSessionDetail(s.sessionId))
          : await commonStore.state.client.get(
              `/api/teammate/sessions/${encodeURIComponent(s.sessionId)}/turns`, this.requestConfig());
        const data = res.data || {};
        const turns = Array.isArray(data.turns) ? data.turns : [];
        // Enrich with the richer session summary (module / seniority / rolling totals) from the loaded
        // list when we have it — the /turns payload itself only carries sessionId + title.
        const meta = this.sessions.find((x) => x.sessionId === s.sessionId) || {};
        const log = {
          exportedAt: new Date().toISOString(),
          app: "GNey TeamMate",
          kind: "teammate-chat-log",
          session: {
            sessionId: data.sessionId || s.sessionId,
            title: data.title || s.title || meta.title || "",
            module: meta.module != null ? meta.module : null,
            seniority: meta.seniority != null ? meta.seniority : null,
            turnCount: meta.turnCount != null ? meta.turnCount : turns.length,
            totalInputTokens: meta.totalInputTokens != null ? meta.totalInputTokens : null,
            totalOutputTokens: meta.totalOutputTokens != null ? meta.totalOutputTokens : null,
            totalCostUsd: meta.totalCostUsd != null ? meta.totalCostUsd : null,
            lastTurnAt: meta.lastTurnAt || meta.updatedAt || null,
          },
          turns: turns.map((t) => ({
            turnNumber: t.turnNumber,
            createdDate: t.createdDate || null,
            userMessage: t.userMessage || "",
            attachments: Array.isArray(t.attachments) ? t.attachments : [],
            assistantResponse: t.assistantResponse || "",
            actionsPerformed: Array.isArray(t.actionsPerformed) ? t.actionsPerformed : [],
            proposals: Array.isArray(t.proposals) ? t.proposals : [],
            pendingActions: Array.isArray(t.pendingActions) ? t.pendingActions : [],
            model: t.model || "",
            inputTokens: Number(t.inputTokens || 0),
            outputTokens: Number(t.outputTokens || 0),
            cacheReadInputTokens: Number(t.cacheReadInputTokens || 0),
            cacheCreationInputTokens: Number(t.cacheCreationInputTokens || 0),
            toolCalls: Number(t.toolCalls || 0),
            costUsd: t.costUsd != null ? Number(t.costUsd) : null,
            credits: t.credits != null ? Number(t.credits) : null,
            latencyMs: Number(t.latencyMs || 0),
          })),
        };
        this.saveJsonFile(`gney-log-${this.safeFileSlug(log.session.title || log.session.sessionId)}.json`, log);
      } catch (err) {
        this.reportError(err);
      } finally {
        this.downloadingId = null;
      }
    },
    /** Trim a title/id down to a filesystem-safe slug for the download filename. */
    safeFileSlug(value) {
      const base = String(value || "session").trim().toLowerCase()
        .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
      return (base || "session").slice(0, 60);
    },
    /** Serialize an object to pretty JSON and trigger a browser download (no server round-trip). */
    saveJsonFile(filename, obj) {
      const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    },
    /** Compact relative timestamp (now / 5m / 3h / 4d / 2w) for a session's last-updated time. */
    relativeTime(value) {
      if (!value) return "";
      const then = new Date(value).getTime();
      if (!isFinite(then)) return "";
      const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
      if (secs < 45) return "now";
      const mins = Math.floor(secs / 60);
      if (mins < 60) return `${mins}m`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h`;
      const days = Math.floor(hrs / 24);
      if (days < 7) return `${days}d`;
      const weeks = Math.floor(days / 7);
      if (weeks < 5) return `${weeks}w`;
      const months = Math.floor(days / 30);
      if (months < 12) return `${months}mo`;
      return `${Math.floor(days / 365)}y`;
    },
    /**
     * In-app links (entry/module refs, #/… hash routes) render as plain anchors so
     * the hash router navigates without a reload. When fullscreen, drop to the docked
     * view first so the destination isn't hidden behind the overlay.
     */
    onListClick(evt) {
      const a = evt.target && evt.target.closest && evt.target.closest("a");
      if (!a) return;

      // Listing chip → open that specific listing of the module. When a turn recorded an applied
      // view for the same module+listing, reuse it so the exact filter/sort/search the teammate
      // used comes along (openTeammateView stashes it for the destination list view); otherwise
      // the saved listing opens with its own designed filters.
      const chipListingId = a.getAttribute("data-listing-id");
      if (chipListingId && a.classList.contains("tmd-ref--list")) {
        evt.preventDefault();
        const chipModule = a.getAttribute("data-module") || "";
        this.openTeammateView(
          this.teammateViews[`${chipModule}|${chipListingId}`]
          || { module: chipModule, listingId: chipListingId });
        return;
      }

      // Activity reference pill → open that activity's form for the entry in place (edit, or a
      // custom activity), mirroring an open-form pill. data-activity-id is what distinguishes it
      // from a read-only entry chip. The AI never commits these — the user fills in and submits.
      const activityId = a.getAttribute("data-activity-id");
      if (activityId && a.classList.contains("tmd-ref--activity")) {
        evt.preventDefault();
        if (this.windowState === "full") this.windowState = "normal";
        window.eventBus.$emit("quickViewData", {
          id: a.getAttribute("data-entry-id") || undefined,
          vectorId: a.getAttribute("data-vector-id") || undefined,
          activityId,
          // Clicking a chip is an explicit "open this" — when the overlay already holds exactly
          // this target it refetches instead of ignoring the click. A form mid-fill is guarded
          // there (the quick view asks before discarding edits), so this never eats work.
          refresh: true,
        });
        return;
      }

      // Entry reference pill → open the entry's quick-view overlay in place rather than
      // navigating to its list view (which reloads the underlying list). Whatever list the
      // user is on stays put; the entry just opens on top, beside the docked rail.
      const entryId = a.getAttribute("data-entry-id");
      if (entryId && a.classList.contains("tmd-ref--entry")) {
        evt.preventDefault();
        if (this.windowState === "full") this.windowState = "normal";
        window.eventBus.$emit("quickViewData", {
          id: entryId,
          vectorId: a.getAttribute("data-vector-id") || undefined,
          // Re-clicking the chip for the entry already open refetches it — see the activity pill.
          refresh: true,
        });
        return;
      }

      // Module reference pill → navigate to its list view, but keep the chat open (just drop out
      // of fullscreen so the list isn't hidden behind the overlay). Clicking a module must never
      // close or minimize the teammate.
      if (a.classList.contains("tmd-ref--module")) {
        // The workflow diagram, if open, would hover over the list we're navigating to — close it
        // so the destination is visible. Leave every overlay untouched while a single-entry form
        // is open, though: the diagram may sit under that form and we must not disturb the edit.
        if (!this.formContext && this.$refs.ftsFlowDiagramModal) this.$refs.ftsFlowDiagramModal.hide();
        if (this.windowState === "full") this.windowState = "normal";
        return;
      }

      const href = a.getAttribute("href") || "";
      const inApp = a.target !== "_blank" && /^[#/]/.test(href);
      // Navigating to a list/module view — get out of the way so the user can see it.
      if (inApp) this.minimize();
    },

    /* ---------------- request plumbing (ported from the test bench) ---------------- */
    requestConfig() {
      const wsId = this.workspaceId;
      return wsId ? { headers: { wsId } } : {};
    },
    /** teammateChatMixin hook: workspace id for chat-attachment download URLs. */
    chatWorkspaceId() {
      return this.workspaceId || "";
    },
    /** Best-effort human-readable error text from an axios failure. */
    errorMessage(err) {
      const data = err && err.response && err.response.data;
      const fromBody = data && (data.message || data.error || (typeof data === "string" ? data : ""));
      const status = err && err.response && err.response.status;
      return fromBody || (err && err.message) || (status ? `Request failed (HTTP ${status})` : "Request failed");
    },
    /**
     * Typed block from the AI gates — the credit gate's 402s and the per-member GNey switch's
     * 403 (pricing spec §11 R3) — or null for any other failure. The rule and the copy live in
     * teammateBlocks.js.
     */
    billingBlockFromError(err) {
      return blockFromError(err);
    },
    billingCardTitle(b) {
      return blockTitle(b);
    },
    billingCardText(b) {
      return blockText(b);
    },
    /**
     * Push the turn's failure into the chat: billing 402s render as a card (expected state, not a
     * failure — no toast, no console.error); everything else keeps the generic error bubble + toast.
     */
    pushTurnError(err) {
      const billing = this.billingBlockFromError(err);
      if (billing) {
        this.chat.history.push({ role: "assistant", content: "", billing });
        return;
      }
      this.chat.history.push({ role: "assistant", content: "", error: this.errorMessage(err) });
      this.reportError(err);
    },
    /** Log full detail to the console and toast the user so failures are never silent. */
    reportError(err) {
      console.error("[GNey] /api/teammate/chat failed", {
        status: err && err.response && err.response.status,
        data: err && err.response && err.response.data,
        error: err,
      });
      try {
        if (this.$toasted && this.$toasted.global && this.$toasted.global.error) {
          this.$toasted.global.error(err, this.errorMessage(err));
        }
      } catch (e) { /* toast is best-effort */ }
    },
    /**
     * Live progress frame from the teammate's current turn (SignalR). Ignores frames for other
     * chat sessions, and is a no-op once the turn is no longer inflight (the real response, which
     * arrives in the /chat body, is authoritative — a late/dropped frame must not linger).
     */
    onTeammateProgress(p) {
      if (!p || p.sessionId !== this.chat.sessionId) return;
      if (!this.inflight) return;
      // Only advance the label when a new tool starts; leave the last action showing through
      // tool_done so it stays readable (tools finish in ms — clearing on done just flickers).
      // sendChat/confirmPending reset it at turn start and in their finally block.
      if (p.phase === "tool") {
        const label = commons.describeTeammateProgress(p);
        if (label) this.progressLabel = label;
      }
    },
    /**
     * Start the long-wait filler for the current turn: after THINKING_DELAY_MS of thinking, show a
     * random message, then rotate it every THINKING_ROTATE_MS. A real progress label (running tool)
     * still wins in the template — this only fills the silent gaps so a long turn doesn't look stuck.
     */
    startThinkingFiller() {
      this.stopThinkingFiller();
      this._thinkTimer = window.setTimeout(() => {
        this.thinkingMsg = this.pickThinkingMessage();
        this._thinkTimer = window.setInterval(() => {
          this.thinkingMsg = this.pickThinkingMessage();
        }, THINKING_ROTATE_MS);
      }, THINKING_DELAY_MS);
    },
    stopThinkingFiller() {
      if (this._thinkTimer) {
        // one ref holds either the initial timeout or the rotation interval; clear as both
        window.clearTimeout(this._thinkTimer);
        window.clearInterval(this._thinkTimer);
        this._thinkTimer = null;
      }
      this.thinkingMsg = null;
    },
    /** Pick a filler line, avoiding an immediate repeat of the one already showing. */
    pickThinkingMessage() {
      const pool = THINKING_MESSAGES.filter((m) => m !== this.thinkingMsg);
      const list = pool.length ? pool : THINKING_MESSAGES;
      return list[Math.floor(Math.random() * list.length)];
    },
    /** The quick-view tells us which entry is on screen (and its friendly document id). */
    onEntryContext(ctx) {
      this.entryContext = ctx || null;
    },
    /** The notification reader hands over the message the user wants to ask about. */
    onNotificationContext(ctx) {
      this.notificationContext = ctx && ctx.id ? { id: String(ctx.id), title: ctx.title || "Notification", label: ctx.label || "" } : null;
      if (this.notificationContext) this.$set(this.dismissed, "notification", null);
    },
    /**
     * A bell row opened INTO the dock (D11): a fresh conversation whose first card is the
     * notification itself, with the context attached — nothing is sent and no session is
     * stored until the user types. Clicking the same row again keeps the conversation that
     * already started from it. Mid-turn the dock can't swap sessions, so it falls back to
     * attaching the context only (the pre-D11 behaviour).
     */
    async openNotificationCard(ctx) {
      const id = ctx && ctx.id != null ? String(ctx.id) : "";
      if (!id) return;
      if (this.windowState === "min") this.open();
      this.closeHistory();
      const first = this.chat.history[0];
      if (first && first.notification && first.notification.id === id) {
        this.onNotificationContext({ id, title: ctx.title, label: ctx.label });
        return;
      }
      if (this.inflight) {
        this.onNotificationContext({ id, title: ctx.title, label: ctx.label });
        return;
      }
      if (this.chat.history.length) this.resetChat();
      const card = { role: "ai", content: "", notification: { id, loading: true, error: null, item: null } };
      this.chat.history.push(card);
      this.scrollToEnd();
      try {
        const res = await clientStore.dispatch("retrieveNotificationDetail", id);
        const item = res && res.data ? res.data : null;
        if (!item) throw new Error("empty");
        card.notification.item = item;
        this.onNotificationContext({ id, title: item.title || ctx.title || "Notification", label: this.notificationCardLabel(item) });
      } catch (err) {
        card.notification.error = err && err.response && err.response.status === 404
          ? "This notification is no longer available."
          : "Could not load the notification. Try again.";
      } finally {
        card.notification.loading = false;
        this.scrollToEnd();
      }
    },
    notificationCardLabel(item) { return contentSourceLabel(item) || "Notification"; },
    notificationCardTime(item) {
      const d = item && item.audit && item.audit.createdDate;
      return d ? commons.momentRelativeTime(d) : "";
    },
    /** Body HTML: markdown through the chat renderer (chips included); html as-is; text escaped. */
    notificationCardBody(item) {
      if (!item) return "";
      const body = item.body || item.summary || "";
      if (!body) return "";
      const fmt = item.bodyFormat || "markdown";
      if (fmt === "markdown") return this.renderMarkdown(body);
      if (fmt === "html") return body;
      return this.renderMarkdown(body.replace(/[*_`#>[\]]/g, "\\$&"));
    },
    notificationRoutineId(item) {
      const s = item && item.source;
      return s && String(s.kind || "").toLowerCase() === "routine" && s.routineId ? s.routineId : null;
    },
    /** The originating chat, openable only by its owner (sessions are per user). */
    notificationSessionId(item) {
      const s = item && item.source;
      if (!s || !s.sessionId) return null;
      const me = authorizationStore.state.userName;
      return item.actor && me && String(item.actor).toLowerCase() === String(me).toLowerCase() ? s.sessionId : null;
    },
    openNotificationEntry(item) {
      if (!item || !item.about || !item.about.locusId) return;
      window.eventBus.$emit("quickViewData", { id: item.about.locusId, vectorId: item.about.vectorId });
    },
    openNotificationRoutine(item) {
      const routineId = this.notificationRoutineId(item);
      if (!routineId) return;
      this.$router.push({ name: "routines", query: { routine: String(routineId) } }).catch(() => {});
    },
    openNotificationSession(item) {
      const sessionId = this.notificationSessionId(item);
      if (sessionId) this.openSession({ sessionId });
    },
    /** The quick-view tells us which single-entry activity form is open (and its current values). */
    onFormContext(ctx) {
      this.formContext = ctx || null;
    },
    /**
     * Broadcast whether the teammate wants the open form to track itself for co-fill — true only
     * while the panel is open (not minimized). The shared quick-view stays inert (no deep watch,
     * no broadcasts) for everyone else, including app contexts where this rail isn't even mounted.
     */
    signalFormInterest() {
      window.eventBus.$emit("teammate:wantFormContext", this.windowState !== "min");
    },
    /**
     * Hand pushed Form-card values to the open form (fired per card by pushFormCard, never on
     * response arrival). Each entry carries the form identity so the quick-view can confirm it
     * still matches before merging; nothing is committed (the user submits).
     */
    dispatchFormFill(data) {
      const fills = Array.isArray(data && data.formFill) ? data.formFill : [];
      fills.forEach((f) => window.eventBus.$emit("teammate:fillForm", f));
    },
    /**
     * Mock-only (internal alpha): make the Form-card scenario self-contained. The real fill_form
     * path assumes the user already has the target form open; in mock mode they usually don't. So
     * open each fill's activity form blank — the Form card then activates and the tester pushes
     * from it, exactly like the live path. Requires the mock's target module to exist in this
     * workspace (same best-effort constraint as the cards' "Open form" buttons).
     */
    mockOpenFormsForFills(data) {
      const fills = Array.isArray(data && data.formFill) ? data.formFill : [];
      fills.forEach((f) => {
        this.openFormPill({ module: f.module, activityId: f.activityId, entryId: f.entryId });
      });
    },
    /**
     * Mock-only (internal alpha): when the teammate opens a form for one of the mock modules, attach a
     * canned form schema so the quick-view renders a real, fillable form without a backend. Resolved
     * here (not in chatModules) so it only affects the open-form path — reference chips and "Open this
     * view" keep their normal behaviour and the fake module never leaks into the app's module lists.
     */
    decorateQuickViewData(evt, pa) {
      if (!(this.isInternal && this.mockMode)) return;
      const mod = teammateMock.resolveMockModule(pa && pa.module);
      if (!mod) return;
      evt.vectorId = mod.id;
      evt.mockForm = teammateMock.mockActivityForm({
        module: mod.name,
        emoji: mod.emoji,
        activityId: evt.activityId,
        entryId: evt.id,
        vectorId: mod.id,
      });
    },
    buildAssistantMessage(data) {
      const pendingActions = Array.isArray(data.pendingActions) ? data.pendingActions : [];
      const formPills = Array.isArray(data.formPills) ? data.formPills : [];
      // Publish-to-workspace proposals share the pendingActions[] wire (type "report") but are
      // their own card, not an Activity card — split them out before grouping.
      const publishCards = pendingActions.filter((pa) => pa && pa.type === "report");
      // So do access cards (type "access"): GNey's proposed change to who can do what, which only
      // the user's Approve applies — as an Activity row its changes would not show.
      const accessCards = pendingActions.filter(isAccessCard);
      return {
        role: "assistant",
        content: data.response || "",
        actions: Array.isArray(data.actions) ? data.actions : [],
        proposals: Array.isArray(data.proposals) ? data.proposals : [],
        pendingActions,
        formPills,
        // Activity cards grouped for rendering: a bulk fan-out (same module + activity)
        // becomes one paged group with per-page buttons and (confirm groups) Confirm all.
        // Items are the SAME objects as pendingActions/formPills, so per-item state
        // (_status/_opened) stays shared with every other code path.
        activityGroups: this.initActivityGroups(pendingActions.filter((pa) => !(pa && pa.type === "report") && !isAccessCard(pa)), formPills),
        publishCards,
        accessCards,
        // Clarifying-question cards (ask_user). Seed the per-question UI state up front so Vue
        // tracks it; the user's picks ride back next turn via submitAnswers -> payload.Answers.
        // _qPage is the visible question index — the card pages one question at a time (design).
        questions: this.initQuestions(data.questions),
        _questionsSent: false,
        _qPage: 0,
        // Form cards (fill_form). Values reach the open form only when the user pushes them
        // from the card — nothing is auto-applied on arrival.
        formCards: this.initFormCards(data.formFill),
        // Analytic report REFERENCES ({id, title, rowCount, ...}); bodies come from the Bench by id.
        reports: Array.isArray(data.reports) ? data.reports : [],
        usage: this.extractUsage(data),
        error: null,
      };
    },
    /**
     * Group a turn's Activity cards for rendering (ruling #6): items sharing the same variant +
     * module + activity — a bulk propose/pill fan-out — collapse into ONE paged group; everything
     * else is a group of one (rendered with no group chrome, exactly as before). Groups keep
     * first-appearance order; _page / _bulkCommitting are seeded here so Vue tracks them.
     */
    initActivityGroups(pendingActions, formPills) {
      const groups = [];
      const byKey = {};
      const add = (kind, items) => (items || []).forEach((pa) => {
        const key = `${kind}:${String(pa.module || "")}:${String(pa.activityId || pa.activity || "")}`.toLowerCase();
        let grp = byKey[key];
        if (!grp) {
          grp = { kind, key, items: [], _page: 0, _bulkCommitting: false };
          byKey[key] = grp;
          groups.push(grp);
        }
        grp.items.push(pa);
      });
      add("confirm", pendingActions);
      add("open", formPills);
      return groups;
    },
    /** The visible item index, clamped (mirrors askPage) so a stale _page can't point past the list. */
    groupPage(grp) {
      const n = (grp.items || []).length;
      return Math.max(0, Math.min(grp._page || 0, n - 1));
    },
    moveGroupPage(grp, delta) {
      const n = (grp.items || []).length;
      this.$set(grp, "_page", Math.max(0, Math.min(this.groupPage(grp) + delta, n - 1)));
    },
    /** Group bar caption: the shared activity over the module ("Pick Up · Issue Tracker"). */
    groupTitle(grp) {
      const first = (grp.items && grp.items[0]) || {};
      const activity = String(first.activity || first.activityId || "Activity");
      const label = `${activity.charAt(0).toUpperCase()}${activity.slice(1)}`;
      return first.module ? `${label} · ${first.module}` : label;
    },
    /** Foot hint: how far through the batch the user is ("2/4 confirmed" / "1/4 opened"). */
    groupProgress(grp) {
      const items = grp.items || [];
      const done = grp.kind === "confirm"
        ? items.filter((it) => it._status === "done").length
        : items.filter((it) => it._opened).length;
      return `${done}/${items.length} ${grp.kind === "confirm" ? "confirmed" : "opened"}`;
    },
    /** Items Confirm all would commit: not yet done/committing, and not taken over by an open form. */
    groupRemaining(grp) {
      return (grp.items || []).filter(
        (it) => it._status !== "done" && it._status !== "committing" && !it._opened);
    },
    /** After acting on the current page, page forward to the next item still awaiting the user. */
    advanceGroupPage(grp) {
      if (!grp || (grp.items || []).length < 2) return;
      const items = grp.items;
      const from = this.groupPage(grp);
      const awaiting = (it) => it._status !== "done" && !it._opened;
      for (let step = 1; step <= items.length; step += 1) {
        const i = (from + step) % items.length;
        if (awaiting(items[i])) {
          this.$set(grp, "_page", i);
          return;
        }
      }
    },
    /** Seed reactive UI state on each ask_user question so selections / free-text are tracked. */
    initQuestions(raw) {
      return (Array.isArray(raw) ? raw : []).map((q) => ({
        ...q,
        _sel: [],
        _custom: "",
        _customDraft: "",
        _completing: false,
      }));
    },
    /**
     * Seed reactive UI state on each fill_form result so the Form card can track its checklist.
     * Server rows (fields[]) carry label + current -> proposed + the exact formData keys each
     * checkbox controls; without them fall back to one row per raw value key. All checked by
     * default; keys no row claims are form plumbing and ride along with any push.
     */
    initFormCards(raw) {
      return (Array.isArray(raw) ? raw : []).map((f) => {
        const rows = (Array.isArray(f.fields) && f.fields.length
          ? f.fields
          : Object.keys(f.values || {}).map((k) => ({ fieldName: k, label: k, proposed: (f.values || {})[k] })))
          .map((r) => ({
            ...r,
            keys: Array.isArray(r.keys) && r.keys.length ? r.keys : [r.fieldName],
            _checked: true,
          }));
        return { ...f, _rows: rows, _pushed: false, _pushedLabel: "" };
      });
    },
    /**
     * Entry-chip HTML for an activity action that touched an entry (create / edit / any
     * entry-targeting activity), so a committed action always offers a clickable chip to open
     * the affected entry — the same chip the prose uses (clicks handled by onListClick via
     * delegation). Returns "" for module actions or actions without an entry, so the card falls
     * back to its plain "view" link. renderRef resolves the module by name (lookupModule).
     */
    actionEntryChip(act) {
      if (!act || act.type === "module" || act.entryId == null) return "";
      const label = act.documentId || `entry ${act.entryId}`;
      return this.renderRef(String(act.module || ""), String(act.entryId), String(label), null);
    },
    /** Persist the picked seniority so it sticks across reloads. */
    onSeniorityChange() {
      try { window.localStorage.setItem(SENIORITY_KEY, this.seniority); } catch (e) { /* non-fatal */ }
    },
    /** Open/close the custom seniority dropdown; wire outside-click close while open. */
    toggleLevel() {
      if (this.levelOpen) { this.closeLevel(); return; }
      this.levelOpen = true;
      // Defer wiring outside-click so the opening click doesn't immediately re-close it.
      this.$nextTick(() => document.addEventListener("mousedown", this.onLevelOutside));
    },
    closeLevel() {
      this.levelOpen = false;
      document.removeEventListener("mousedown", this.onLevelOutside);
    },
    /** Close the menu on a click outside it (and outside the toggle, which handles itself). */
    onLevelOutside(evt) {
      const t = evt.target;
      if (t && t.closest && (t.closest(".ct-level-menu") || t.closest("[data-level-toggle]"))) return;
      this.closeLevel();
    },
    /** Junior can't be picked while Design is on — design turns run Senior and above only. */
    seniorityDisabled(value) {
      return value === "junior" && this.canDesign && this.design;
    },
    /** Pick a seniority level from the custom dropdown, persist it, and close the menu. */
    selectSeniority(value) {
      if (this.seniorityDisabled(value)) return;
      if (value !== this.seniority) {
        this.seniority = value;
        this.onSeniorityChange();
        // A model pinned under the previous tier may not belong to the new one — drop it back to auto.
        if (this.modelOverride) this.selectModel("");
      }
      this.closeLevel();
    },
    /** Alpha-only: fetch the tier->model lists once so the level dropdown can offer an exact pick. */
    async fetchModelTiers() {
      try {
        const res = await commonStore.state.client.get("/api/teammate/meta", this.requestConfig());
        this.modelTiers = (res.data && res.data.modelTiers) || {};
      } catch (e) { /* non-fatal — dropdown just won't offer per-model picks */ }
    },
    /** Persist the picked model override (alpha only) so it sticks across reloads. */
    onModelOverrideChange() {
      try { window.localStorage.setItem(MODEL_OVERRIDE_KEY, this.modelOverride || ""); } catch (e) { /* non-fatal */ }
    },
    /** Pin an exact model within the current tier (alpha only), or "" to go back to auto. */
    selectModel(value) {
      const next = value || "";
      if (next !== this.modelOverride) {
        this.modelOverride = next;
        this.onModelOverrideChange();
      }
      this.closeLevel();
    },
    /** Strip the vendor prefix off a model id ("anthropic/claude-haiku-4.5" -> "claude-haiku-4.5"). */
    shortModelLabel(id) {
      if (!id) return "";
      const i = id.indexOf("/");
      return i === -1 ? id : id.slice(i + 1);
    },
    /** Persist the design-mode toggle so it sticks across reloads. */
    onDesignChange() {
      try { window.localStorage.setItem(DESIGN_KEY, this.design ? "1" : "0"); } catch (e) { /* non-fatal */ }
      // Design runs Senior and above only — bump a Junior pick to Senior (mirrors the
      // server-side floor, and also drops any model pinned under the junior tier).
      if (this.design && this.seniority === "junior") this.selectSeniority("senior");
    },
    /** Persist the internal-only mock-mode toggle. */
    onMockChange() {
      try { window.localStorage.setItem(MOCK_KEY, this.mockMode ? "1" : "0"); } catch (e) { /* non-fatal */ }
    },
    /** Persist the internal-only end-user preview toggle. */
    onEndUserViewChange() {
      try { window.localStorage.setItem(ENDUSER_KEY, this.endUserView ? "1" : "0"); } catch (e) { /* non-fatal */ }
    },
    // Short-circuit a /chat POST in mock mode (internal alpha only) — returns a canned { data }
    // shaped like the real response, after a small delay so the "thinking" state shows.
    mockPost(builder) {
      return new Promise((resolve) => {
        setTimeout(() => resolve({ data: builder() }), 350);
      });
    },
    async sendChat() {
      const text = this.chat.draft.trim();
      const attachments = this.chat.attachments.slice();
      if ((!text && !attachments.length) || this.inflight) return;

      const history = this.buildHistory(this.chat.history);
      this.chat.draft = "";
      this.chat.attachments = [];
      this.$nextTick(this.autoGrow);
      const userMsg = { role: "user", content: text, attachments: this.attachmentsPreview(attachments) };
      this.chat.history.push(userMsg);
      this.scrollToEnd();

      // Scope to the current view via Module/EntryId — the same contract the
      // teammate test bench uses. (No Context field: the /chat DTO doesn't bind it.)
      const payload = { Message: text, History: history };
      // Context is auto-derived from what the user is looking at, but anything they dismissed from
      // the strip above the composer is suppressed (omission == removal; the server holds no state).
      const moduleId = this.currentModule ? String(this.currentModule.id) : "";
      const moduleDropped = this.isCtxDismissed("module", moduleId);
      if (this.currentModule && !moduleDropped) payload.Module = this.currentModule.name;
      if (this.currentEntryId && !this.isCtxDismissed("entry", String(this.currentEntryId))) payload.EntryId = this.currentEntryId;
      // The notification the user is asking about (reader → "Ask GNey"): grounds the turn server-side.
      if (this.notificationContext && !this.isCtxDismissed("notification", this.notificationContext.id)) payload.NotificationId = this.notificationContext.id;
      if (this.chat.sessionId) payload.SessionId = this.chat.sessionId;
      if (this.effectiveSeniority) payload.Seniority = this.effectiveSeniority;
      // Alpha-only exact model pin; only sent while it's still valid for the current tier.
      if (this.isInternal && this.modelOverride && this.alphaModelOptions.includes(this.modelOverride)) {
        payload.Model = this.modelOverride;
      }
      if (this.canDesign && this.design) payload.Design = true;
      // The mode rides on every send, derived from Bench visibility (never model discretion).
      payload.Mode = this.mode;
      // Uploads were kicked off when the files were added; wait for the S3 refs so the
      // payload carries { name, path } instead of falling back to base64 (or dropping a
      // large file whose upload is still in flight). Composer is already cleared, so the
      // send button stays disabled while this waits.
      if (attachments.length) await this.awaitAttachmentUploads(attachments);
      if (attachments.length) payload.Attachments = this.attachmentsPayload(attachments);
      // Ground the turn in the listing/filter the user is currently looking at — unless the module
      // context or the List tag itself was dismissed (the listing belongs to that module).
      const currentView = this.collectCurrentView();
      const viewKey = currentView ? `${currentView.moduleId}|${currentView.listingId != null ? currentView.listingId : ""}` : "";
      if (currentView && !moduleDropped && !this.isCtxDismissed("view", viewKey)) payload.View = currentView;
      // ...and in the single-entry activity form they have open, so the teammate can help fill it.
      if (this.formContext && !this.isCtxDismissed("form", this.formKey())) payload.Form = this.formContext;

      this.inflight = true;
      this.progressLabel = null;
      try {
        const res = (this.isInternal && this.mockMode)
          ? await this.mockPost(() => teammateMock.mockChat(text, { design: this.design }))
          : await commonStore.state.client.post(this.chatEndpoint(), this.decorateStudioPayload(payload), this.requestConfig());
        // Stored refs for this turn's uploads (promoted to S3 server-side). Kept on the user
        // bubble and replayed in History (buildHistory) so the model can still attach these
        // files to File/Image fields — incl. table cells — on later turns.
        const refs = res.data && Array.isArray(res.data.attachments) ? res.data.attachments : [];
        if (refs.length) this.$set(userMsg, "attachmentRefs", refs);
        const msg = this.buildAssistantMessage(res.data || {});
        // Never leave the turn silent — if the agent returned no prose and no
        // actions, surface that rather than showing nothing.
        if (!this.chatResponseHasContent(msg)) {
          msg.content = "_(No response returned.)_";
        }
        this.chat.history.push(msg);
        // A create/edit this turn changed an entry — reload any open list showing it.
        this.refreshOpenListForActions(msg.actions);
        // Record the teammate's resulting view so its inline listing chip can restore the exact
        // filter when clicked (no "Open this view" card is rendered).
        if (res.data && res.data.view) this.applyTeammateView(res.data.view);
        // fill_form results render as Form cards on the reply — values reach the open form only
        // when the user pushes them (pushFormCard). In mock mode still open the target form so
        // the card is active without the tester pre-opening it.
        if (this.isInternal && this.mockMode) this.mockOpenFormsForFills(res.data || {});
      } catch (err) {
        this.pushTurnError(err);
      } finally {
        this.inflight = false;
        this.progressLabel = null;
        if (this.windowState === "min") this.unread = true;
        this.scrollToEnd();
      }
    },

    /** Stable per-question key for an option (label is unique within a question by validation). */
    optKey(o) { return o && o.label != null ? String(o.label) : ""; },
    isOptSel(q, o) { return !!q._sel && q._sel.indexOf(this.optKey(o)) !== -1; },
    /** Toggle an option: multi-select adds/removes; single-select replaces and clears free-text. */
    toggleOpt(q, o) {
      const key = this.optKey(o);
      const i = q._sel.indexOf(key);
      if (q.multiSelect) {
        if (i === -1) q._sel.push(key); else q._sel.splice(i, 1);
      } else {
        q._sel = i === -1 ? [key] : [];
        if (q._sel.length) {
          q._custom = "";
          q._customDraft = "";
        }
      }
    },
    /** Keep the inline "Something else" draft as the answer without submitting per keystroke. */
    updateCustom(q) {
      q._custom = (q._customDraft || "").trim();
      if (q._custom && !q.multiSelect) q._sel = [];
    },
    /** Commit an inline free-text answer; custom answers require explicit Send answers. */
    commitElse(q, evt) {
      // ignore the Enter that commits an IME composition (keyCode 229 = older Safari)
      if (evt && (evt.isComposing || evt.keyCode === 229)) return;
      q._custom = (q._customDraft || "").trim();
      if (!q._custom) return;
      if (!q.multiSelect) q._sel = [];
    },
    /* ---- Ask card paging (one question per page, design) ---- */
    /** The visible question index, clamped so a stale _qPage can never point past the list. */
    askPage(msg) {
      const n = (msg.questions || []).length;
      return Math.max(0, Math.min(msg._qPage || 0, n - 1));
    },
    askCurrent(msg) {
      return (msg.questions || [])[this.askPage(msg)];
    },
    movePage(msg, delta) {
      const n = (msg.questions || []).length;
      this.$set(msg, "_qPage", Math.max(0, Math.min(this.askPage(msg) + delta, n - 1)));
    },
    /** Questions the user has answered (a pick or a custom answer) — the "1/2 answered" hint. */
    answeredCount(msg) {
      return (msg.questions || []).filter((q) => (q._sel && q._sel.length) || q._custom).length;
    },
    askCurrentNeedsSubmit(msg) {
      const current = this.askCurrent(msg);
      return !!(current && (current.multiSelect || current._custom));
    },
    askHasMulti(msg) {
      return (msg.questions || []).some((q) => !!q.multiSelect);
    },
    questionAnswered(q) {
      return !!((q._sel && q._sel.length) || q._custom);
    },
    nextUnansweredPage(msg, current) {
      const questions = msg.questions || [];
      for (let offset = 1; offset < questions.length; offset += 1) {
        const index = (current + offset) % questions.length;
        if (!this.questionAnswered(questions[index])) return index;
      }
      return -1;
    },
    nextMultiPage(msg, current) {
      const questions = msg.questions || [];
      for (let offset = 1; offset <= questions.length; offset += 1) {
        const index = (current + offset) % questions.length;
        if (questions[index] && questions[index].multiSelect) return index;
      }
      return -1;
    },
    /**
     * Explicit confirmation for custom or multi-select answers. Continue through unanswered
     * questions first; a multi-select page must be the page that performs the final submission.
     */
    submitOrAdvanceAnswers(msg) {
      if (this.inflight || !msg || msg._questionsSent) return;
      const current = this.askPage(msg);
      const next = this.nextUnansweredPage(msg, current);
      if (next !== -1) {
        this.$set(msg, "_qPage", next);
        return;
      }
      if (this.askHasMulti(msg) && !this.askCurrent(msg).multiSelect) {
        const multiPage = this.nextMultiPage(msg, current);
        if (multiPage !== -1) {
          this.$set(msg, "_qPage", multiPage);
          return;
        }
      }
      this.submitAnswers(msg);
    },
    /**
     * A single-choice answer is complete as soon as it is selected. Move to the next unanswered
     * question. A fully answered set auto-submits only when it has no multi-select question;
     * otherwise return to a multi-select page for explicit Send answers confirmation.
     */
    completeQuestion(msg, q) {
      if (!this.questionAnswered(q) || q.multiSelect || q._completing || msg._questionsSent) return;
      q._completing = true;
      const completedPage = this.askPage(msg);
      window.setTimeout(() => {
        q._completing = false;
        if (msg._questionsSent) return;
        const next = this.nextUnansweredPage(msg, completedPage);
        if (next !== -1) {
          this.$set(msg, "_qPage", next);
        } else if (this.askHasMulti(msg)) {
          const multiPage = this.nextMultiPage(msg, completedPage);
          if (multiPage !== -1) this.$set(msg, "_qPage", multiPage);
        } else {
          this.submitAnswers(msg);
        }
      }, 250);
    },
    /**
     * Option click on the paged card: multi-select remains freely toggleable; a selected
     * single-choice answer advances, and the final answered question auto-submits the set.
     */
    pickOpt(msg, q, o) {
      this.toggleOpt(q, o);
      if (!q.multiSelect && q._sel.length) this.completeQuestion(msg, q);
    },
    /** The user's answers echoed as their turn — a Q/A quote block (design), or a skip note. */
    answersSummary(answers) {
      const list = answers || [];
      if (list.length && list.every((a) => a.skipped)) return "_Skipped the questions._";
      const lines = [];
      for (const a of list) {
        const picks = (a.selected || []).slice();
        if (a.custom) picks.push(`“${a.custom}”`);
        lines.push(`> **Q:** ${a.question || a.header}`);
        lines.push(`> **A:** ${a.skipped ? "(skipped)" : (picks.join(", ") || "(none)")}`);
      }
      return lines.join("\n");
    },
    /** Attach the same auto-derived context sendChat uses (module / entry / view / form / session). */
    applyContextToPayload(payload) {
      const moduleId = this.currentModule ? String(this.currentModule.id) : "";
      const moduleDropped = this.isCtxDismissed("module", moduleId);
      if (this.currentModule && !moduleDropped) payload.Module = this.currentModule.name;
      if (this.currentEntryId && !this.isCtxDismissed("entry", String(this.currentEntryId))) payload.EntryId = this.currentEntryId;
      // The notification the user is asking about (reader → "Ask GNey"): grounds the turn server-side.
      if (this.notificationContext && !this.isCtxDismissed("notification", this.notificationContext.id)) payload.NotificationId = this.notificationContext.id;
      if (this.chat.sessionId) payload.SessionId = this.chat.sessionId;
      if (this.effectiveSeniority) payload.Seniority = this.effectiveSeniority;
      if (this.isInternal && this.modelOverride && this.alphaModelOptions.includes(this.modelOverride)) {
        payload.Model = this.modelOverride;
      }
      if (this.canDesign && this.design) payload.Design = true;
      // The mode rides on every send, derived from Bench visibility (never model discretion).
      payload.Mode = this.mode;
      const currentView = this.collectCurrentView();
      const viewKey = currentView ? `${currentView.moduleId}|${currentView.listingId != null ? currentView.listingId : ""}` : "";
      if (currentView && !moduleDropped && !this.isCtxDismissed("view", viewKey)) payload.View = currentView;
      if (this.formContext && !this.isCtxDismissed("form", this.formKey())) payload.Form = this.formContext;
      return payload;
    },
    /**
     * Send the user's answers to a prior ask_user card back to /chat. Answers are context, not a
     * commit: they ride in payload.Answers and the server folds them into the next turn. A question
     * left untouched (or all, via Skip) is marked skipped so the model isn't left waiting.
     */
    async submitAnswers(msg, opts = {}) {
      if (this.inflight || !msg || msg._questionsSent) return;
      const skipAll = !!(opts && opts.skipAll);
      const answers = (msg.questions || []).map((q) => {
        const picked = (q._sel && q._sel.length) || q._custom;
        const skipped = skipAll || !picked;
        return {
          questionId: q.id,
          header: q.header,
          question: q.question,
          selected: skipped ? [] : (q._sel ? q._sel.slice() : []),
          custom: skipped ? null : (q._custom || null),
          skipped,
        };
      });

      const history = this.buildHistory(this.chat.history);
      const summary = this.answersSummary(answers);
      this.chat.history.push({ role: "user", content: summary });
      this.scrollToEnd();

      const payload = this.applyContextToPayload({ History: history, Answers: answers });

      msg._questionsSent = true;
      this.inflight = true;
      this.progressLabel = null;
      try {
        const res = (this.isInternal && this.mockMode)
          ? await this.mockPost(() => teammateMock.mockChat(summary, { design: this.design }))
          : await commonStore.state.client.post(this.chatEndpoint(), this.decorateStudioPayload(payload), this.requestConfig());
        const reply = this.buildAssistantMessage(res.data || {});
        if (!this.chatResponseHasContent(reply)) reply.content = "_(No response returned.)_";
        this.chat.history.push(reply);
        this.refreshOpenListForActions(reply.actions);
        if (res.data && res.data.view) this.applyTeammateView(res.data.view);
      } catch (err) {
        msg._questionsSent = false;
        this.pushTurnError(err);
      } finally {
        this.inflight = false;
        this.progressLabel = null;
        if (this.windowState === "min") this.unread = true;
        this.scrollToEnd();
      }
    },
    /**
     * Flatten an activity's display-name-keyed `input` object into { label, value } rows for
     * review on a card. Empty values dropped; a reference's { value, id } shows its value; arrays
     * are joined. Shared by the hybrid confirm card and the "done" receipt card.
     */
    kvRows(input) {
      if (!input || typeof input !== "object") return [];
      return Object.keys(input)
        .map((k) => ({ label: k, value: this.fmtCardValue(input[k]) }))
        .filter((f) => f.value != null && f.value !== "");
    },
    /** One-line display value shared by the review cards (kv rows + Form card rows). */
    fmtCardValue(v) {
      if (v == null || v === "") return null;
      if (Array.isArray(v)) {
        const parts = v.map((x) => this.fmtCardValue(x)).filter((x) => x != null);
        return parts.length ? parts.join(", ") : null;
      }
      if (typeof v === "object") {
        if (v.value != null && v.value !== "") return String(v.value);
        if (v.name != null && v.name !== "") return String(v.name);
        return null;
      }
      return String(v);
    },
    /** Prepared values on an Activity card: what Confirm submits (hybrid) / the form opens with (open). */
    pendingValues(pa) {
      return this.kvRows(pa && pa.input);
    },
    /** True when the reply carries Activity cards in either variant (confirm / open-form). */
    hasActivityCards(msg) {
      if (Array.isArray(msg.activityGroups)) return msg.activityGroups.length > 0;
      return !!((msg.pendingActions && msg.pendingActions.length)
        || (msg.formPills && msg.formPills.length));
    },
    /** Emoji of a module resolved by name (chatModules) — the identity icon on the cards. */
    moduleEmojiFor(name) {
      const mod = this.lookupModule(name);
      return mod && mod.emoji ? String(mod.emoji) : "";
    },
    /** Doc ref for a card's identity row ("Job Log | #JB 77576"): document id, else entry id. */
    pendingDocRef(pa) {
      if (!pa) return "";
      if (pa.documentId) return String(pa.documentId);
      return pa.entryId != null && pa.entryId !== "" ? String(pa.entryId) : "";
    },
    /** Prepared-value rows visible on an Activity card — first KV_VISIBLE until expanded. */
    visiblePendingValues(pa) {
      const rows = this.pendingValues(pa);
      return pa._kvExpanded ? rows : rows.slice(0, KV_VISIBLE);
    },
    /** How many prepared-value rows sit behind the "View more" fold. */
    kvOverflow(pa) {
      return Math.max(0, this.pendingValues(pa).length - KV_VISIBLE);
    },
    toggleKv(pa) {
      this.$set(pa, "_kvExpanded", !pa._kvExpanded);
    },
    /* ---- confirmed Activity card → in-place receipt (design) ---- */
    /**
     * Header title once a pending action is confirmed. Prefers the commit result's short message
     * (e.g. "Submitted for Review"); falls back to "«Activity» confirmed". A session-restored
     * card (committed in an earlier session, no _result) gets the fallback.
     */
    pendingReceiptTitle(pa) {
      const r = pa && pa._result;
      if (r && r.message) return r.message;
      const a = String((pa && (pa.activity || pa.activityId)) || "Activity");
      return `${a.charAt(0).toUpperCase()}${a.slice(1)} confirmed`;
    },
    /** Module name behind a confirmed receipt — commit result first, else the proposal. */
    pendingReceiptModule(pa) {
      const r = (pa && pa._result) || {};
      return String(r.module || (pa && pa.module) || "");
    },
    /**
     * Identity text on the confirmed receipt: the document ref ("JB 10082"), else "#entryId",
     * else the module name. Plain text (module emoji leads it in the template) — the clickable
     * entry chip lives in the receipt prose the server sends under the card.
     */
    pendingReceiptRef(pa) {
      const r = (pa && pa._result) || pa || {};
      const doc = r.documentId || (pa && pa.documentId);
      if (doc) return String(doc);
      const entryId = r.entryId != null ? r.entryId : (pa && pa.entryId);
      if (entryId != null && entryId !== "") return `#${entryId}`;
      return this.pendingReceiptModule(pa);
    },
    /** Resulting state shown beside the receipt chip (commit result, else the proposal's target). */
    pendingReceiptState(pa) {
      return (pa && pa._result && pa._result.state) || (pa && pa.state) || "";
    },
    /** Next-step activities on the confirmed receipt (from the commit result). */
    pendingReceiptNext(pa) {
      return pa && pa._result ? this.nextActivities(pa._result) : [];
    },
    /** Submitted values on a "done" receipt card. */
    actionValues(act) {
      return this.kvRows(act && act.input);
    },
    /** Successful create receipts read as the completed outcome, not the submitted verb. */
    doneActionLabel(act) {
      const activity = String((act && act.activity) || "").toLowerCase();
      return act && !act.error && activity === "create" ? "Created" : this.actionLabel(act);
    },
    /** Done receipts show two submitted values, then progressively reveal any remaining rows. */
    visibleActionValues(act) {
      const rows = this.actionValues(act);
      return act && act._kvExpanded ? rows : rows.slice(0, DONE_KV_VISIBLE);
    },
    actionValuesOverflow(act) {
      return Math.max(0, this.actionValues(act).length - DONE_KV_VISIBLE);
    },
    toggleActionValues(act) {
      this.$set(act, "_kvExpanded", !act._kvExpanded);
    },
    /**
     * Next-step buttons on a done card: the server's canvas-resolved rows
     * ({ id, name, standard, color?, emoji? } — custom ones carry their canvas id so the
     * form deep-link resolves). Entry actions only; module actions have no next step here.
     */
    nextActivities(act) {
      if (!act || act.type === "module" || act.entryId == null) return [];
      if (!Array.isArray(act.nextActivities)) return [];
      // Only the module's own user-defined activities read as next steps here. The standard verbs
      // (edit / delete / change status / assign / duplicate / comment / manage — all flagged
      // standard by the server) are chrome the user already has on the entry itself, so they're
      // dropped from the card; when a state has no custom activity, the section simply hides.
      return act.nextActivities.filter(na => na && !na.standard);
    },
    /** Button label for a next-step activity: custom = its name; standard = a friendly verb. */
    nextActivityLabel(na) {
      if (!na) return "";
      if (!na.standard) return String(na.name || na.id);
      const map = {
        edit: "Edit", delete: "Delete", comment: "Comment", duplicate: "Duplicate",
        changestatus: "Change status", manage: "Manage",
      };
      const key = String(na.id).toLowerCase();
      return map[key] || (String(na.id).charAt(0).toUpperCase() + String(na.id).slice(1));
    },
    /**
     * Open the next-step activity's form on the card's entry, in place — the button never
     * performs the activity; the user reviews and submits from the form (or lands on the
     * hybrid/human path there like any manually opened form).
     */
    openNextActivity(act, na) {
      this.openFormPill({
        module: act.module,
        activityId: na.id,
        entryId: act.entryId,
        activity: na.name,
        color: na.color,
        emoji: na.emoji,
      });
    },
    /** Resulting state of an activity action (not applicable to module actions). */
    actionState(act) {
      return (act && act.type !== "module" && act.state) ? act.state : "";
    },
    /**
     * "Open form" on an Activity card (both variants): open the activity form pre-filled so the user
     * can edit before submitting (submission goes through the normal form path — no Confirmations
     * post). Reuses the form-pill deep-link: prefill rides on pa.values (fieldName-keyed, set by the
     * server alongside the display-name `input`). On the hybrid variant, `_opened` also disables
     * Confirm to avoid a double submit; on the open variant it just shows the "submit it there" cue.
     */
    openPendingForm(pa, grp) {
      this.openFormPill(pa);
      this.$set(pa, "_opened", true);
      // In a paged bulk group, move on so the next form is one click away when they return.
      if (grp) this.advanceGroupPage(grp);
    },
    /* ---------------- Form card (fill_form review -> push) ---------------- */
    /**
     * A Form card can push only while the exact form it targets is still open — same activity,
     * module, and entry as the live form context (the quick-view re-checks on merge anyway).
     */
    formCardActive(fc) {
      const ctx = this.formContext;
      if (!ctx || !fc || !fc.activityId) return false;
      if (String(ctx.activityId) !== String(fc.activityId)) return false;
      if (fc.module && ctx.module !== fc.module) return false;
      if (fc.entryId != null && fc.entryId !== ""
        && String(ctx.entryId != null ? ctx.entryId : "") !== String(fc.entryId)) return false;
      return true;
    },
    checkedRowCount(fc) {
      return ((fc && fc._rows) || []).filter((r) => r._checked).length;
    },
    /** Header title on the Form card — the form it fills ("Update Leave Request"). */
    formCardTitle(fc) {
      return `Update ${fc && fc.module ? fc.module : "form"}`;
    },
    /** Muted "module · activity · #entry" caption — now the header title's tooltip. */
    formCardTarget(fc) {
      const bits = [];
      if (fc.module) bits.push(fc.module);
      if (fc.activityId) bits.push(fc.activityId);
      if (fc.entryId != null && fc.entryId !== "") bits.push(`#${fc.entryId}`);
      return bits.join(" · ");
    },
    /** "Select All" master checkbox state: every row checked… */
    allRowsChecked(fc) {
      const rows = (fc && fc._rows) || [];
      return rows.length > 0 && rows.every((r) => r._checked);
    },
    /** …or only some (renders the master checkbox indeterminate). */
    someRowsChecked(fc) {
      const n = this.checkedRowCount(fc);
      return n > 0 && n < (((fc && fc._rows) || []).length);
    },
    toggleAllRows(fc, evt) {
      const on = !!(evt && evt.target && evt.target.checked);
      ((fc && fc._rows) || []).forEach((r) => { r._checked = on; });
    },
    /** "a", "a and b", "a, b and c" — for the applied-receipt sentence. */
    joinAnd(list) {
      const l = list || [];
      if (!l.length) return "";
      if (l.length === 1) return l[0];
      return `${l.slice(0, -1).join(", ")} and ${l[l.length - 1]}`;
    },
    /**
     * One-line value for a Form card row — same formatting as the other cards' kv rows, plus a
     * "N rows" placeholder for table values that have no scalar rendering. Bare ISO dates are
     * prettified ("2026-07-03" → "Jul 3, 2026") to read as the form will show them (design).
     */
    fmtRowVal(v) {
      const s = this.fmtCardValue(v);
      if (s != null && s !== "") return this.fmtDateish(s);
      if (Array.isArray(v) && v.length) return `${v.length} row${v.length > 1 ? "s" : ""}`;
      return "";
    },
    fmtDateish(s) {
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s).trim());
      if (!m) return s;
      // Construct in local time (not Date-parse of the ISO string, which is UTC and can
      // shift a day in negative-offset timezones).
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      if (isNaN(d.getTime())) return s;
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    },
    /**
     * Push the checked subset into the open form via the normal fillForm event — the quick-view
     * re-checks the target and merges; nothing is committed (the user still submits the form).
     * Unchecked rows' keys (and their companions) stay out; keys no row claims are plumbing and
     * always ride. The card then freezes to a receipt (idempotent).
     */
    pushFormCard(fc) {
      if (fc._pushed || !this.formCardActive(fc)) return;
      const values = fc.values || {};
      const claimed = {};
      fc._rows.forEach((r) => r.keys.forEach((k) => { claimed[k] = true; }));
      const subset = {};
      fc._rows.filter((r) => r._checked).forEach((r) => r.keys.forEach((k) => {
        if (Object.prototype.hasOwnProperty.call(values, k)) subset[k] = values[k];
      }));
      Object.keys(values).forEach((k) => { if (!claimed[k]) subset[k] = values[k]; });
      this.dispatchFormFill({
        formFill: [{ module: fc.module, activityId: fc.activityId, entryId: fc.entryId, values: subset, note: fc.note }],
      });
      // Applied-receipt sentence (design): "Applied — Start Date, End Date and Reason have been
      // filled into the Leave Request form." — with the unchecked rows called out when any.
      const pushed = fc._rows.filter((r) => r._checked).map((r) => r.label);
      const skipped = fc._rows.filter((r) => !r._checked).map((r) => r.label);
      fc._pushedLabel = `Applied — ${this.joinAnd(pushed)} ${pushed.length === 1 ? "has" : "have"}`
        + ` been filled into the ${fc.module ? `${fc.module} ` : ""}form.`
        + (skipped.length ? ` (${this.joinAnd(skipped)} skipped.)` : "");
      fc._pushed = true;
    },

    async confirmPending(item, grp) {
      if (this.inflight || item._status === "committing" || item._status === "done" || item._opened) return;
      const conf = {};
      Object.keys(item).forEach((k) => { if (k[0] !== "_") conf[k] = item[k]; });

      this.$set(item, "_status", "committing");
      this.inflight = true;
      this.progressLabel = null;
      try {
        const payload = { Confirmations: [conf], History: this.buildHistory(this.chat.history) };
        if (this.currentModule) payload.Module = this.currentModule.name;
        if (this.currentEntryId) payload.EntryId = this.currentEntryId;
        // Same session id as the propose turn so the whole exchange lands in one log file.
        if (this.chat.sessionId) payload.SessionId = this.chat.sessionId;
        const res = (this.isInternal && this.mockMode)
          ? await this.mockPost(() => teammateMock.mockConfirm(conf))
          : await commonStore.state.client.post(this.chatEndpoint(), this.decorateStudioPayload(payload), this.requestConfig());
        const data = res.data || {};
        const resultMsg = this.buildAssistantMessage(data);
        // The commit receipt morphs INTO this card (green header + entry chip + resulting state,
        // per the design) — pull the committed activity result off the response and drop its
        // separate done card so the same receipt isn't shown twice. The response prose (which
        // references the entry inline) is kept.
        const doneIdx = (resultMsg.actions || []).findIndex(
          (a) => a && a.type !== "module" && !a.error && a.success !== false);
        if (doneIdx > -1) {
          this.$set(item, "_result", resultMsg.actions[doneIdx]);
          resultMsg.actions = resultMsg.actions.filter((_, i) => i !== doneIdx);
        }
        this.$set(item, "_status", "done");
        // In a paged bulk group, move to the next item still awaiting the user.
        if (grp) this.advanceGroupPage(grp);
        // Push even a prose-less turn when it carries usage, so session totals stay honest.
        if (this.chatResponseHasContent(resultMsg) || resultMsg.usage) this.chat.history.push(resultMsg);
        // The committed activity may have created/changed an entry — reload any open list
        // (from the raw response actions; resultMsg.actions was filtered above).
        this.refreshOpenListForActions(Array.isArray(data.actions) ? data.actions : []);
        if (data.view) this.applyTeammateView(data.view);
      } catch (err) {
        this.$set(item, "_status", "error");
        this.pushTurnError(err);
      } finally {
        this.inflight = false;
        this.progressLabel = null;
        this.scrollToEnd();
      }
    },

    /**
     * Confirm all — commits every remaining item of a paged bulk group in ONE round-trip:
     * the wire already carries N confirmations (Confirmations[]), and the server commits them
     * deterministically in order, one result each. Per-item outcomes map back onto the cards
     * (a confirmed item can still fail individually — ruling #6), so a partial failure leaves
     * the failed pages retryable while the rest morph into receipts.
     */
    async confirmAllPending(grp) {
      const items = this.groupRemaining(grp);
      if (this.inflight || grp._bulkCommitting || !items.length) return;
      const confs = items.map((item) => {
        const conf = {};
        Object.keys(item).forEach((k) => { if (k[0] !== "_") conf[k] = item[k]; });
        return conf;
      });

      items.forEach((it) => this.$set(it, "_status", "committing"));
      this.$set(grp, "_bulkCommitting", true);
      this.inflight = true;
      this.progressLabel = null;
      try {
        const payload = { Confirmations: confs, History: this.buildHistory(this.chat.history) };
        if (this.currentModule) payload.Module = this.currentModule.name;
        if (this.currentEntryId) payload.EntryId = this.currentEntryId;
        if (this.chat.sessionId) payload.SessionId = this.chat.sessionId;
        const res = (this.isInternal && this.mockMode)
          ? await this.mockPost(() => teammateMock.mockConfirm(confs))
          : await commonStore.state.client.post(this.chatEndpoint(), this.decorateStudioPayload(payload), this.requestConfig());
        const data = res.data || {};
        const resultMsg = this.buildAssistantMessage(data);
        // The server returns one result per confirmation, in order — zip them back onto the
        // items (guarded by entryId when both sides carry one) so each card morphs into its
        // own receipt or shows its own failure. Matched successes leave resultMsg.actions so
        // the same receipt isn't rendered twice; failures stay for the error card.
        const acts = (resultMsg.actions || []).filter((a) => a && a.type !== "module");
        const claimed = [];
        items.forEach((it, i) => {
          let act = acts[i];
          if (act && it.entryId != null && act.entryId != null
            && String(act.entryId) !== String(it.entryId)) {
            act = acts.find((a) => a && a.entryId != null && String(a.entryId) === String(it.entryId)) || act;
          }
          if (act && !act.error && act.success !== false) {
            this.$set(it, "_result", act);
            this.$set(it, "_status", "done");
            claimed.push(act);
          } else {
            this.$set(it, "_status", "error");
          }
        });
        resultMsg.actions = (resultMsg.actions || []).filter((a) => claimed.indexOf(a) === -1);
        this.advanceGroupPage(grp);
        if (this.chatResponseHasContent(resultMsg) || resultMsg.usage) this.chat.history.push(resultMsg);
        this.refreshOpenListForActions(Array.isArray(data.actions) ? data.actions : []);
        if (data.view) this.applyTeammateView(data.view);
      } catch (err) {
        items.forEach((it) => { if (it._status === "committing") this.$set(it, "_status", "error"); });
        this.pushTurnError(err);
      } finally {
        this.$set(grp, "_bulkCommitting", false);
        this.inflight = false;
        this.progressLabel = null;
        this.scrollToEnd();
      }
    },

    /**
     * Commits a design/modify proposal (create module or apply changes). The reviewed schema
     * travels back in Confirmations so the server commits it deterministically (no model);
     * mirrors confirmPending but for module proposals.
     */
    async confirmProposal(item) {
      if (this.inflight || item._status === "committing" || item._status === "done") return;
      const conf = { type: "module", kind: item.kind || "design", schema: this.proposalSchema(item) };
      if (item.module) conf.module = item.module;

      this.$set(item, "_status", "committing");
      this.inflight = true;
      this.progressLabel = null;
      try {
        const payload = { Confirmations: [conf], History: this.buildHistory(this.chat.history) };
        if (this.currentModule) payload.Module = this.currentModule.name;
        if (this.currentEntryId) payload.EntryId = this.currentEntryId;
        if (this.chat.sessionId) payload.SessionId = this.chat.sessionId;
        if (this.canDesign && this.design) payload.Design = true;
      // The mode rides on every send, derived from Bench visibility (never model discretion).
      payload.Mode = this.mode;
        const res = (this.isInternal && this.mockMode)
          ? await this.mockPost(() => teammateMock.mockConfirm(conf))
          : await commonStore.state.client.post(this.chatEndpoint(), this.decorateStudioPayload(payload), this.requestConfig());
        const data = res.data || {};
        // Only mark the button "done" if the server actually committed the module — a failed
        // commit comes back as a module action with an error, which we surface and let the user retry.
        const committed = (data.actions || []).find(
          (a) => a && a.type === "module" && a.success && !a.error);
        const moduleCommitted = !!committed;
        this.$set(item, "_status", moduleCommitted ? "done" : "error");
        // Stash the committed module on the proposal so proposalModuleChip can build the clickable
        // module pill rendered on the commit-response message — the receipt, in place of a card.
        if (moduleCommitted) {
          this.$set(item, "_committedModule", {
            moduleId: committed.moduleId != null ? committed.moduleId : null,
            name: committed.module || this.proposalName(item),
            kind: committed.kind || item.kind,
            // The proposal's icon rides along so the receipt chip carries the module's identity mark.
            emoji: this.proposalEmoji(item) || null,
          });
        }
        const resultMsg = this.buildAssistantMessage(data);
        // Drop the committed module's "Created module" action card — the module pill below now
        // represents it, so we don't show the same result twice. A failed commit (error) is kept.
        resultMsg.actions = (resultMsg.actions || []).filter(
          (a) => !(a && a.type === "module" && a.success && !a.error));
        // The receipt line — "Module «chip» created." — normally arrives as the turn's response
        // prose (a {{module}} token the server emits), which is what persists into history.
        // Building it here is only a fallback for a server that sent no prose.
        if (moduleCommitted && !(resultMsg.content && resultMsg.content.trim())) {
          const verb = (committed.kind || item.kind) === "modify" ? "updated" : "created";
          resultMsg.moduleChip = `Module ${this.proposalModuleChip(item)} ${verb}.`;
        }
        const pushed = this.chatResponseHasContent(resultMsg);
        if (pushed) this.chat.history.push(resultMsg);
        if (data.view) this.applyTeammateView(data.view);

        // A created/modified module changed the workspace schema — refresh so the new (or
        // updated) module shows up in the host's navigation. forceReload bypasses the version
        // cache so we always pull the fresh canvas.
        if (moduleCommitted) {
          try {
            if (this.isStudio) {
              // Studio host: open the committed module in the designer (the addVector
              // create-then-edit pattern) and refresh the studio menu.
              const committedId = committed.moduleId != null ? committed.moduleId : null;
              if (committedId != null) await canvasDesignStore.dispatch("loadVector", committedId);
              canvasDesignStore.dispatch("updateWorkspaceMenus");
            } else {
              await clientStore.dispatch("loadWorkspace", { forceReload: true });
            }
          }
          catch (e) { /* refresh is best-effort; the commit already succeeded */ }
        }
      } catch (err) {
        this.$set(item, "_status", "error");
        this.pushTurnError(err);
      } finally {
        this.inflight = false;
        this.progressLabel = null;
        this.scrollToEnd();
      }
    },

    /* ---------------- canvas-ops checklist card (studio, D9/D12) ---------------- */
    /** Lazily initialized per-op check state — every VIABLE op starts checked (the model
        was asked for the complete cascade; unchecking is the user's explicit narrowing).
        Rows that can never apply (malformed / unresolvable) start unchecked and disabled,
        with the reason inline — the user should never meet a raw refusal. */
    opChecks(p) {
      if (!p._checks) {
        this.ensureOpProblems(p);
        this.$set(p, "_checks", (p.ops || []).map((op, i) => !p._problems[i]));
      }
      return p._checks;
    },
    ensureOpProblems(p) {
      if (!p._problems) {
        const canvas = canvasDesignStore.state.canvas;
        this.$set(p, "_problems", (p.ops || []).map((op) => precheckOp(op, canvas)));
      }
      return p._problems;
    },
    opProblem(p, i) {
      return this.ensureOpProblems(p)[i];
    },
    checkedOpCount(p) {
      return this.opChecks(p).filter(Boolean).length;
    },
    opLabel(op) {
      return describeOpHuman(op, canvasDesignStore.state.canvas);
    },
    isFormulaOp(op) {
      return op.op === "set" &&
        ["formula", "visibleFormula", "readOnlyFormula", "requiredFormula"].indexOf(op.key) >= 0;
    },
    /** Apply exactly the checked ops through THE apply path (P1 engine, atomic, recorded).
        A refusal renders in-card naming the tier / referrer / unchecked dependency —
        partial application never occurs. */
    async applyCanvasOpsProposal(p) {
      if (!this.isStudio) return;
      const checks = this.opChecks(p);
      const checked = (p.ops || []).filter((op, i) => checks[i]);
      if (!checked.length) return;
      this.$set(p, "_status", "committing");
      this.$set(p, "_errors", null);
      try {
        const result = await canvasDesignStore.dispatch("applyCanvasOps", {
          ops: checked,
          source: "gney",
          label: p.summary || `GNey: ${checked.length} canvas edit${checked.length === 1 ? "" : "s"}`,
        });
        if (result.ok) {
          this.$set(p, "_status", "done");
          this.$set(p, "_recordId", result.record.id);
          this.$set(p, "_appliedCount", checked.length);
        } else {
          const errors = result.errors.concat(...result.ops.map((o) => o.errors));
          // dedupe repeated messages (N malformed rows would otherwise repeat one line N times)
          const unique = [...new Set(errors.map((e) => e.message))];
          this.$set(p, "_status", null);
          this.$set(p, "_errors",
            "Nothing was changed — the whole set is applied together, and:\n" +
            unique.slice(0, 5).map((m) => "• " + m).join("\n") +
            (unique.length > 5 ? `\n… +${unique.length - 5} more` : ""));
        }
      } catch (err) {
        this.$set(p, "_status", null);
        this.$set(p, "_errors", (err && err.message) || "The changes could not be applied.");
      }
    },
    /* ---------------- script context chip (studio, D17) ---------------- */
    /** The focused script editor's announcement. `closed` clears the chip only when it is
        the same editor (a modal closing must not drop a chip a later focus claimed). */
    onScriptContext(evt) {
      if (!this.isStudio || !evt || !evt.key) return;
      if (evt.closed) {
        if (this.scriptContext && this.scriptContext.key === evt.key) this.scriptContext = null;
        return;
      }
      this.scriptContext = {
        key: String(evt.key),
        label: evt.label || String(evt.key),
        lang: evt.lang || "",
        script: typeof evt.script === "string" ? evt.script : "",
      };
    },

    /* ---------------- htmljs document draft card (studio, D16) ---------------- */
    /** Hand the drafted bundle to the htmljs design modal (hosted by FTSConsole) over the
        global event bus — the modal's live preview is the review surface and its Save is
        the commit, exactly as when the designer opens it themselves. */
    openHtmlJsDraft(p) {
      if (!this.isStudio || !window.eventBus) return;
      const nonce = commons.newUUID();
      this.$set(p, "_nonce", nonce);
      this._htmljsPending = this._htmljsPending || {};
      this._htmljsPending[nonce] = p;
      window.eventBus.$emit("gney:openHtmlJsDraft", {
        nonce,
        item: {
          id: commons.newUUID(),
          name: p.name || "New Document",
          engine: "htmljs",
          dataSource: p.dataSource === "Entries" ? "Entries" : "Entry",
          bundle: p.bundle,
          page: p.page,
          version: 1.0,
        },
      });
    },
    onHtmlJsDraftSaved(evt) {
      const p = evt && this._htmljsPending && this._htmljsPending[evt.nonce];
      if (!p) return;
      this.$set(p, "_status", "done");
      this.$set(p, "_savedName", evt.name || p.name);
      delete this._htmljsPending[evt.nonce];
    },

    /** The local draft check (studio, P4): auditCanvas over the open draft, surfaced as a
        card in the conversation. No model, no network — pure engine. The message content
        replays into later turns' history, so GNey can pick the findings up next turn. */
    runDraftAudit() {
      if (!this.isStudio) return;
      const canvas = canvasDesignStore.state.canvas;
      if (!canvas) {
        this.$toasted.info("Open a module first — there is no draft to check.");
        return;
      }
      let rows;
      try {
        rows = auditCanvas(canvas);
      } catch (err) {
        console.error("[GNey] draft audit failed", err);
        this.$toasted.global.error({ message: "The draft check failed to run." });
        return;
      }
      const moduleName = (this.currentModule && this.currentModule.name) || "this module";
      const content = rows.length
        ? `Draft check over ${moduleName} (local, no AI): ${rows.length} finding${rows.length === 1 ? "" : "s"} — listed on the card.`
        : `Draft check over ${moduleName} (local, no AI): no dangling references or invariant problems found.`;
      this.chat.history.push({ role: "ai", content, audit: rows });
      this.scrollToEnd();
    },

    /** D19: trigger ids this applied card wrote whole automations for — each gets a
        Review button that opens the automation designer on it. */
    appliedAutomationIds(p) {
      if (!this.isStudio || p._status !== "done") return [];
      const ids = [];
      for (const op of p.ops || []) {
        if (op.op === "upsert" && op.address && op.address.length === 1 &&
            op.address[0][0] === "triggers" && op.item && op.item.automation)
          ids.push(String(op.address[0][1]));
        if (op.op === "set" && op.address && op.address.length === 1 &&
            op.address[0][0] === "triggers" &&
            (op.key === "automation" || op.key === "automationBefore") && !op.unset)
          ids.push(String(op.address[0][1]));
      }
      return [...new Set(ids)];
    },
    reviewAppliedAutomation(triggerId) {
      if (window.eventBus) window.eventBus.$emit("gney:openAutomation", { triggerId });
    },

    /** Revert an applied card: the record's inverses as an ordinary checklist proposal
        (conflicts unchecked) — same flow as the console's apply-history strip. */
    async revertCanvasOpsProposal(p) {
      const record = (canvasDesignStore.getters.applyHistory || [])
        .find((r) => r.id === p._recordId);
      if (!record) {
        this.$toasted.info("This change is no longer in the draft session's history — use the module's revision history instead.");
        return;
      }
      const revert = await canvasDesignStore.dispatch("composeRevert", { record });
      const rows = revert.rows.map((row) => ({
        label: describeOpHuman(row.op, canvasDesignStore.state.canvas),
        reason: row.reason, conflict: row.conflict, op: row.op,
      }));
      const res = await this.$refs.ftsCanvasGuardDialog.show({
        mode: "revert", subject: record.label, revert: { rows },
      });
      if (!res) return;
      const result = await canvasDesignStore.dispatch("applyRevert", { record, ops: res.ops });
      if (result.ok) {
        this.$set(p, "_status", null);
        this.$set(p, "_recordId", null);
        this.$toasted.info(`Reverted: ${record.label}`);
      } else {
        const first = result.errors.concat(...result.ops.map((o) => o.errors))[0];
        this.$toasted.global.error({
          message: first ? first.message : "The revert could not be applied cleanly.",
        });
      }
    },

    /* ---------------- design/modify proposal helpers ---------------- */
    // The RAW proposal schema, exactly as the server sent it. This is what travels back
    // verbatim on commit (confirmProposal) — never normalize what we send.
    proposalSchema(p) {
      return (p && (p.schema || p.proposedSchema)) || {};
    },
    /**
     * The proposal schema mapped to the shape the card + flowDiagram render against. The design
     * backend emits a name-keyed schema — states/activities carry no `id`, flows reference states
     * and activities by name (`from`/`to`/`activity`), fields are keyed `name`, and the module icon
     * lives under `icon`. The rendering + diagram code keys on `id`, `fromStateId`/`toStateId`/
     * `activityId`, `fieldName`/`displayName` and `emoji`. normalizeSchema bridges the two for display
     * only; the original schema is untouched and still round-trips on commit. Idempotent — a schema
     * that already carries ids passes straight through.
     */
    displaySchema(p) {
      return this.normalizeSchema(this.proposalSchema(p));
    },
    normalizeSchema(schema) {
      const s = schema || {};
      const arr = (a) => (Array.isArray(a) ? a : []);
      // States/activities: synthesize an id from the name when the backend didn't send one, so the
      // name-based flows below resolve against it (and the diagram, which keys on id, lines up).
      const states = arr(s.states).map((st) => ({ ...st, id: st && st.id != null ? st.id : st && st.name }));
      const activities = arr(s.activities).map((a) => ({ ...a, id: a && a.id != null ? a.id : a && a.name }));
      // Name → id lookups (case-insensitive): a modify schema carries REAL canvas ids on states/
      // activities while its flows still reference them by name, so names must resolve to the ids
      // the diagram nodes use — passed through raw, edges point at nonexistent nodes and cytoscape
      // rejects the whole graph (blank diagram). Design schemas are unaffected: their synthesized
      // ids ARE the names. An empty/absent ref stays undefined so getEdges' start/any fallback runs.
      const idByName = (list) => {
        const m = {};
        list.forEach((x) => { if (x && x.name != null && x.name !== "") m[String(x.name).toLowerCase()] = x.id; });
        return m;
      };
      const stateIdByName = idByName(states);
      const activityIdByName = idByName(activities);
      const ref = (id, name, map) => {
        if (id != null && id !== "") return id;
        if (name == null || name === "") return undefined;
        const hit = map[String(name).toLowerCase()];
        return hit != null ? hit : name;
      };
      const flows = arr(s.flows).map((f, i) => ({
        ...f,
        id: f && f.id != null ? f.id : `flow-${i}`,
        fromStateId: ref(f && f.fromStateId, f && f.from, stateIdByName),
        toStateId: ref(f && f.toStateId, f && f.to, stateIdByName),
        activityId: ref(f && f.activityId, f && f.activity, activityIdByName),
      }));
      const information = arr(s.information).map((inf) => ({
        ...inf,
        fieldName: (inf && (inf.fieldName || inf.name)) || "",
        displayName: (inf && (inf.displayName || inf.name)) || "",
      }));
      return { ...s, emoji: s.emoji || s.icon || "", states, activities, flows, information };
    },
    proposalName(p) {
      return this.displaySchema(p).name || "Module";
    },
    /** The module's own icon (emoji) for the proposal header; "" when none is set. */
    proposalEmoji(p) {
      const e = this.displaySchema(p).emoji;
      return e ? String(e) : "";
    },
    /** Header action title — "Create module" for a new design, "Update module" for a modify. */
    proposalActionTitle(p) {
      return (p && p.kind === "modify") ? "Update module" : "Create module";
    },
    proposalValid(p) {
      if (p && p.validation && typeof p.validation.valid === "boolean") return p.validation.valid;
      return !!(p && p.success);
    },
    proposalErrors(p) {
      const e = p && p.validation && p.validation.errors;
      if (!e || (Array.isArray(e) && !e.length)) return "";
      return typeof e === "string" ? e : this.prettyJson(e);
    },
    proposalCount(p, key) {
      const arr = this.displaySchema(p)[key];
      return Array.isArray(arr) ? arr.length : 0;
    },
    /**
     * One-line state-flow summary for the proposal card body (design): the chain of states from
     * the initial one, joined with →; when a state fans out, the branch targets join with " / "
     * ("Draft → Pending Approval → Approved / Rejected"). Kept deliberately shallow — it stops at
     * the first branch (after listing its targets) and skips disconnected sub-flows; the full
     * picture lives in the Show-diagram modal.
     */
    proposalFlowSummary(p) {
      const schema = this.displaySchema(p);
      const states = Array.isArray(schema.states) ? schema.states : [];
      const flows = Array.isArray(schema.flows) ? schema.flows : [];
      if (!states.length) return "";
      const nameById = {};
      states.forEach((s) => { if (s && s.id != null) nameById[s.id] = s.name != null && s.name !== "" ? String(s.name) : String(s.id); });
      const out = {};
      flows.forEach((f) => {
        if (!f || f.fromStateId == null || f.toStateId == null) return;
        (out[f.fromStateId] = out[f.fromStateId] || []).push(f.toStateId);
      });
      const initial = states.find((s) => s && s.initial) || states[0];
      if (!initial || initial.id == null) return "";
      const seen = new Set([initial.id]);
      const parts = [nameById[initial.id]];
      let cur = initial.id;
      let guard = 0;
      while (guard++ < 100) {
        const nexts = [...new Set((out[cur] || []).filter((t) => !seen.has(t)))];
        if (!nexts.length) break;
        nexts.forEach((t) => seen.add(t));
        parts.push(nexts.map((t) => nameById[t] || String(t)).join(" / "));
        if (nexts.length > 1) break; // branch listed — the diagram tells the rest
        cur = nexts[0];
      }
      return parts.join(" → ");
    },
    /**
     * Build the diagramData FTSFlowDiagram expects ({ states, flows, activities }) from a proposal's
     * schema. Arrays are deep-cloned because the diagram lib mutates node/edge objects in place — we
     * don't want that bleeding back into the reactive proposal.
     */
    proposalDiagramData(p) {
      const s = this.displaySchema(p);
      const clone = (a) => (Array.isArray(a) ? JSON.parse(JSON.stringify(a)) : []);
      return {
        name: this.proposalName(p),
        emoji: this.proposalEmoji(p),
        states: clone(s.states),
        flows: clone(s.flows),
        activities: clone(s.activities),
        // The module's information list — drives the diagram modal's side panel and the
        // per-activity "information involved" hover. Cloned like the rest so the diagram lib's
        // in-place mutations never bleed back into the reactive proposal.
        information: clone(s.information),
      };
    },
    /** Open the workflow diagram modal for a proposal's proposed schema. */
    showProposalDiagram(p) {
      this.$refs.ftsFlowDiagramModal.show(this.proposalDiagramData(p));
    },
    /** Button label for a proposal's Create/Apply action, reflecting commit state. */
    proposalCommitLabel(p) {
      const modify = p && p.kind === "modify";
      if (p && p._status === "done") return modify ? "Applied" : "Created";
      return modify ? "Apply changes" : "Create module";
    },

    /* ---------------- module source for shared ref/link resolution ---------------- */
    chatModules() {
      return this.isStudio
        ? (canvasDesignStore.getters.workspaceModules || [])
        : (clientStore.getters.workspaceModules || []);
    },

    /* ---------------- studio host adapter ---------------- */
    /** The chat endpoint for this surface (D10: the studio lane is its own route). */
    chatEndpoint() {
      return this.isStudio ? "/api/teammate/studio/chat" : "/api/teammate/chat";
    },
    /**
     * Reshape a built /chat payload for the studio lane: client-surface context never
     * rides (entry/view/form/design — their producers aren't mounted in the Studio, but
     * omission here is the contract, not an accident), and the code-stripped CanvasDraft
     * attaches while the Studio diff is dirty (D5).
     */
    decorateStudioPayload(payload) {
      if (!this.isStudio) return payload;
      delete payload.EntryId;
      delete payload.View;
      delete payload.Form;
      delete payload.Design;
      delete payload.Attachments;
      const draft = buildCanvasDraft(
        canvasDesignStore.state.vector, canvasDesignStore.state.originalVector);
      if (draft) payload.CanvasDraft = draft;
      const sel = this.studioSelection;
      if (sel && this.dismissed.selection !== `${sel.type}:${sel.id}`)
        payload.Selection = sel;
      if (this.scriptContext && this.dismissed.script !== this.scriptContext.key) {
        payload.ScriptContext = {
          label: this.scriptContext.label,
          lang: this.scriptContext.lang,
          script: this.scriptContext.script,
        };
      }
      return payload;
    },

    /* ---------------- removable context (tags above the composer) ---------------- */
    // Stable key for the open form's context instance (activity + entry), or "" when none.
    formKey() {
      const fc = this.formContext;
      if (!fc || !fc.activityId) return "";
      return `${fc.activityId}:${fc.entryId != null ? fc.entryId : ""}`;
    },
    // True when this context type's current instance has been dismissed by the user.
    isCtxDismissed(type, key) {
      return key != null && key !== "" && this.dismissed[type] === key;
    },
    // Drop a context item: record the dismissed instance so it (and only it) stops being sent.
    dismissContext(tag) {
      this.$set(this.dismissed, tag.type, tag.key);
    },
    /**
     * Re-attach everything the current view derives — undoes tag dismissals (the strip's "+").
     * Clearing all four keys is safe: dismissals for other instances are inert anyway (their
     * tags re-add on navigation), and the snapshot refresh brings the List tag straight back.
     */
    restoreContext() {
      this.dismissed = { module: null, entry: null, form: null, view: null, selection: null, script: null, notification: null };
      this.refreshViewSnapshot(true);
    },

    /* ---------------- AI TeamMate <-> listing view bridge ---------------- */
    /**
     * Refresh the List-tag snapshot from the mounted list view. Best-effort UI mirror only —
     * the send paths pull a fresh view at send time, so the payload and dismissal gating never
     * depend on this being current. The delayed second pass catches a list view that is still
     * mounting right after a navigation.
     */
    refreshViewSnapshot(withRetry) {
      this.viewSnapshot = this.collectCurrentView();
      if (withRetry) {
        clearTimeout(this._viewSnapTimer);
        this._viewSnapTimer = setTimeout(() => { this.viewSnapshot = this.collectCurrentView(); }, 1000);
      }
    },
    // Pull the current module listing view from the mounted FTSListView (synchronous
    // callback over the event bus). Returns null when no list view is active.
    collectCurrentView() {
      let view = null;
      try {
        window.eventBus.$emit("teammate:collectView", (v) => { view = v; });
      } catch (e) { /* best effort — no list view mounted */ }
      return view;
    },
    /**
     * After a turn that created/changed an entry, nudge any open list view to reload so the
     * new (or updated) row shows up without a manual refresh. The list view only answers
     * "listing:refresh" when it's actually mounted and its module matches the payload — so
     * this is a no-op when the relevant list isn't on screen. We skip module actions (they
     * don't touch entry rows; a new module refreshes via loadWorkspace) and failed actions.
     * When an action's module can't be resolved to an id, fall back to refreshing whatever
     * list is open rather than silently missing it.
     */
    refreshOpenListForActions(actions) {
      const acts = Array.isArray(actions) ? actions : [];
      const moduleIds = new Set();
      let refreshAny = false;
      for (const act of acts) {
        if (!act || act.error || act.success === false || act.type === "module" || act.entryId == null) continue;
        const mod = this.lookupModule(act.module);
        if (mod) moduleIds.add(mod.id);
        else refreshAny = true;
      }
      if (!moduleIds.size && !refreshAny) return;
      try {
        if (refreshAny) window.eventBus.$emit("listing:refresh", {});
        else moduleIds.forEach((moduleId) => window.eventBus.$emit("listing:refresh", { moduleId }));
      } catch (e) { /* best effort — refresh is purely a convenience */ }
    },
    // Remember a teammate-returned view's filter so a Listing chip ({{module:listing:…}}) for the
    // same module+listing can restore the exact filter/sort/search when clicked. We never render a
    // separate "Open this view" card and never push the filter or navigate on our own — the inline
    // listing chip in the reply is the only affordance, opened on the user's terms (onListClick).
    applyTeammateView(view) {
      if (!view || !view.module || !view.listingId) return;
      const targetModule = this.chatModules().find((m) => m.name === view.module);
      if (!targetModule) return;
      this.teammateViews[`${view.module}|${view.listingId}`] = view;
    },
    // Navigate to the view's module+listing and apply its filter on arrival. Reached when the
    // user clicks an inline listing chip ({{module:listing:…}}) for a resolved module (onListClick).
    openTeammateView(view) {
      if (!view || !view.module) return;
      const targetModule = this.chatModules().find((m) => m.name === view.module);
      if (!targetModule) return;
      // Stash for the destination FTSListView to consume after it mounts.
      window.__teammatePendingView = view;
      this.$router.push({
        name: "list-view",
        params: { moduleId: commons.encodeids(targetModule.id) },
        query: { l: view.listingId || "everything" },
      }).catch(() => { });
      // Step aside so the opened list view is visible.
      this.minimize();
    },
  },
};
</script>

<style scoped>
/* ===== design-system tokens (teammate-scoped) =====
   The cards / pills / chips / buttons below pull from these so the panel follows
   the app design system (colors.scss --fts-*) and themes with it, rather than
   hardcoding hexes. GNey's accent is the app primary (teal-blue), routed through --tm-accent;
   status colors map to the shared success / warning / danger tokens. Scoped to the
   teammate roots so nothing leaks, and inherited by child components + v-html. */
.ct-fab,
.ct-panel {
  /* GNey accent — aligned to the app primary (teal-blue) per the Figma redesign,
     so the panel reads as part of the product rather than a separate violet brand. */
  --tm-accent: var(--fts-primary, #2b9ec6);
  --tm-accent-rgb: var(--fts-primary-rgb, 43, 158, 198);
  --tm-on-accent: #fff;
  /* app primary (teal) — used for the confirm/commit buttons so they match the
     standard primary button scheme rather than the AI accent */
  --tm-primary-rgb: var(--fts-primary-rgb, 43, 158, 198);
  /* surfaces + ink */
  --tm-surface: var(--fts-gray-25, #fff);
  --tm-surface-2: var(--fts-gray-50, #f6f7f9);
  --tm-surface-3: var(--fts-gray-100, #eef0f3);
  --tm-text: var(--fts-text, #1f2328);
  --tm-text-muted: var(--fts-gray-500, #6b7280);
  --tm-text-subtle: var(--fts-gray-400, #9095a1);
  --tm-border: var(--fts-border, #e5e7eb);
  /* status */
  --tm-success: var(--fts-success, #16a34a);
  --tm-success-rgb: var(--fts-success-rgb, 22, 163, 74);
  --tm-warning: var(--fts-warning, #fb8c00);
  --tm-warning-rgb: var(--fts-warning-rgb, 251, 140, 0);
  --tm-danger: var(--fts-danger, #dc2626);
  --tm-danger-rgb: var(--fts-danger-rgb, 220, 38, 38);
}

/* ===== minimized movable mascot ===== */
.ct-fab {
  position: fixed;
  z-index: 2147483000;
  width: 58px;
  height: 58px;
  cursor: grab;
  user-select: none;
  filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.18));
  transition: transform 0.12s ease;
}
.ct-fab:hover { transform: scale(1.06); }
.ct-fab:active { cursor: grabbing; }
.ct-fab-mascot { pointer-events: none; }
.ct-fab-dot {
  position: absolute;
  top: 4px; right: 4px;
  width: 11px; height: 11px;
  border-radius: 50%;
  background: var(--tm-danger);
  border: 2px solid var(--tm-surface);
}

/* ===== open panel ===== */
.ct-panel {
  position: fixed;
  z-index: 2147483000;
  display: flex;
  flex-direction: column;
  background: var(--tm-surface);
  color: var(--tm-text);
  border: 1px solid var(--tm-border);
  border-radius: 14px;
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.28);
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 14px;
}
.ct-panel--normal {
  bottom: 24px;
  right: 24px;
  width: 400px;
  height: 600px;
  max-height: calc(100vh - 48px);
  max-width: calc(100vw - 48px);
}
.ct-panel--side {
  top: 0; right: 0; bottom: 0;
  width: var(--gney-dock-width, 380px);
  height: 100vh;
  max-height: 100vh;
  border-radius: 0;
  border-top: none; border-right: none; border-bottom: none;
  /* docked = flush split: no cast shadow at the seam, just the 1px left border. */
  box-shadow: none;
}
/* left-edge grab strip for resizing the docked rail (kept inside the panel,
   which clips overflow) */
.ct-resize {
  position: absolute;
  top: 0; left: 0; bottom: 0;
  width: 6px;
  cursor: col-resize;
  z-index: 5;
}
.ct-resize::after {
  content: "";
  position: absolute;
  top: 0; bottom: 0; left: 0;
  width: 2px;
  background: transparent;
  transition: background 0.12s ease;
}
.ct-resize:hover::after { background: var(--tm-accent); }
.ct-panel--full {
  top: 32px; left: 32px; right: 32px; bottom: 32px;
  width: auto; height: auto;
  border-radius: 16px;
}

/* header */
.ct-head {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--tm-border);
  flex-shrink: 0;
  overflow: hidden;
}
/* Figma redesign: clean white header — the colored gradient wash is dropped. */
.ct-head-bg { display: none; }
.ct-head-main { position: relative; z-index: 1; display: flex; align-items: center; gap: 8px; min-width: 0; }
.ct-head-mascot { flex-shrink: 0; }
.ct-head-text { min-width: 0; }
.ct-head-title {
  /* GNey wordmark — the brand gradient is the identity mark, kept fixed in both themes */
  font-weight: 700; font-size: 14px; display: flex; align-items: center; gap: 6px;
  background: linear-gradient(120deg, #5b8def, #2b9ec6);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
}
/* Showing a real conversation title: drop the brand gradient for plain text and truncate long titles. */
.ct-head-title--session {
  display: block; background: none; -webkit-text-fill-color: initial;
  color: var(--tm-text); max-width: 240px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.ct-alpha {
  -webkit-text-fill-color: initial;
  font-size: 9px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
  color: var(--tm-accent); background: rgba(var(--tm-accent-rgb), 0.12); padding: 1px 6px; border-radius: 8px;
}
.ct-head-ctx {
  font-size: 11px; color: var(--tm-text-muted); max-width: 220px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.ct-head-actions { position: relative; z-index: 1; display: flex; gap: 2px; flex-shrink: 0; }
.ct-icon-btn {
  border: none; background: transparent; color: var(--tm-text-muted);
  width: 28px; height: 28px; border-radius: 7px; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; font-size: 13px;
}
.ct-icon-btn:hover { background: rgba(var(--tm-accent-rgb), 0.1); color: var(--tm-accent); }
.ct-icon-btn:disabled { opacity: 0.4; cursor: default; }
.ct-icon-btn--on { background: rgba(var(--tm-accent-rgb), 0.14); color: var(--tm-accent); }

/* ===== conversation history picker ===== */
.ct-hist {
  position: relative;
  z-index: 6;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  max-height: 60%;
  background: var(--tm-surface);
  border-bottom: 1px solid var(--tm-border);
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.12);
}
.ct-hist-top {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--tm-border);
}
.ct-hist-search {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
}
.ct-hist-search-ic {
  position: absolute;
  left: 10px;
  font-size: 12px;
  color: var(--tm-text-subtle);
  pointer-events: none;
}
.ct-hist-search-input {
  width: 100%;
  border: 1px solid var(--tm-border);
  background: var(--tm-surface-2);
  color: var(--tm-text);
  border-radius: 8px;
  padding: 7px 10px 7px 30px;
  font-size: 13px;
  outline: none;
}
.ct-hist-search-input:focus { border-color: rgba(var(--tm-accent-rgb), 0.5); }
.ct-hist-new {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid var(--tm-border);
  background: var(--tm-surface-2);
  color: var(--tm-text);
  border-radius: 8px;
  padding: 7px 10px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}
.ct-hist-new:hover { border-color: rgba(var(--tm-accent-rgb), 0.4); background: rgba(var(--tm-accent-rgb), 0.08); color: var(--tm-accent); }
.ct-hist-list { overflow-y: auto; padding: 6px; }
.ct-hist-state {
  padding: 18px 12px;
  text-align: center;
  color: var(--tm-text-muted);
  font-size: 12.5px;
}
.ct-hist-state--err { color: var(--tm-danger); }
.ct-hist-retry {
  margin-left: 6px;
  border: none;
  background: transparent;
  color: var(--tm-accent);
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
}
.ct-hist-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
}
.ct-hist-item:hover { background: var(--tm-surface-2); }
.ct-hist-item.is-active { background: rgba(var(--tm-accent-rgb), 0.1); }
.ct-hist-title {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: var(--tm-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ct-hist-item.is-active .ct-hist-title { font-weight: 600; }
.ct-hist-time {
  flex-shrink: 0;
  font-size: 11px;
  color: var(--tm-text-subtle);
}
.ct-hist-actions {
  flex-shrink: 0;
  display: none;
  align-items: center;
  gap: 2px;
}
.ct-hist-item:hover .ct-hist-actions { display: inline-flex; }
.ct-hist-act {
  border: none;
  background: transparent;
  color: var(--tm-text-muted);
  width: 24px;
  height: 24px;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
}
.ct-hist-act:hover { background: rgba(var(--tm-accent-rgb), 0.12); color: var(--tm-accent); }
.ct-hist-act--del:hover { background: rgba(var(--tm-danger-rgb), 0.12); color: var(--tm-danger); }
.ct-hist-act:disabled { opacity: 0.5; cursor: default; }
.ct-hist-rename {
  flex: 1;
  min-width: 0;
  border: 1px solid rgba(var(--tm-accent-rgb), 0.5);
  background: var(--tm-surface);
  color: var(--tm-text);
  border-radius: 6px;
  padding: 5px 8px;
  font-size: 13px;
  outline: none;
}

/* messages */
.ct-list { flex: 1; overflow-y: auto; padding: 14px; display: flex; flex-direction: column; gap: 12px; }
.ct-empty { margin: auto; text-align: center; color: var(--tm-text-muted); display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 0 16px; }
.ct-empty-title {
  /* Blue brand gradient headline (Figma): "Hello, I'm GNey. Let's do magic!" */
  font-weight: 700; font-size: 18px; margin: 10px 0 0; line-height: 1.3;
  background: linear-gradient(120deg, #5b8def, #2b9ec6);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
}
.ct-empty-sub { margin: 4px 0 0; font-size: 13px; max-width: 300px; color: var(--tm-text-muted); }
/* Suggestions render as centered rounded pills (Figma) rather than a stacked list. */
.ct-suggest { display: flex; flex-wrap: wrap; justify-content: center; gap: 8px; margin-top: 16px; max-width: 320px; }
.ct-suggest-btn {
  border: 1px solid var(--tm-border); background: var(--tm-surface); color: var(--tm-text);
  border-radius: 999px; padding: 7px 14px; font-size: 12.5px; cursor: pointer; text-align: center; font-weight: 500;
}
.ct-suggest-btn:hover { border-color: rgba(var(--tm-accent-rgb), 0.4); background: rgba(var(--tm-accent-rgb), 0.08); color: var(--tm-accent); }

.ct-msg { display: flex; gap: 8px; align-items: flex-start; }
.ct-msg--user { flex-direction: row-reverse; }
.ct-msg-body { max-width: 82%; }
/* User turns sit in a light pill on the right (Figma), not a solid accent fill. */
.ct-msg--user .ct-msg-body {
  background: var(--tm-surface-3); color: var(--tm-text); padding: 8px 12px; border-radius: 14px 14px 4px 14px;
}
/* Assistant turns render full-width with no bubble (Figma) — prose sits flush in the panel. */
.ct-msg--assistant { gap: 0; }
.ct-msg--assistant .ct-msg-body {
  background: transparent; padding: 0; max-width: 100%; width: 100%;
}
.ct-msg-content { line-height: 1.5; word-break: break-word; }
.ct-msg--user .ct-msg-content :deep(a:not(.tmd-ref)) { color: var(--tm-accent); text-decoration: underline; }
.ct-msg-error { color: var(--tm-danger); font-size: 12.5px; }

/* billing gate card (402: credits_exhausted / ai_not_included) */
.ct-billing-card {
  border: 1px solid var(--tm-border);
  border-left: 3px solid var(--tm-warning, #b45309);
  border-radius: 8px;
  background: var(--tm-surface);
  padding: 10px 12px;
  max-width: 420px;
}
.ct-billing-title { font-weight: 600; font-size: 13px; margin-bottom: 2px; }
.ct-billing-text { font-size: 12.5px; color: var(--tm-text-subtle); }

/* per-turn usage / tool calls / cost */
.ct-msg-usage {
  margin-top: 6px; display: flex; flex-wrap: wrap; align-items: center; gap: 8px;
  font-size: 10.5px; color: var(--tm-text-subtle);
}
.ct-msg-usage .ct-u-model { color: var(--tm-accent); font-weight: 600; }
.ct-msg-usage .ct-u-cache { color: var(--tm-accent); }
.ct-msg-usage .ct-u-tools { color: var(--tm-warning); }
.ct-msg-usage .ct-u-total { color: var(--tm-text-muted); }
.ct-msg-usage .ct-u-latency { color: #0e7490; }
.ct-msg-usage .ct-u-cost { margin-left: auto; font-weight: 600; color: var(--tm-text-muted); }

/* session totals strip */
.ct-session {
  flex-shrink: 0; display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  padding: 6px 14px; border-top: 1px solid var(--tm-border); background: var(--tm-surface-2);
  font-size: 11px; color: var(--tm-text-muted);
}
.ct-session-label { font-weight: 600; color: var(--tm-text-muted); }
.ct-session-toks { display: flex; gap: 8px; }
.ct-session-toks .ct-u-cache { color: var(--tm-accent); }
.ct-session-toks .ct-u-tools { color: var(--tm-warning); }
.ct-session-cost { margin-left: auto; font-weight: 700; color: var(--tm-accent); }
/* End-user variant: the conversation's total credit is the ONLY usage line an end
   user sees, so give it a touch more presence than the internal debug readout. */
.ct-session--enduser { font-size: 12px; }
.ct-session--enduser .ct-session-cost { font-size: 12px; }

/* attachment previews on a message bubble */
.ct-msg-attachments { margin-top: 8px; display: flex; flex-wrap: wrap; gap: 6px; }
.ct-msg-att { display: inline-flex; }
/* stored refs render as download links — keep the chip look, add affordance on hover */
a.ct-msg-att { text-decoration: none; color: inherit; cursor: pointer; }
a.ct-msg-att:hover .ct-msg-att-file { border-color: var(--tm-accent); color: var(--tm-accent); }
a.ct-msg-att:hover .ct-msg-att-thumb { border-color: var(--tm-accent); }
.ct-msg-att-thumb {
  width: 64px; height: 64px; border-radius: 8px; object-fit: cover;
  border: 1px solid rgba(0, 0, 0, 0.08); background: var(--tm-surface-2);
}
.ct-msg-att-file {
  display: inline-flex; align-items: center; gap: 6px;
  max-width: 200px; padding: 5px 9px; font-size: 12px;
  border: 1px solid var(--tm-border); border-radius: 8px; background: var(--tm-surface); color: var(--tm-text);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.ct-msg--user .ct-msg-att-file { background: var(--tm-surface); border-color: var(--tm-border); color: var(--tm-text); }

/* markdown */
.ct-md :deep(p) { margin: 0 0 8px; }
.ct-md :deep(p:last-child) { margin-bottom: 0; }
.ct-md :deep(ul), .ct-md :deep(ol) { margin: 4px 0 8px; padding-left: 20px; }
/* breathing room between list items so stacked reference chips/pills don't touch */
.ct-md :deep(li) { margin: 8px 0; }
.ct-md :deep(li:first-child) { margin-top: 0; }
.ct-md :deep(li:last-child) { margin-bottom: 0; }
.ct-md :deep(h1), .ct-md :deep(h2), .ct-md :deep(h3),
.ct-md :deep(h4), .ct-md :deep(h5), .ct-md :deep(h6) { margin: 8px 0 4px; font-size: 1em; font-weight: 700; }
.ct-md :deep(code) { background: rgba(var(--tm-accent-rgb), 0.1); padding: 1px 5px; border-radius: 4px; font-size: 0.9em; }
.ct-md :deep(pre) { background: #1f2330; color: #e6e8ec; padding: 10px; border-radius: 8px; overflow-x: auto; }
.ct-md :deep(pre code) { background: none; padding: 0; color: inherit; }
.ct-md :deep(blockquote) { margin: 4px 0; padding-left: 10px; border-left: 3px solid rgba(var(--tm-accent-rgb), 0.35); color: var(--tm-text-muted); }
/* The Q/A answers echo (ask card) renders as a quote inside the user bubble — give it a
   soft accent panel (design) instead of the bare assistant-quote rule above. */
.ct-msg--user .ct-md :deep(blockquote) {
  background: rgba(var(--tm-accent-rgb), 0.08);
  border-left: 3px solid rgba(var(--tm-accent-rgb), 0.4);
  padding: 6px 10px; border-radius: 6px; color: var(--tm-text);
}
/* Generic in-prose links only — reference chips (.tmd-ref) keep their own type colors
   from the shared teammateChat.css, so they're excluded here. */
.ct-md :deep(a:not(.tmd-ref)) { color: var(--tm-accent); text-decoration: underline; word-break: break-word; }
/* Tables (.tmd-table) and reference chips (.tmd-ref) are styled by the shared
   teammateChat.css; only the in-bubble overrides live here. */
.ct-msg--user .ct-md :deep(a:not(.tmd-ref)) { color: var(--tm-accent); }

/* ===== action cards (design): header row + white body =====
   One anatomy for every card the teammate emits — Module / Activity / Fill / Ask / Done:
   a header row carrying the colored title (accent tinted strip = needs the user,
   red = failed; done = plain muted "✓ …" line, no strip) and the action buttons,
   over a white body. */
.ct-cards { margin-top: 8px; display: flex; flex-direction: column; gap: 8px; }
.ct-card {
  border: none; border-radius: 10px; background: var(--tm-surface);
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden; font-size: 12.5px;
}
.ct-card.is-inactive { opacity: 0.65; }
.ct-card-head {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  padding: 6px 10px;
}
.ct-card-head.is-pending { background: rgba(var(--tm-accent-rgb), 0.07); }
.ct-card-head.is-pending .ct-card-title { color: var(--tm-accent); }
/* done / muted = no strip at all (density direction): completed work — and a card whose
   action moved elsewhere (form opened) — recedes to a plain muted title line over the body.
   Only pending (accent) and failed (danger) states ask for the eye. done additionally gets
   a small tick — grey like the title; no green anywhere in headers for now. */
.ct-card-head.is-done,
.ct-card-head.is-muted { background: transparent; }
.ct-card-head.is-done .ct-card-title,
.ct-card-head.is-muted .ct-card-title { color: var(--tm-text-muted); }
.ct-card-head.is-done .ct-card-title::before {
  content: "✓";
  margin-right: 6px;
  font-weight: 700;
  color: var(--tm-text-muted);
}
.ct-card-head.is-error { background: rgba(var(--tm-danger-rgb), 0.08); }
.ct-card-head.is-error .ct-card-title { color: var(--tm-danger); }
.ct-card-title { font-weight: 600; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ct-card-actions { margin-left: auto; display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.ct-card-link { color: var(--tm-accent); text-decoration: none; font-weight: 600; font-size: 12px; }
.ct-card-body { padding: 8px 10px; display: flex; flex-direction: column; gap: 7px; }
.ct-card-note { font-size: 11.5px; color: var(--tm-text-muted); font-style: italic; }
.ct-card-note.is-danger { color: var(--tm-danger); font-style: normal; }
.ct-card-receipt { display: flex; align-items: baseline; gap: 6px; font-size: 12px; color: var(--tm-text-muted); }
/* identity row: module emoji + name + "| #docid" */
.ct-ident { display: flex; align-items: center; gap: 6px; min-width: 0; }
.ct-ident-ic { font-size: 14px; line-height: 1; flex-shrink: 0; color: var(--tm-text-muted); }
.ct-ident-name { font-weight: 600; color: var(--tm-text); }
.ct-ident-doc { color: var(--tm-text-muted); white-space: nowrap; }
/* trailing action on an identity row (done module card's "Show diagram") */
.ct-ident-action { margin-left: auto; flex-shrink: 0; }
/* receipt row: identity (emoji + doc ref, or entry chip on done cards) + resulting state */
.ct-receipt-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.ct-receipt-chip { display: inline-flex; min-width: 0; }
.ct-receipt-note { color: var(--tm-text-muted); font-size: 12px; }
.ct-status-chip {
  display: inline-flex; align-items: center; border-radius: 999px; padding: 1px 9px;
  font-size: 11px; font-weight: 600;
  color: var(--tm-warning); background: rgba(var(--tm-warning-rgb), 0.1);
}
/* field/value review rows: label left, value right (design) */
.ct-kv-rows { display: flex; flex-direction: column; gap: 4px; }
.ct-kv { display: flex; align-items: baseline; gap: 12px; font-size: 12px; line-height: 1.4; }
.ct-kv-k { color: var(--tm-text-muted); flex-shrink: 0; }
.ct-kv-v { margin-left: auto; color: var(--tm-text); text-align: right; word-break: break-word; }
/* "View more" fold under long value lists */
.ct-kv-more {
  align-self: center; display: inline-flex; align-items: center; gap: 4px;
  margin-top: 8px; border: none; background: transparent; color: var(--tm-accent);
  border-radius: 7px; font-size: 11.5px; font-weight: 400; cursor: pointer; padding: 2px 6px;
}
.ct-kv-more:hover { text-decoration: none; background: rgba(var(--tm-accent-rgb), 0.08); }
.ct-kv-more-icon { font-size: 16px; line-height: 1; }
/* next-step buttons — open the activity's form on the entry, never perform it */
.ct-done-next {
  display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 6px;
  max-width: 100%; min-width: 0;
}
.ct-receipt-row > .ct-done-next { margin-left: auto; }
.ct-next-btn {
  display: inline-flex; align-items: center; gap: 4px; max-width: 200px; min-width: 0;
  border: 1px solid var(--tm-border); background: var(--tm-surface); color: var(--tm-text);
  border-radius: 7px; padding: 3px 9px; font-size: 12px; cursor: pointer; font-weight: 600;
}
.ct-next-btn-emoji { flex-shrink: 0; }
.ct-next-btn-label {
  min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.ct-next-btn:hover:not(:disabled) { border-color: var(--tm-accent); color: var(--tm-accent); }
.ct-next-btn:disabled { opacity: 0.6; cursor: default; }
.ct-next-btn.is-custom { border-color: rgba(var(--tm-accent-rgb), 0.45); color: var(--tm-accent); }
.ct-action-err { color: var(--tm-danger); }
/* solid primary button (Confirm / Apply / Create module / Send answers) */
.ct-confirm {
  border: none; background: rgb(var(--tm-primary-rgb)); color: #fff;
  border-radius: 7px; padding: 4px 12px; font-size: 12px; cursor: pointer; font-weight: 600;
}
.ct-confirm:hover:not(:disabled) { background: rgba(var(--tm-primary-rgb), 0.9); }
/* disabled = grey, not faded teal (same scheme as the disabled send button) */
.ct-confirm:disabled { background: var(--tm-surface-3); color: var(--tm-text-subtle); cursor: default; }
/* Form card rows — fill_form checklist; Apply fills the open form, it never commits
   (Apply ≠ Confirm ≠ Submit) */
.ct-form-row { display: flex; align-items: flex-start; gap: 8px; font-size: 12px; cursor: pointer; margin: 0; font-weight: 400; }
.ct-form-row.is-off { opacity: 0.45; }
.ct-form-row input[type="checkbox"] { margin: 2px 0 0; flex-shrink: 0; cursor: pointer; accent-color: var(--tm-accent); }
.ct-form-row--all { padding-bottom: 2px; }
/* label left, value chips beside it on the right (same row; chips wrap under themselves
   right-aligned when the rail is narrow) */
.ct-form-row-main { flex: 1; display: flex; align-items: baseline; flex-wrap: wrap; gap: 3px 12px; min-width: 0; }
.ct-form-row-label { color: var(--tm-text); font-weight: 600; flex-shrink: 0; }
.ct-form-row-vals { display: flex; align-items: center; flex-wrap: wrap; justify-content: flex-end; gap: 4px; margin-left: auto; }
.ct-val-chip { background: var(--tm-surface-3); border-radius: 6px; padding: 1px 7px; color: var(--tm-text); }
/* old value in an "old → new" pair: plain muted text, no chip box — only the new value reads as a chip */
.ct-val-chip.is-old { color: var(--tm-text-muted); background: transparent; padding: 1px 0; }
.ct-form-row-arrow { color: var(--tm-text-muted); }

/* thinking */
.ct-thinking { display: flex; align-items: center; gap: 4px; padding: 10px 4px; }
.ct-progress { margin-left: 8px; font-size: 12px; color: var(--tm-text-muted); }
.ct-dot { width: 6px; height: 6px; border-radius: 50%; background: rgba(var(--tm-accent-rgb), 0.55); animation: ct-bounce 1.2s infinite ease-in-out; }
.ct-dot:nth-child(2) { animation-delay: 0.15s; }
.ct-dot:nth-child(3) { animation-delay: 0.3s; }
@keyframes ct-bounce { 0%, 80%, 100% { transform: translateY(0); opacity: 0.5; } 40% { transform: translateY(-4px); opacity: 1; } }

/* composer */
/* Composer is a self-contained rounded card (Figma) floated above the panel base,
   rather than a flush bar with a top divider. */
.ct-composer {
  flex-shrink: 0; display: flex; flex-direction: column; gap: 8px;
  margin: 8px 12px 12px; padding: 8px 10px;
  border: 1px solid var(--tm-border); border-radius: 16px; background: var(--tm-surface);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
}
.ct-composer:focus-within { border-color: rgba(var(--tm-accent-rgb), 0.5); box-shadow: 0 0 0 3px rgba(var(--tm-accent-rgb), 0.12); }
/* removable context tags */
.ct-ctx-tags { display: flex; flex-wrap: wrap; gap: 6px; }
.ct-ctx-tag {
  display: inline-flex; align-items: center; gap: 5px; max-width: 220px;
  background: rgba(var(--tm-accent-rgb), 0.10); border: 1px solid rgba(var(--tm-accent-rgb), 0.30); border-radius: 999px;
  padding: 2px 4px 2px 8px; font-size: 11.5px; color: var(--tm-accent);
}
.ct-ctx-kind { font-weight: 700; text-transform: uppercase; font-size: 9.5px; letter-spacing: 0.03em; color: var(--tm-accent); }
.ct-ctx-icon { font-size: 13px; line-height: 1; flex-shrink: 0; }
.ct-ctx-label { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ct-ctx-x {
  border: none; background: transparent; color: var(--tm-accent); cursor: pointer;
  width: 16px; height: 16px; border-radius: 50%; line-height: 1; font-size: 14px;
  display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.ct-ctx-x:hover { background: rgba(var(--tm-accent-rgb), 0.18); color: var(--tm-accent); }
/* "+" — re-attach the current view's dismissed context; ghost pill matching the tag height */
.ct-ctx-add {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 22px; height: 22px; padding: 0 6px; border-radius: 999px;
  border: 1px dashed rgba(var(--tm-accent-rgb), 0.45); background: transparent;
  color: var(--tm-accent); cursor: pointer; font-size: 14px; line-height: 1;
}
.ct-ctx-add:hover { background: rgba(var(--tm-accent-rgb), 0.10); border-style: solid; }
.ct-composer-row { display: flex; align-items: center; gap: 6px; }
/* Seniority selector as a compact rounded pill (Figma), inline in the control row.
   Custom dropdown (not a native <select>) so the open menu matches the teammate theme. */
.ct-level { position: relative; flex-shrink: 0; }
.ct-level-btn {
  display: inline-flex; align-items: center; gap: 5px;
  border: 1px solid var(--tm-border); border-radius: 999px; padding: 3px 9px 3px 10px;
  background: var(--tm-surface); font-family: inherit; font-size: 12px; font-weight: 600;
  color: var(--tm-text); cursor: pointer; outline: none;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}
.ct-level-btn:hover:not(:disabled) { border-color: var(--tm-accent); background: var(--tm-surface-2); }
.ct-level.is-open .ct-level-btn {
  border-color: var(--tm-accent);
  box-shadow: 0 0 0 2px rgba(var(--tm-accent-rgb), 0.15);
}
.ct-level-btn:disabled { opacity: 0.5; cursor: default; }
.ct-level-ic { font-size: 12px; color: var(--tm-accent); }
.ct-level-txt { line-height: 1; }
.ct-level-caret {
  font-size: 9px; color: var(--tm-text-subtle); margin-left: 1px;
  transition: transform 0.15s ease;
}
.ct-level.is-open .ct-level-caret { transform: rotate(180deg); }
/* Popup opens upward — the pill sits at the bottom of the composer. */
.ct-level-menu {
  position: absolute; bottom: calc(100% + 6px); left: 0; z-index: 20;
  min-width: 100%; width: max-content;
  display: flex; flex-direction: column; gap: 1px; padding: 4px;
  background: var(--tm-surface); border: 1px solid var(--tm-border); border-radius: 10px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.16);
}
.ct-level-opt {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  border: none; border-radius: 6px; padding: 6px 9px; background: transparent;
  font-family: inherit; font-size: 12px; font-weight: 500; color: var(--tm-text);
  text-align: left; cursor: pointer; white-space: nowrap;
  transition: background 0.12s ease;
}
.ct-level-opt:hover:not(:disabled) { background: var(--tm-surface-2); }
.ct-level-opt.is-sel { color: var(--tm-accent); font-weight: 600; background: rgba(var(--tm-accent-rgb), 0.1); }
.ct-level-opt:disabled { opacity: 0.45; cursor: default; }
.ct-level-opt-check { font-size: 10px; color: var(--tm-accent); }
.ct-level-divider { height: 1px; margin: 3px 2px; background: var(--tm-border); }
.ct-level-opt-model { font-size: 11px; color: var(--tm-text-muted); }
.ct-level-opt-model.is-sel { color: var(--tm-accent); font-weight: 600; }
.ct-level-model-tag {
  font-size: 10px; font-weight: 600; color: var(--tm-text-muted); line-height: 1.4;
  border: 1px solid var(--tm-border); border-radius: 999px; padding: 1px 6px;
}
.ct-design { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--tm-text-muted); cursor: pointer; user-select: none; }
.ct-design-ic { font-size: 12px; color: var(--tm-accent); }
.ct-design-cb { accent-color: var(--tm-accent); cursor: pointer; margin: 0; }
.ct-design-cb:disabled { cursor: default; }
/* internal-only mock toggle — amber accent to signal "testing, not real" */
.ct-mock { color: var(--tm-warning); font-weight: 600; }
.ct-mock .ct-design-cb { accent-color: var(--tm-warning); }
/* internal-only end-user preview toggle — muted, since it only changes what staff see */
.ct-enduser { color: var(--tm-text-muted); font-weight: 600; }
.ct-enduser .ct-design-cb { accent-color: var(--tm-text-muted); }

/* clarifying questions (ask_user) — paged card: ‹ 1 of N › pager + Skip in the header strip.
   The strip is neutral grey, not accent: an ask pauses the flow rather than driving it. */
/* analytics mode pill (composer) + the operation-mode "Open Analytics" hand-off + report receipt */
.ct-mode-pill {
  display: inline-flex; align-items: center; gap: 5px; border-radius: 999px; padding: 2px 9px;
  font-size: 11.5px; font-weight: 600; color: var(--tm-accent); background: rgba(var(--tm-accent-rgb), 0.1);
  white-space: nowrap;
}
.ct-report .ct-card-head.is-pending { background: rgba(var(--tm-accent-rgb), 0.07); }

/* access card (§13.7): one row per change, widening rows flagged, warnings below */
.ct-access-changes,
.ct-access-warnings { list-style: none; margin: 6px 0 0; padding: 0; }
.ct-access-change { display: flex; flex-wrap: wrap; align-items: baseline; gap: 4px 8px; padding: 4px 0; font-size: 12.5px; }
.ct-access-change + .ct-access-change { border-top: 1px solid var(--tm-border); }
.ct-access-item { font-weight: 600; color: var(--tm-text); }
.ct-access-rule { color: var(--tm-text-muted); }
.ct-access-flag {
  font-size: 11px; font-weight: 600; border-radius: 999px; padding: 1px 7px;
  color: var(--tm-danger); background: rgba(var(--tm-danger-rgb), 0.1);
}
.ct-access-warnings li {
  font-size: 12px; color: var(--tm-text-muted); padding: 4px 8px; margin-top: 4px;
  border-left: 2px solid rgba(var(--tm-danger-rgb), 0.5); background: var(--tm-surface-2); border-radius: 0 6px 6px 0;
}

.ct-ask { margin-top: 8px; }
.ct-ask .ct-card-head.is-pending { background: var(--tm-surface-2); }
.ct-ask .ct-card-head.is-pending .ct-card-title { color: var(--tm-text); }
.ct-ask-pager { display: flex; align-items: center; gap: 4px; }
/* borderless chevrons — the ‹ 1 of N › group reads as one quiet control */
.ct-ask-nav {
  border: none; background: transparent; color: var(--tm-text-muted);
  width: 22px; height: 22px; border-radius: 6px; cursor: pointer; font-size: 14px; line-height: 1;
  display: inline-flex; align-items: center; justify-content: center; padding: 0 0 2px;
}
.ct-ask-nav:hover:not(:disabled) { color: var(--tm-accent); background: rgba(var(--tm-accent-rgb), 0.08); }
.ct-ask-nav:disabled { opacity: 0.4; cursor: default; }
.ct-ask-count { font-size: 11.5px; font-weight: 600; color: var(--tm-text-muted); padding: 0 3px; }
.ct-ask-foot { display: flex; align-items: center; justify-content: flex-end; gap: 8px; }
.ct-ask-hint { font-size: 11px; color: var(--tm-text-subtle); }
/* Bulk Activity-card group — a same-activity fan-out collapsed to one paged card. The bar
   (caption + the ask card's quiet ‹ n of N › chevrons) and foot (progress + Confirm all) frame
   the single visible card; groups of one render no chrome and look exactly like before. */
.ct-group { display: flex; flex-direction: column; min-width: 0; }
.ct-group.is-multi { gap: 4px; }
.ct-group-bar { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 0 2px; }
.ct-group-title {
  font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.02em;
  color: var(--tm-text-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.ct-group-foot { display: flex; align-items: center; justify-content: flex-end; gap: 8px; padding: 0 2px; }
.ct-question { display: flex; flex-direction: column; }
.ct-question-head { display: flex; align-items: baseline; gap: 7px; flex-wrap: wrap; }
.ct-question-chip {
  flex-shrink: 0; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.02em;
  color: var(--tm-accent); background: rgba(var(--tm-accent-rgb), 0.2); border-radius: 999px; padding: 1px 8px;
}
.ct-question-q { font-size: 12.5px; font-weight: 600; color: var(--tm-text); line-height: 1.35; }
.ct-qopts { margin-top: 7px; display: flex; flex-direction: column; gap: 5px; }
.ct-qopt {
  display: flex; align-items: center; gap: 9px; width: 100%; text-align: left; cursor: pointer;
  border: 1px solid var(--tm-border); background: var(--tm-surface); border-radius: 7px; padding: 6px 9px;
  font-size: 12.5px; color: var(--tm-text);
}
.ct-qopt:hover:not(:disabled) { border-color: rgba(var(--tm-accent-rgb), 0.4); }
.ct-qopt:disabled { cursor: default; opacity: 0.75; }
.ct-qopt.is-sel { border-color: var(--tm-accent); background: rgba(var(--tm-accent-rgb), 0.1); }
.ct-qopt-badge {
  flex-shrink: 0; width: 20px; height: 20px; border-radius: 6px; display: inline-flex;
  align-items: center; justify-content: center; font-size: 11px; font-weight: 700;
  background: var(--tm-surface-3); color: var(--tm-text-muted);
}
.ct-qopt.is-sel .ct-qopt-badge { background: var(--tm-accent); color: #fff; }
.ct-qopt-body { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
.ct-qopt-label { line-height: 1.3; }
.ct-qopt-desc { font-size: 11px; color: var(--tm-text-muted); line-height: 1.3; }
.ct-qopt-else { color: var(--tm-text-muted); cursor: text; }
.ct-qelse-input {
  flex: 1; min-width: 0; border: none; padding: 0;
  font-size: 12.5px; font-family: inherit; background: transparent; color: var(--tm-text); outline: none;
}
.ct-qelse-input::placeholder { color: var(--tm-text-muted); opacity: 1; }

/* draft-check card + composer button (studio) */
.ct-checkdraft-btn {
  display: inline-flex; align-items: center; gap: 5px;
  border: 1px solid var(--tm-border); border-radius: 8px;
  background: transparent; color: var(--tm-text-2);
  font-size: 11.5px; padding: 3px 9px; cursor: pointer;
}
.ct-checkdraft-btn:hover:not(:disabled) { border-color: rgba(var(--tm-accent-rgb), 0.4); color: var(--tm-accent); }
.ct-checkdraft-btn:disabled { opacity: 0.45; cursor: default; }
.ct-audit-row {
  display: flex; align-items: baseline; gap: 7px;
  font-size: 12.5px; line-height: 1.5; padding: 1px 0;
}
.ct-audit-dot { flex: none; width: 7px; height: 7px; border-radius: 50%; transform: translateY(-1px); }
.ct-audit-row.is-error .ct-audit-dot { background: var(--tm-danger); }
.ct-audit-row.is-warn .ct-audit-dot { background: #f59e0b; }
.ct-audit-label { word-break: break-word; }
/* Notification card (D11) — the reader's anatomy inside the card family: source strip,
   title + sender, markdown body, footer actions + the cost hint. */
.ct-ntf .ct-card-head { gap: 6px; }
.ct-ntf-source { font-size: 11px; letter-spacing: 0.02em; text-transform: uppercase; font-weight: 600; color: var(--tm-text-muted); min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ct-ntf-ai { padding: 1px 5px; border-radius: 999px; border: 1px solid rgba(var(--tm-accent-rgb), 0.45); color: var(--tm-accent); font-size: 10px; font-weight: 700; line-height: 1.3; }
.ct-ntf-time { margin-left: auto; font-size: 11px; color: var(--tm-text-subtle); white-space: nowrap; }
.ct-ntf-title { font-size: 15px; font-weight: 600; line-height: 1.35; color: var(--tm-text); text-wrap: balance; }
.ct-ntf-meta { font-size: 12px; color: var(--tm-text-muted); }
.ct-ntf-body { font-size: 13.5px; line-height: 1.55; margin-top: 2px; }
.ct-ntf-foot { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; padding: 8px 10px 10px; border-top: 1px solid var(--tm-surface-3); }
.ct-ntf-hint { margin-left: auto; font-size: 11px; color: var(--tm-text-subtle); white-space: nowrap; }
.ct-ntf-state { font-size: 12.5px; color: var(--tm-text-muted); }

/* canvas-ops checklist card (studio) */
.ct-ops-list { display: flex; flex-direction: column; gap: 2px; margin-top: 6px; }
.ct-ops-row {
  display: flex; align-items: baseline; gap: 7px;
  font-size: 12.5px; line-height: 1.45; cursor: pointer;
  padding: 2px 4px; border-radius: 5px;
}
.ct-ops-row:hover { background: var(--tm-surface-2); }
.ct-ops-row.is-unviable { opacity: 0.75; cursor: default; }
.ct-ops-row input[type="checkbox"] { flex: none; transform: translateY(1px); }
.ct-ops-label { flex: 1; word-break: break-word; }
.ct-ops-problem { display: block; font-size: 11px; color: var(--tm-danger); }
.ct-ops-tag {
  flex: none; font-size: 10px; padding: 0 6px; border-radius: 8px;
  background: var(--tm-surface-2); color: var(--tm-text-2);
}
.ct-ops-tag.is-remove { background: color-mix(in srgb, var(--tm-danger) 12%, transparent); color: var(--tm-danger); }
.ct-ops-tag.is-formula { background: color-mix(in srgb, var(--tm-accent) 12%, transparent); color: var(--tm-accent); }

/* design / modify proposals — body extras under the shared card anatomy */
.ct-flow-summary { font-size: 12px; color: var(--tm-text-muted); }
.ct-proposal-errors { font-size: 11.5px; color: var(--tm-danger); white-space: pre-wrap; }
/* committed-module receipt line — "Module «chip» created." on the commit-response message */
.ct-msg-modulechip { margin-top: 6px; }
/* quiet bordered button (Show diagram / Open form / Skip) */
.ct-ghost-btn {
  display: inline-flex; align-items: center; gap: 6px;
  border: 1px solid var(--tm-border); background: var(--tm-surface); color: var(--tm-text-muted);
  border-radius: 7px; padding: 4px 10px; font-size: 12px; cursor: pointer; font-weight: 600;
}
.ct-ghost-btn:hover:not(:disabled) { background: rgba(var(--tm-accent-rgb), 0.08); color: var(--tm-accent); border-color: rgba(var(--tm-accent-rgb), 0.35); }
.ct-ghost-btn:disabled { opacity: 0.6; cursor: default; }
/* Input is borderless — the composer card supplies the frame and focus ring. */
.ct-composer textarea {
  flex: 0 0 auto; width: 100%; resize: none; border: none; border-radius: 0;
  padding: 4px 6px; font-family: inherit; font-size: 14px; line-height: 1.5;
  /* Five 21px text lines plus 8px vertical padding; scroll after this cap. */
  outline: none; min-height: 29px; max-height: 113px; box-sizing: border-box;
  overflow-y: hidden; background: transparent; color: var(--tm-text);
}
.ct-composer textarea::placeholder { color: var(--tm-text-subtle); }
/* Circular send button with an up-arrow (Figma); greys out when there's nothing to send. */
.ct-send {
  flex-shrink: 0; margin-left: auto; border: none; background: var(--tm-accent); color: #fff;
  width: 32px; height: 32px; border-radius: 50%; cursor: pointer; font-size: 13px;
  display: inline-flex; align-items: center; justify-content: center;
}
.ct-send:hover:not(:disabled) { filter: brightness(0.92); }
.ct-send:disabled { opacity: 1; cursor: default; background: var(--tm-surface-3); color: var(--tm-text-subtle); }
/* "+" attach trigger — a quiet ghost button at the start of the control row */
.ct-attach-btn {
  flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; border-radius: 50%; cursor: pointer; font-size: 14px;
  border: none; background: transparent; color: var(--tm-text-muted);
}
.ct-attach-btn:hover:not(:disabled) { background: var(--tm-surface-2); color: var(--tm-accent); }
.ct-attach-btn:disabled { opacity: 0.45; cursor: default; }
/* drop-target outline while a file is dragged over the composer */
.ct-composer--drag {
  outline: 2px dashed rgba(var(--tm-accent-rgb), 0.6); outline-offset: -4px; border-radius: 8px;
}
</style>

<!-- Non-scoped: GNey reaches outside its own subtree to reflow the app shell when docked
     to the side, so content sits beside the rail instead of being overlaid by it. -->
<style>
/* Scope the gutter to the layout's main content pane only (a direct child of .fts-client).
   The `.client-content` class is also reused on body-appended dropdown popovers
   (e.g. the workspace switcher: content-class="client-content client-dropdown-menu"),
   which are width-capped — a broad rule would add the rail-width padding to those and
   collapse their text to one character per line. The `> ` child combinator excludes them. */
.fts-client > .client-content { transition: padding-right 0.2s ease; }
body.gney-docked .fts-client > .client-content { padding-right: var(--gney-dock-width, 380px); }
/* Studio host (D14/D18 — the panel docks there too): same reserved rail, scoped to the
   studio's content column. */
body.gney-docked .studio-content { padding-right: var(--gney-dock-width, 380px); transition: padding-right 0.2s ease; }
/* Fixed full-viewport overlays (quick-view + every other modal) ignore the gutter, so
   pull their right edge in by the rail width — the form then fills the area beside it. */
.fts-modal { transition: right 0.2s ease; }
body.gney-docked .fts-modal { right: var(--gney-dock-width, 380px); }
/* Docked is a side-by-side split, not an overlay — so drop the dim backdrop (and the card's
   floating elevation) and let the entry quick-view fill the whole pane beside the rail instead
   of sitting as a short card over an empty dimmed gap. The rail's own left-edge shadow gives the
   separation. Small dialogs (confirmations etc.) are left alone — they keep their centred card. */
body.gney-docked .fts-modal { background-color: transparent; }
body.gney-docked .fts-quick-view > .fts-modal-wrapper > .fts-modal-container {
  box-shadow: none;
}
body.gney-docked .fts-quick-view:not(.full-modal) > .fts-modal-wrapper {
  height: 100%;
  margin-top: 0;
  max-width: none;
}
/* While dragging the rail, kill the transitions so content tracks the cursor 1:1. */
body.gney-resizing { user-select: none; cursor: col-resize; }
body.gney-resizing .fts-client > .client-content,
body.gney-resizing .fts-modal { transition: none; }
/* Too narrow to split — drop the gutter; the rail overlays as a normal panel would. */
@media (max-width: 1100px) {
  body.gney-docked .fts-client > .client-content { padding-right: 0; }
  body.gney-docked .studio-content { padding-right: 0; }
  body.gney-docked .fts-modal { right: 0; }
}
</style>
